import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import {
  AlertTriangle, Bell, CalendarClock, CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Download,
  FileSpreadsheet, FileText, Info, Loader2, Lock, Plus, RefreshCw, RotateCcw, Settings, Trash2, Unlock, Users, X,
} from "lucide-react";
import { API, formatApiError } from "../auth/api";
import { buildPaySheetPdf } from "../lib/paySheetPdf";
import { parseDk } from "../lib/tourHelpers";
import SuggestInput from "../components/SuggestInput";
import ImportDialog from "../components/ImportDialog";
import { applyPayCatalog, checkCode, danlonOptions, danlonType, dataloenType, payTerms, DANLON_SUGGEST } from "../lib/payCodes";

/**
 * Løn (2026-10-23) - Tid & ressourcer › Løn. Stemplinger bliver til timer pr.
 * lønart for lønperioden (LOENGRUNDLAG_ANALYSE.md § 17). Beregningen sker i
 * backend/paybasis.py; siden viser perioden, lader admin rette dage, sætte
 * fravær, godkende og hente lønoversigt/timesedler/CSV.
 * Runde 9 (§ 19): egne arbejdstidsskemaer, fraværstyper med betalingsmåde
 * og lønsatser med trin. Runde 10 (§ 20): dagsvinduet er ét skema med alle
 * vagter og én gem-knap; fravær dækker resten af den normale dag.
 */

const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const chipBase = "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors";
const chipOn = "bg-emerald-600 border-emerald-600 text-white";
const chipOff = "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700";
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const inputFit = input.replace("w-full ", "");
const label = "block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";

const WD_SHORT = ["søn", "man", "tir", "ons", "tor", "fre", "lør"];
const WEEKDAYS = [["mon", "Man"], ["tue", "Tir"], ["wed", "Ons"], ["thu", "Tor"], ["fri", "Fre"], ["sat", "Lør"], ["sun", "Søn"]];
const ABSENCE_SHORT = { syg: "Syg", barn_syg: "Barn", ferie: "Ferie", feriefridag: "FFri", ferie_uden_loen: "Fe÷", fri: "Fri", fri_uden_loen: "Fri÷", afspadsering: "Afsp" };
// 2026-10-25: Fridag efter chaufførens eget skema (før: lør/søn grå for alle -
// også for en, der kører lørdag, og ikke fredag for en på 4-dages uge).
const FREE_BG = "bg-[repeating-linear-gradient(135deg,transparent_0_4px,rgba(148,163,184,0.16)_4px_8px)]";
const isoWeek = (iso) => {
  const d = new Date(iso + "T12:00:00");
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  return Math.ceil(((t - new Date(Date.UTC(t.getUTCFullYear(), 0, 1))) / 86400000 + 1) / 7);
};
const absShort = (ab) => ABSENCE_SHORT[ab.code] || (ab.label || ab.code).slice(0, 5);
const TOTAL_COLS = [["timer", "Timer"], ["ot1", "OT 1"], ["ot2", "OT 2"], ["aften", "Aften"], ["nat", "Nat"], ["fravaer", "Fravær"]];
const SICK = ["syg", "barn_syg"];
// Fraværstimer der går til løn (fx syg på timeløn) - fravær via feriepenge giver ingen timer.
const absenceMinutes = (totals, types) => (types || []).reduce((s, t) => s + (totals?.[t.code] || 0), 0) + (totals?.fri || 0);
// Hvad en fraværstype betyder for netop denne ansættelse.
// 2026-10-25: En afløser MED skema får timer som en timelønnet (som motoren) -
// kun uden skema er der ingen normal dag og derfor ingen timer.
function payEffect(pay, emp, noNorm = true) {
  if (emp === "ingen") return "Registreres kun";
  if (emp === "fast") return pay === "unpaid" ? "Trækkes i den faste løn" : "Den faste løn kører videre";
  if (emp === "afloeser" && noNorm) return "Ingen timer (afløser uden fast skema)";
  return pay === "normal" ? "Giver skemaets timer til løn" : pay === "allowance" ? "Ingen timer – betales af feriepenge / frihedskonto" : "Ingen timer (ubetalt)";
}
// 2026-10-25: Overenskomsternes satser (fx ATL) kommer fra ContainerFlow (Super admin ›
// Overenskomster) med datoer - før stod de her i koden og ville stå stille, når der kom
// nye satser. Et firma, der henter dem, får dem som egne satser (mærket med overenskomsten),
// så nye satser kan foreslås, og firmaet selv bestemmer, hvornår de tilføjes.
const agreementVersions = (a, only) => a.versions.filter((v) => !only || only.includes(v.valid_from))
  .map((v) => ({ valid_from: v.valid_from, rates: { timer: v.base_rate, ...v.rates }, pct1: v.pct1, pct2: v.pct2, agreement: a.id }));
const agreementGroups = (a, id) => {
  const step = `${id}t${a.step_months}`;
  const base = { id, name: `Chauffør (${a.short})`, rates: a.versions.map((v) => ({ valid_from: v.valid_from, rate: v.base_rate })),
    step_months: a.step_months || 0, step_to: a.step_months ? step : "", agreement: { id: a.id, kind: "base" } };
  return a.step_months ? [base, { id: step, name: `Chauffør (${a.short}) efter ${a.step_months} måneder`, rates: a.versions.map((v) => ({ valid_from: v.valid_from, rate: v.step_rate })),
    step_months: 0, step_to: "", agreement: { id: a.id, kind: "step" } }] : [base];
};
// Overenskomster, firmaet har hentet satser fra, med nyere satser end firmaets egne (som serveren).
const agreementUpdates = (m, agreements) => (agreements || []).map((a) => {
  const dates = [...(m.groups || []).filter((g) => g.agreement?.id === a.id).flatMap((g) => g.rates.map((x) => x.valid_from)),
    ...(m.versions || []).filter((v) => v.agreement === a.id).map((v) => v.valid_from)];
  if (!dates.length) return null;
  const last = dates.sort().slice(-1)[0];
  const newer = a.versions.filter((v) => v.valid_from > last).map((v) => v.valid_from);
  return newer.length ? { a, newer } : null;
}).filter(Boolean);
const dkDate = (s) => new Date(s + "T12:00:00").toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
const kr2n = (n) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const hrs = (m) => ((m || 0) / 60).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hrs1 = (m) => ((m || 0) / 60).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const kr = (n) => (n == null ? "–" : Number(n).toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " kr");
const kr2 = (n) => (n == null ? "–" : Number(n).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " kr");
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const longDate = (s) => new Date(s + "T12:00:00").toLocaleDateString("da-DK", { weekday: "long", day: "numeric", month: "long" });
// formatApiError tager hele axios-fejlen (detail kan være tekst eller {message}).
const errText = (e, fb) => (e?.response?.data?.detail != null ? formatApiError(e) : fb);
const dkShort = (s) => new Date(s + "T12:00:00").toLocaleDateString("da-DK", { day: "numeric", month: "short" });
function slugify(v) {
  return String(v || "").trim().toLowerCase().replace(/æ/g, "ae").replace(/ø/g, "oe").replace(/å/g, "aa")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30);
}
const newId = (p) => `${p}${Date.now().toString(36)}`;

function datesBetween(a, b) {
  const out = [];
  const d = new Date(a + "T12:00:00");
  const end = new Date(b + "T12:00:00");
  while (d <= end) { out.push(localDate(d)); d.setDate(d.getDate() + 1); }
  return out;
}

async function download(url, filename) {
  const r = await axios.get(url, { responseType: "blob" });
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename="([^"]+)"/.exec(cd);
  saveAs(r.data, m ? m[1] : filename);
  // 2026-10-25: Danløn/DataLøn viser en oversigt før indlæsning - sig, hvad den skal vise.
  const sys = r.headers?.["x-pay-system"];
  if (sys) {
    const name = sys === "danlon" ? "Danløn" : "DataLøn";
    // 2026-10-25 (L8): Kontroltal - summen pr. kode for hele filen.
    const sums = decodeURIComponent(r.headers?.["x-pay-sums"] || "");
    toast.success(`Lønfil til ${name}: ${r.headers["x-pay-lines"]} linjer for ${r.headers["x-pay-employees"]} medarbejdere`, {
      description: `${sums ? `Kontroltal pr. kode: ${sums}. ` : ""}Tjek at ${name}s oversigt viser det samme, før du trykker Indlæs.`, duration: sums ? 30000 : 9000 });
    // 2026-10-25: Linjer i minus (rettelser, der trækker fra) - sig det.
    const neg = decodeURIComponent(r.headers?.["x-pay-negative"] || "");
    if (neg) toast.warning("Lønfilen har linjer i minus (rettelser til tidligere perioder)", {
      description: `${neg}. Det er rigtigt, hvis der er betalt for meget - tjek det, før du trykker Indlæs.`, duration: 20000 });
  }
}
// Knappens navn efter lønsystemet (Løn › Regler › Lønsystem).
const payFileLabel = (sys) => (sys === "danlon" ? "Lønfil til Danløn" : sys === "dataloen" ? "Lønfil til DataLøn" : "Lønarter (CSV)");

export default function PayrollPanel({ canPayroll = true, initialTab = "", initialRulesSec = "" }) {
  const [tab, setTab] = useState(initialTab === "rules" && canPayroll ? "rules" : "period");
  const [rulesSec, setRulesSec] = useState(initialRulesSec);   // åbn Regler på en bestemt fane (fx lønartkoderne)
  const [meta, setMeta] = useState(null);
  const loadMeta = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payroll/rules`);
      setMeta(Array.isArray(r.data) ? null : r.data);
    } catch (_) { setMeta(null); }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // 2026-10-23 (runde 10): Overblikket (analyser over tid) er flyttet til
  // fanen Overblik i topmenuen. Det der kræver handling, står i Lønperiode.
  // 2026-10-24 (F5): Godkendelse, eksport og regler kræver rettigheden "Løn".
  const tabs = [["period", "Lønperiode", CalendarDays], ["people", "Chauffører", Users],
    ...(canPayroll ? [["rules", "Regler", Settings]] : [])];
  return (
    <div className="space-y-4" data-testid="payroll-panel">
      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`${chipBase} inline-flex items-center gap-1.5 ${tab === k ? chipOn : chipOff}`}
            data-testid={`payroll-tab-${k}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === "period" && <PeriodView meta={meta} canPayroll={canPayroll} onGoRules={(sec) => { setRulesSec(typeof sec === "string" ? sec : ""); setTab(canPayroll ? "rules" : "period"); }} onGoPeople={() => setTab("people")} />}
      {tab === "people" && meta && <PeopleView meta={meta} />}
      {tab === "rules" && meta && <RulesView key={rulesSec} meta={meta} initialSec={rulesSec} onSaved={() => { loadMeta(); setTab("period"); }} onMeta={loadMeta} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2026-10-25 (T9): Ret en regel bagud. Aldrig automatisk: man vælger, hvad der
// var forkert, og fra hvilken godkendt periode, ser forskellen og sender den.
// ---------------------------------------------------------------------------
const corrQty = (x) => (x.unit === "dage" ? `${x.days > 0 ? "+" : ""}${x.days} dage`
  : "qty" in x ? `${x.qty > 0 ? "+" : ""}${String(x.qty).replace(".", ",")} ${x.unit}` : `${x.minutes > 0 ? "+" : "−"}${hrs(Math.abs(x.minutes))} t`);

export function RerateDialog({ initialFrom = "", initialSections = null, onClose, onDone }) {
  const [opt, setOpt] = useState(null);
  const [from, setFrom] = useState(initialFrom);
  const [secs, setSecs] = useState(initialSections || []);
  const [terms, setTerms] = useState(false);
  const [reason, setReason] = useState("");
  const [prev, setPrev] = useState(null);       // {periods: [...]}
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/payroll/rerate`).then((r) => {
      const o = r.data;
      setOpt(o);
      const ps = o.periods || [];
      const f = ps.some((p) => p.start === initialFrom) ? initialFrom : (ps[ps.length - 1]?.start || "");
      setFrom(f);
      // Forvalgt: de afsnit, der er ændret siden perioden blev godkendt.
      if (!initialSections) setSecs((ps.find((p) => p.start === f) || {}).changed || []);
    }).catch((e) => { toast.error(errText(e, "Kunne ikke hente perioderne")); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = (opt?.periods || []).filter((p) => p.start >= from);
  const changedAny = new Set(chosen.flatMap((p) => p.changed));
  const run = async (apply) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payroll/rerate`, { from, sections: secs, terms, reason, preview: !apply });
      if (!apply) { setPrev(r.data); return; }
      toast.success("Forskellen sendes med næste lønfil");
      onDone();
    } catch (e) { toast.error(errText(e, "Kunne ikke regne om")); }
    finally { setBusy(false); }
  };
  const total = (prev?.periods || []).reduce((n, p) => n + p.rows.length, 0);
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-lg p-5 space-y-3 max-h-[88vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid="payroll-rerate-dialog">
        <div className="font-bold">Ret en regel bagud</div>
        <div className="text-sm text-slate-600 dark:text-slate-300">
          Brug det kun, når en regel <b>var forkert</b> i godkendte perioder. Perioderne regnes om med de nuværende regler for det, du vælger - resten bliver,
          som det var. Forskellen sendes som rettelse med næste lønfil. De sendte lønfiler ændres ikke. En ny aftale, der først gælder fremover, skal ikke rettes bagud.
        </div>
        {!opt ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : !opt.periods.length ? (
          <div className="text-sm">Der er ingen godkendte perioder de seneste 3 år.</div>
        ) : (
          <>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Hvad var forkert?</div>
              {opt.sections.map((x) => {
                const ch = changedAny.has(x.key);
                return (
                  <label key={x.key} className={`flex items-center gap-2 text-sm py-0.5 ${ch ? "" : "opacity-50"}`}>
                    <input type="checkbox" disabled={!ch} checked={secs.includes(x.key)} data-testid={`payroll-rerate-sec-${x.key}`}
                      onChange={(e) => { setPrev(null); setSecs(e.target.checked ? [...secs, x.key] : secs.filter((k) => k !== x.key)); }} />
                    {x.label} <span className="text-xs text-slate-500">{ch ? "(ændret siden)" : "(uændret)"}</span>
                  </label>
                );
              })}
              <label className="flex items-start gap-2 text-sm py-0.5">
                <input type="checkbox" className="mt-1" checked={terms} onChange={(e) => { setPrev(null); setTerms(e.target.checked); }} data-testid="payroll-rerate-terms" />
                <span>Chaufførernes vilkår (skema, ansættelse, timer pr. dag)<span className="block text-xs text-slate-500">Kun hvis en chaufførs vilkår var forkerte. Vilkår med "gælder fra" følger deres dato.</span></span>
              </label>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Fra og med lønperioden</div>
              <select className={`${inputFit} w-full`} value={from} onChange={(e) => { setPrev(null); setFrom(e.target.value); }} data-testid="payroll-rerate-from">
                {opt.periods.map((p) => <option key={p.start} value={p.start}>{p.label}{p.pending ? " (omregnet, ikke sendt)" : ""}</option>)}
              </select>
              <div className="text-xs text-slate-500 mt-1">Og alle godkendte perioder efter den ({chosen.length}). Reglerne gjaldt hele perioden, da den blev godkendt - derfor hele perioder.</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Hvad var forkert? (står ved rettelsen og på timesedlen)</div>
              <input className={`${inputFit} w-full`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="fx Pausen er betalt efter lokalaftalen" data-testid="payroll-rerate-reason" />
            </div>
            {prev && (prev.periods.length === 0 ? (
              <div className="text-sm font-semibold text-emerald-700" data-testid="payroll-rerate-none">Ingen forskel - det valgte giver det samme som det godkendte.</div>
            ) : (
              <div className="space-y-2" data-testid="payroll-rerate-rows">
                {prev.periods.map((p) => (
                  <div key={p.start}>
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">{p.label}</div>
                    <div className="text-sm divide-y divide-slate-100 dark:divide-slate-700">
                      {p.rows.map((x, i) => (
                        <div key={i} className="py-1.5 flex items-center justify-between gap-3">
                          <span className="min-w-0"><span className="font-semibold">{x.driver_name}</span>{x.left ? <span className="text-xs text-amber-700"> (fratrådt)</span> : ""} <span className="text-slate-500">· {x.label}</span></span>
                          <span className={`shrink-0 font-mono ${(x.days ?? x.qty ?? x.minutes) < 0 ? "text-red-700" : "text-emerald-700"}`}>{corrQty(x)}</span>
                        </div>))}
                    </div>
                  </div>
                ))}
                {prev.periods.some((p) => p.rows.some((x) => (x.days ?? x.qty ?? x.minutes) < 0)) && (
                  <div className="text-xs text-amber-800">Minus = timer, der er betalt for meget. De trækkes i næste løn - tal med chaufføren først.</div>
                )}
              </div>
            ))}
          </>
        )}
        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Annuller</button>
          {opt?.periods.length > 0 && <button className={btnGhost} disabled={busy || (!secs.length && !terms)} onClick={() => run(false)} data-testid="payroll-rerate-preview">
            {busy && !prev ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Vis forskellen</button>}
          {total > 0 && <button className={btnPrimary} disabled={busy || reason.trim().length < 3} onClick={() => run(true)} data-testid="payroll-rerate-apply"
            title={reason.trim().length < 3 ? "Skriv hvad der var forkert" : ""}><Check className="w-4 h-4" /> Send forskellen med næste lønfil</button>}
        </div>
      </div>
    </div>, document.body);
}

// ---------------------------------------------------------------------------
// Lønperiode
// ---------------------------------------------------------------------------
function PeriodView({ meta, canPayroll = true, onGoRules, onGoPeople }) {
  const [ref, setRef] = useState(localDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cell, setCell] = useState(null);       // {driverId, date}
  const [cellVer, setCellVer] = useState(0);    // dagsvinduet startes forfra efter gem
  const [sheet, setSheet] = useState(null);     // driverId
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);   // kræver handling + kommende fravær
  const [rr, setRr] = useState(null);           // T9: "Ret en regel bagud" {from}

  const load = useCallback(async (r = ref) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payroll/period`, { params: { ref: r } });
      setData(res.data);
    } catch (e) {
      toast.error(errText(e, "Kunne ikke hente lønperioden"));
    } finally { setLoading(false); }
    // 2026-10-25: For den periode man ser på - den 21. godkender man perioden,
    // der sluttede den 19., og det er dens røde dage, der skal rettes.
    axios.get(`${API}/payroll/status`, { params: { ref: r } }).then((x) => setStatus(x.data)).catch(() => setStatus(null));
  }, [ref]);
  useEffect(() => { load(ref); }, [ref]); // eslint-disable-line react-hooks/exhaustive-deps

  const dates = useMemo(() => (data ? datesBetween(data.start, data.end) : []), [data]);
  // Søn-/helligdag = overtid 2 fra første minut (Løn › Regler; ATL er standard).
  // 2026-10-25: Søndag og helligdag vælges hver for sig (godkendte perioder før da: ét valg).
  const both = data?.rules?.sun_hol_ot2 !== false;
  const sunOt2 = data?.rules?.sun_ot2 ?? both, holOt2 = data?.rules?.hol_ot2 ?? both;
  const sunHol = sunOt2 || holOt2;
  const sum = useMemo(() => {
    const s = { timer: 0, ot: 0, band: 0, abs: 0, sickDays: 0, otherDays: 0, red: 0, orange: 0, cost: 0, rev: 0 };
    for (const d of data?.drivers || []) {
      const t = d.totals || {};
      s.timer += t.timer || 0; s.ot += (t.ot1 || 0) + (t.ot2 || 0); s.band += (t.aften || 0) + (t.nat || 0);
      if (d.profile.employment !== "fast") s.abs += absenceMinutes(t, data.absence_types);
      for (const [k, n] of Object.entries(t.absence_days || {})) { if (SICK.includes(k)) s.sickDays += n; else s.otherDays += n; }
      s.red += d.flags.red; s.orange += d.flags.orange;
      s.cost += d.money?.total || 0; s.rev += d.revenue || 0;
    }
    return s;
  }, [data]);
  const missingNo = (data?.drivers || []).filter((d) => d.profile.employment !== "ingen" && !d.profile.employee_no).length;

  // 2026-10-24: Serveren siger fra med en kode; her spørges brugeren og der
  // sendes igen med bekræftelsen (før perioden er slut, røde dage, eksporteret).
  const approve = async (trial, flags = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payroll/period/approve`, { start: data.start, end: data.end, trial, ...flags });
      toast.success(trial ? "Godkendt som prøve" : `Perioden er godkendt${r.data.corrections ? ` (med ${r.data.corrections} rettelser)` : ""}`,
        r.data.mail_to ? { description: `Bogholderen har fået besked (${r.data.mail_to}).` } : undefined);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setBusy(false);
      if (e?.response?.status === 409 && d?.code === "before_end") {
        if (window.confirm(`${d.message}\n\nGodkend alligevel?`)) return approve(trial, { ...flags, confirm_early: true });
        return;
      }
      if (e?.response?.status === 409 && d?.code === "red_days") {
        const list = [d.open?.length ? `Uden udstempling (0 timer): ${d.open.join(", ")}` : "",
          d.conflict?.length ? `Fravær + arbejde (fraværet regnes som resten af dagen): ${d.conflict.join(", ")}` : "",
          d.other?.length ? `Andre: ${d.other.join(", ")}` : ""].filter(Boolean).join("\n");
        if (window.confirm(`${d.message}\n\n${list}\n\nGodkend alligevel?`)) return approve(trial, { ...flags, force: true });
        return;
      }
      toast.error(errText(e, "Kunne ikke godkende"));
    } finally { setBusy(false); }
  };
  // 2026-10-25 (T9): Fortryd en omregning, der ikke er sendt endnu (alle perioder).
  const undoRerate = async () => {
    if (!window.confirm("Fortryd omregningen? Perioderne får de regler igen, de blev godkendt med.")) return;
    try { await axios.post(`${API}/payroll/rerate`, { undo: true }); toast.success("Omregningen er fortrudt"); load(); }
    catch (e) { toast.error(errText(e, "Kunne ikke fortryde")); }
  };
  const reopen = async (confirmExported = false) => {
    if (!confirmExported && !window.confirm("Åbn perioden igen? Gør det kun, hvis bogholderen ikke har brugt tallene endnu.")) return;
    setBusy(true);
    try { await axios.post(`${API}/payroll/period/reopen`, { start: data.start, end: data.end, confirm_exported: confirmExported }); toast.success("Perioden er åbnet igen"); load(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      setBusy(false);
      if (e?.response?.status === 409 && d?.code === "exported") {
        if (window.confirm(`${d.message}\n\nÅbn alligevel?`)) return reopen(true);
        return;
      }
      toast.error(errText(e, "Kunne ikke åbne perioden"));
    }
    finally { setBusy(false); }
  };
  const dl = async (kind, variant = "admin", force = false) => {
    try {
      if (kind === "pdf") {
        const doc = buildPaySheetPdf({ period: data, drivers: data.drivers.filter((d) => d.profile.employment !== "ingen") });
        doc.save(`timesedler_${data.start}_${data.end}.pdf`);
        return;
      }
      await download(`${API}/payroll/period/export?start=${data.start}&end=${data.end}&kind=${kind}&variant=${variant}${force ? "&force=true" : ""}`, `${kind}.dat`);
      if (kind === "lines" || kind === "times") load();
    } catch (e) {
      // Blob-svar: læs fejlen som tekst
      let d = e?.response?.data?.detail;
      if (!d && e?.response?.data instanceof Blob) { try { d = JSON.parse(await e.response.data.text()).detail; } catch (_) { d = null; } }
      if (e?.response?.status === 409 && d?.code === "missing_employee_no") {
        if (window.confirm(`${d.message}\n\nHent filen alligevel?`)) return dl(kind, variant, true);
        return;
      }
      // 2026-10-25: Lønsystemets tjek kan være en lang liste - lad den stå, og vis vejen til opsætningen.
      // 2026-10-25: Kort besked + vej til felterne (hvor hver forkert kode er markeret med et forslag).
      if (e?.response?.status === 409 && d?.code === "export_setup") {
        const ps = d.problems || [d.message];
        toast.error(`Lønfilen til ${d.system || "lønsystemet"} kan ikke laves endnu - ${ps.length} ${ps.length === 1 ? "ting" : "ting"} skal rettes`, {
          description: ps.slice(0, 3).join(" · ") + (ps.length > 3 ? ` · og ${ps.length - 3} mere` : ""), duration: 20000,
          ...(onGoRules ? { action: { label: "Ret lønartkoder", onClick: () => onGoRules("export") } } : {}) });
        return;
      }
      toast.error((d && (typeof d === "string" ? d : d.message)) || errText(e, "Download fejlede"));
    }
  };

  if (loading && !data) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;
  const ap = data.approval;
  const today = localDate();
  const driverOf = (id) => data.drivers.find((d) => d.id === id);
  // Navigation i dagsvinduet: dagen før/efter og næste dag med en markering
  const navFor = (c) => {
    const i = dates.indexOf(c.date);
    const toCheck = [];
    for (const d of data.drivers) {
      for (const x of d.days) {
        if (x.date < today && x.flags.some((f) => f.level === "red" || f.level === "orange")) toCheck.push({ driverId: d.id, date: x.date });
      }
    }
    const pos = toCheck.findIndex((t) => t.driverId === c.driverId && t.date === c.date);
    const nextCheck = toCheck[(pos + 1) % (toCheck.length || 1)];
    return {
      prev: i > 0 ? () => setCell({ ...c, date: dates[i - 1] }) : null,
      next: i < dates.length - 1 ? () => setCell({ ...c, date: dates[i + 1] }) : null,
      nextCheck: nextCheck && !(nextCheck.driverId === c.driverId && nextCheck.date === c.date) ? () => setCell(nextCheck) : null,
    };
  };

  return (
    <div className="space-y-4">
      {/* 2026-10-25: Nye overenskomstsatser (fx 1. marts) - dem, der laver lønnen, skal vide det. */}
      {canPayroll && (meta?.agreement_updates || []).map((u) => (
        <div key={u.id} className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`payroll-agreement-news-${u.id}`}>
          <span className="flex-1 min-w-[220px] text-amber-900 dark:text-amber-200"><b>Nye {u.short}-satser</b> fra {u.valid_from.map(dkDate).join(" og ")} er klar - jeres lønsatser er endnu ikke opdateret.</span>
          <button className={btnGhost} onClick={() => onGoRules("money")}>Se og tilføj</button>
        </div>
      ))}
      {!data.configured && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-4 flex flex-wrap items-center gap-3" data-testid="payroll-setup-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div className="flex-1 min-w-[220px] text-sm text-amber-900 dark:text-amber-200">
            <b>Lønreglerne er ikke sat op endnu.</b> Timerne nedenfor er regnet med standarden (ATL: 7,4 t, overtid efter normal dag). Det tager få minutter at rette dem til jeres aftale.
          </div>
          <button className={btnPrimary} onClick={onGoRules}>Sæt reglerne op</button>
        </div>
      )}

      <div className={`${card} p-4 flex flex-wrap items-center gap-3`}>
        <button className={btnGhost} onClick={() => setRef(data.prev_ref)} aria-label="Forrige periode"><ChevronLeft className="w-4 h-4" /></button>
        <div className="min-w-[200px]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Lønperiode</div>
          <div className="font-bold text-lg text-slate-900 dark:text-white" data-testid="payroll-period-label">{data.label}</div>
        </div>
        <button className={btnGhost} onClick={() => setRef(data.next_ref)} aria-label="Næste periode"><ChevronRight className="w-4 h-4" /></button>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <ApprovalChip ap={ap} />
          {/* 2026-10-25: Frist for lønfilen (Regler › Lønperiode) */}
          {meta?.rules?.deadline_days > 0 && (() => {
            const due = new Date(data.end + "T12:00:00"); due.setDate(due.getDate() + meta.rules.deadline_days);
            const dueStr = localDate(due), sent = ap && !ap.trial && (ap.exports || []).some((e) => e.kind === "lines");
            const left = Math.round((due - new Date(localDate() + "T12:00:00")) / 86400000);
            const cls = sent ? "bg-emerald-50 text-emerald-800" : left < 0 ? "bg-red-100 text-red-800" : left <= 3 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700";
            return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${cls}`} title={`Lønfilen skal være hos lønsystemet senest ${meta.rules.deadline_days} dage efter periodens slut (Regler › Lønperiode)`} data-testid="payroll-deadline">
              {sent ? "Lønfil sendt" : `Frist ${dkShort(dueStr)}${left >= 0 && left <= 7 ? ` (${left === 0 ? "i dag" : `om ${left} d.`})` : left < 0 ? " - overskredet" : ""}`}</span>;
          })()}
          {/* 2026-10-25: "Godkend som prøve" er fjernet - filerne fra en ikke-godkendt
              periode er i forvejen mærket PRØVE, så den gjorde ingen forskel. */}
          {canPayroll && (!ap || ap.trial ? (
            <button className={btnPrimary} disabled={busy} onClick={() => approve(false)} data-testid="payroll-approve">
              <Lock className="w-4 h-4" /> Godkend periode
            </button>
          ) : (
            <button className={btnGhost} disabled={busy} onClick={() => reopen()} data-testid="payroll-reopen"><Unlock className="w-4 h-4" /> Åbn igen</button>
          ))}
          {canPayroll && ap?.trial && <button className={btnGhost} disabled={busy} onClick={() => reopen()}>Fjern prøve</button>}
          {canPayroll && ap && !ap.trial && (
            <button className={btnGhost} disabled={busy} onClick={() => setRr({ from: data.start })} data-testid="payroll-rerate"
              title="Var en regel forkert i en godkendt periode? Regn den om og send forskellen med næste lønfil">
              <RefreshCw className="w-4 h-4" /> Ret en regel bagud</button>
          )}
          {!canPayroll && <span className="text-[11px] text-slate-500">Godkendelse og eksport kræver rettigheden "Løn"</span>}
        </div>
      </div>

      {ap && !ap.trial && ap.early && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-200" data-testid="payroll-early-note">
          Godkendt {fmtShort(ap.approved_at)}, før perioden sluttede {fmtShort(data.end)}. Lønfilen har timerne, som de stod ved godkendelsen.
          {" "}Timer og fravær, der kommer til bagefter (fx de sidste dage af perioden), betales med <b>næste periodes lønfil</b> som rettelser – så intet går tabt.
        </div>
      )}
      {ap && !ap.trial && ap.rerate?.pending && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 dark:bg-sky-900/20 px-4 py-3 text-sm text-sky-900 dark:text-sky-200 flex flex-wrap items-center gap-2" data-testid="payroll-rerate-pending">
          <span className="flex-1">Regnet om med rettede regler {fmtShort(ap.rerate.at)}{ap.rerate.by ? ` af ${ap.rerate.by}` : ""}{ap.rerate.reason ? `: "${ap.rerate.reason}"` : ""}. Forskellen sendes som rettelse med næste periodes lønfil - lønfilen for denne periode er uændret.</span>
          {canPayroll && <button className={btnGhost} onClick={undoRerate} data-testid="payroll-rerate-undo">Fortryd</button>}
        </div>
      )}
      {rr && <RerateDialog initialFrom={rr.from} initialSections={rr.sections} onClose={() => setRr(null)} onDone={() => { setRr(null); load(); }} />}
      {ap && !ap.trial && ap.changed?.length > 0 && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 dark:bg-sky-900/20 px-4 py-3 text-sm text-sky-900 dark:text-sky-200">
          Ændret efter godkendelsen: <b>{ap.changed.join(", ")}</b>. Forskellen kommer med som rettelse i næste periode.
        </div>
      )}

      {status && (status.alerts.length > 0 || status.upcoming.length > 0) && (
        <StatusPanel status={status} onGoPeople={onGoPeople} onOpenDay={(driverId, date) => {
          if (date < data.start || date > data.end) setRef(date);
          setCell({ driverId, date });
        }} />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi label="Timer i alt" value={hrs1(sum.timer)} tone="emerald" />
        <Kpi label="Overtid" value={hrs1(sum.ot)} tone="amber" />
        <Kpi label="Aften/nat" value={hrs1(sum.band)} tone="indigo" />
        <Kpi label="Fravær til løn" value={hrs1(sum.abs)} sub={`${sum.sickDays} sygedage · ${sum.otherDays} andre dage`} tone="sky" />
        <Kpi label="Skal tjekkes" value={`${sum.red} · ${sum.orange}`} sub="røde · orange dage" tone={sum.red ? "red" : "slate"} />
        {data.can_money && <Kpi label="Lønomkostning" value={kr(sum.cost)} tone="slate" />}
        {data.can_money && <Kpi label="Dækningsbidrag" value={kr(sum.rev - sum.cost)} sub={`omsætning ${kr(sum.rev)}`} tone={sum.rev - sum.cost >= 0 ? "emerald" : "red"} />}
      </div>

      {canPayroll && <div className="flex flex-wrap items-center gap-2">
        <button className={btnGhost} onClick={() => dl("xlsx")} data-testid="payroll-dl-xlsx"><FileSpreadsheet className="w-4 h-4" /> Lønoversigt (Excel)</button>
        <button className={btnGhost} onClick={() => dl("pdf")} data-testid="payroll-dl-pdf"><FileText className="w-4 h-4" /> Timesedler (PDF)</button>
        <button className={btnGhost} onClick={() => dl("lines")} data-testid="payroll-dl-lines"><Download className="w-4 h-4" /> {payFileLabel(meta.rules?.export?.system)}</button>
        <button className={btnGhost} onClick={() => dl("times")} data-testid="payroll-dl-times"><Download className="w-4 h-4" /> Godkendte tider (CSV)</button>
        {missingNo > 0 && (
          <button onClick={onGoPeople} className="text-xs font-semibold text-amber-700 hover:underline ml-2">{missingNo} chauffører mangler lønnummer</button>
        )}
        {ap?.exports?.length > 0 && (
          <span className="text-[11px] text-slate-500" data-testid="payroll-exported">
            Lønfil hentet {whenTxt(ap.exports[ap.exports.length - 1].at)}{ap.exports[ap.exports.length - 1].by ? ` af ${ap.exports[ap.exports.length - 1].by}` : ""}
          </span>
        )}
        {/* 2026-10-25: Prøvekørsel uden en særlig knap: filerne fra en ikke-godkendt periode er mærket PRØVE. */}
        {(!ap || ap.trial) && (
          <span className="w-full text-[11px] text-slate-500" data-testid="payroll-trial-note">
            Perioden er ikke godkendt, så filerne er mærket <b>PRØVE</b> (i filnavnet og i Lønoversigten). Brug dem til en prøvekørsel ved siden af jeres nuværende løn - sammenlign, ret reglerne, og godkend først, når tallene passer.
          </span>
        )}
      </div>}

      {data.corrections?.length > 0 && (
        <div className={`${card} p-4`}>
          <div className="font-bold text-sm mb-2">Rettelser til tidligere perioder (kommer med i denne periodes lønfil)</div>
          <div className="text-sm divide-y divide-slate-100 dark:divide-slate-700">
            {data.corrections.map((c, i) => (
              <div key={i} className="py-1.5 flex justify-between gap-3">
                <span>{c.driver_name} · {c.label} <span className="text-slate-500">({c.period}{c.reason ? ` - regel rettet: ${c.reason}` : ""})</span></span>
                {c.unit === "dage"
                  ? <b className="text-sky-700">{c.days > 0 ? "+" : ""}{c.days} {Math.abs(c.days) === 1 ? "dag" : "dage"}</b>
                  : <b className={c.minutes > 0 ? "text-emerald-700" : "text-red-700"}>{c.minutes > 0 ? "+" : ""}{hrs(c.minutes)} t</b>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2026-10-25: Totalerne i deres egen tabel (før yderst til højre i kalenderen, ude af syne).
          Kalenderen nedenfor er dag for dag. */}
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="payroll-summary">
          <thead className="bg-slate-50 dark:bg-slate-900 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Chauffør</th>
              {TOTAL_COLS.map(([, l]) => <th key={l} className="px-3 py-2 text-right whitespace-nowrap">{l}</th>)}
              {data.can_money && <th className="px-3 py-2 text-right">Løn</th>}
              {data.can_money && <th className="px-3 py-2 text-right">DB</th>}
              <th className="px-4 py-2 text-right">Skal tjekkes</th>
            </tr>
          </thead>
          <tbody>
            {data.drivers.map((d) => {
              const t = d.totals || {};
              const inPeriod = d.days.filter((x) => x.date >= data.start && x.date <= data.end);
              const redDays = inPeriod.filter((x) => x.flags.some((f) => f.level === "red"));
              const orangeDays = inPeriod.filter((x) => !x.flags.some((f) => f.level === "red") && x.flags.some((f) => f.level === "orange"));
              return (
                <tr key={d.id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50/60 dark:hover:bg-slate-700/20" data-testid={`payroll-summary-${d.id}`}>
                  <td className="px-4 py-2">
                    <button onClick={() => setSheet(d.id)} className="text-left font-semibold text-slate-800 dark:text-slate-100 hover:underline" data-testid={`payroll-driver-${d.id}`}>{d.name}</button>
                    <div className="text-[11px] text-slate-500">{d.profile.employee_no ? `Nr. ${d.profile.employee_no} · ` : ""}{d.employment_label}</div>
                    {data.settlements?.[d.id] && (
                      <span className="inline-block mt-0.5 px-1.5 py-px rounded bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 text-[10px] font-bold" data-testid={`payroll-settled-${d.id}`}
                        title="Afregnet ved fratrædelse - periodens lønfil har kun det, der er ændret efter afregningen">Afregnet {fmtShort(data.settlements[d.id].at)}</span>
                    )}
                  </td>
                  {TOTAL_COLS.map(([k]) => {
                    const v = k === "timer" ? (t.timer || t.overtimer || 0) : k === "fravaer" ? absenceMinutes(t, data.absence_types) * (d.profile.employment === "fast" ? -1 : 1) : t[k];
                    const tip = k === "fravaer" ? Object.entries(t.absence_days || {}).map(([c, n]) => `${(data.absence_types.find((x) => x.code === c) || {}).name || c}: ${n} ${n === 1 ? "dag" : "dage"}`).join("\n") : "";
                    return <td key={k} title={tip} className={`px-3 py-2 text-right tabular-nums ${k === "timer" ? "font-bold text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-200"}`}>{d.profile.employment === "ingen" && k !== "timer" ? "" : (k === "timer" && d.profile.employment === "ingen" ? hrs(t.work) : (v ? hrs(v) : <span className="text-slate-300">–</span>))}</td>;
                  })}
                  {data.can_money && <td className="px-3 py-2 text-right tabular-nums">{d.money ? kr(d.money.total) : "–"}</td>}
                  {data.can_money && <td className={`px-3 py-2 text-right tabular-nums font-semibold ${d.db < 0 ? "text-red-700" : "text-emerald-700"}`}>{d.money ? kr(d.db) : "–"}</td>}
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {redDays.length > 0 && <button onClick={() => setCell({ driverId: d.id, date: redDays[0].date })} className="ml-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-bold" title="Mangler udstempling / fravær + arbejde - åbn den første">{redDays.length} røde</button>}
                    {orangeDays.length > 0 && <button onClick={() => setCell({ driverId: d.id, date: orangeDays[0].date })} className="ml-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" title="Tjek - åbn den første">{orangeDays.length} at tjekke</button>}
                    {!redDays.length && !orangeDays.length && <span className="text-xs text-emerald-700">✓</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <div className="px-4 pt-3 text-sm font-bold text-slate-900 dark:text-white">Dag for dag</div>
        <table className="text-xs border-collapse" data-testid="payroll-grid">
          <thead>
            {/* Ugenumre - en måned er lettere at læse i uger. */}
            <tr className="bg-slate-50 dark:bg-slate-900 text-[10px] text-slate-400">
              <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-900" />
              {dates.reduce((acc, d) => {
                const w = isoWeek(d);
                if (acc.length && acc[acc.length - 1].w === w) acc[acc.length - 1].n += 1; else acc.push({ w, n: 1, d });
                return acc;
              }, []).map((g) => <th key={g.d} colSpan={g.n} className="font-semibold text-left pl-1.5 pt-1 border-l border-slate-200 dark:border-slate-700">Uge {g.w}</th>)}
            </tr>
            <tr className="bg-slate-50 dark:bg-slate-900">
              <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-900 text-left px-3 py-2 min-w-[150px]">Chauffør</th>
              {dates.map((d) => {
                const dt = new Date(d + "T12:00:00");
                // Søndag og helligdag betyder det samme for alle (overtid 2 fra første minut, Løn › Regler) - lørdag afhænger af skemaet.
                const red = (holOt2 && data.holidays?.[d]) || (sunOt2 && dt.getDay() === 0);
                return (
                  <th key={d} title={data.holidays?.[d] ? `${data.holidays[d]}${red ? " - overtid 2 fra første minut" : ""}` : dt.getDay() === 0 && sunOt2 ? `Søndag - overtid 2 fra første minut${data?.rules?.sun_ot2_except_schedule === false ? "" : " (undtagen for den, der har søndag i sit skema)"}. Reglen står under Løn › Regler › Overtid, helligdage og tillæg.` : ""}
                    className={`px-0.5 py-1 font-semibold text-center min-w-[38px] ${dt.getDay() === 1 ? "border-l border-slate-200 dark:border-slate-700" : ""} ${red ? "text-rose-600 dark:text-rose-300" : ""} ${d === today ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 rounded-t-md" : ""}`}>
                    <div className="text-[10px] uppercase">{WD_SHORT[dt.getDay()]}</div>
                    <div>{dt.getDate()}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.drivers.map((d) => {
              const byDate = Object.fromEntries(d.days.map((x) => [x.date, x]));
              // Har chaufføren faste arbejdsdage? (afløser uden skema: ingen fridage at vise)
              const hasNorm = d.days.some((x) => (x.day_norm_min || 0) > 0);
              return (
                <tr key={d.id} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 px-3 py-1.5 min-w-[150px]">
                    <button onClick={() => setSheet(d.id)} className="text-left font-semibold text-slate-800 dark:text-slate-100 hover:underline">{d.name}</button>
                    <div className="text-[10px] text-slate-500">{d.profile.employee_no ? `Nr. ${d.profile.employee_no}` : d.employment_label}</div>
                  </td>
                  {dates.map((dt) => <GridCell key={dt} x={byDate[dt]} emp={d.profile.employment} free={hasNorm && byDate[dt] && !(byDate[dt].day_norm_min > 0)}
                    monday={new Date(dt + "T12:00:00").getDay() === 1} onClick={() => setCell({ driverId: d.id, date: dt })} />)}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-4 py-2 text-[11px] text-slate-500 space-y-1 border-t border-slate-100 dark:border-slate-700" data-testid="payroll-legend">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="inline-block w-3 h-3 align-middle rounded bg-red-100 border border-red-200 mr-1" />Mangler udstempling / fravær + arbejde</span>
            <span><span className="inline-block w-3 h-3 align-middle rounded bg-amber-50 border border-amber-200 mr-1" /><span className="inline-block w-1.5 h-1.5 align-middle rounded-full bg-amber-500 mr-1" />Tjek (tur-tid, arbejdstidsregler, arbejdsdag uden stempling "–")</span>
            <span><span className="inline-block w-1.5 h-1.5 align-middle rounded-full bg-violet-500 mr-1" />Rettet bagefter (hvem og hvorfor står i dagen)</span>
            <span><span className="inline-block w-3 h-3 align-middle rounded bg-sky-100 border border-sky-200 mr-1" />Fravær · "4,0 +Syg" = 4 timers arbejde og syg resten af dagen</span>
            <span><span className={`inline-block w-3 h-3 align-middle rounded border border-slate-200 mr-1 ${FREE_BG}`} />Fridag efter chaufførens skema · <span className="text-slate-400">grå tekst (fx Syg)</span> = fravær på en fridag, tæller ikke</span>
            <span><span className="inline-block w-3 h-3 align-middle rounded bg-emerald-100 border border-emerald-200 mr-1" />I dag</span>
            {sunHol && <span><b className="text-rose-600 dark:text-rose-300">{sunOt2 && holOt2 ? "søn / helligdag" : sunOt2 ? "søndag" : "helligdag"}</b> = overtid 2 fra første minut (Løn › Regler)</span>}
          </div>
          {(() => {
            // Forkortelserne, der faktisk bruges i perioden.
            const seen = new Map();
            data.drivers.forEach((d) => d.days.forEach((x) => { if (x.absence) seen.set(absShort(x.absence), x.absence.label); }));
            const list = [...seen.entries()].filter(([k, l]) => k !== l);
            return list.length ? <div data-testid="payroll-legend-abbr">Forkortelser: {list.map(([k, l]) => `${k} = ${l}`).join(" · ")}{list.some(([k]) => k.includes("÷")) ? " (÷ = uden løn)" : ""}</div> : null;
          })()}
          <div>Klik på en dag for at se beregningen eller rette den.</div>
        </div>
      </div>

      {cell && driverOf(cell.driverId) && driverOf(cell.driverId).days.some((x) => x.date === cell.date) && (
        <DayDrawer key={`${cell.driverId}-${cell.date}-${cellVer}`} period={data} driver={driverOf(cell.driverId)} day={driverOf(cell.driverId).days.find((x) => x.date === cell.date)}
          onClose={() => setCell(null)} onChanged={async () => { await load(); setCellVer((v) => v + 1); }} nav={navFor(cell)} />
      )}
      {sheet && driverOf(sheet) && <DriverSheet period={data} driver={driverOf(sheet)} onClose={() => setSheet(null)} canPayroll={canPayroll} onChanged={() => load()} />}
    </div>
  );
}

// Det der kræver handling (runde 9, flyttet hertil fra Overblik i runde 10)
// 2026-10-25: "Kræver handling" = noget mangler, er forkert eller har en frist
// (røde dage, tomme arbejdsdage, lønnummer, ansættelsesdato, refusion).
// "Til orientering" = tal, der er gode at kende, men ikke en fejl (overtid,
// arbejdstidsregler, sygedage uden løn, et sygeforløb, der nærmer sig dag 31).
function StatusPanel({ status, onGoPeople, onOpenDay }) {
  const action = status.alerts.filter((a) => a.kind === "action" || (!a.kind && a.level !== "info"));
  const info = status.alerts.filter((a) => !action.includes(a));
  const open = (a) => (a.fix === "people" ? onGoPeople : a.date && a.driver_id ? () => onOpenDay(a.driver_id, a.date) : null);
  const row = (a, i) => {
    const go = open(a);
    return (
      <div key={i} onClick={go || undefined} role={go ? "button" : undefined}
        className={`text-sm rounded-lg px-3 py-2 flex gap-2 items-start ${go ? "cursor-pointer hover:underline" : ""} ${a.level === "red" ? "bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-100" : a.level === "orange" ? "bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100" : "bg-slate-50 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200"}`}
        data-testid="payroll-alert">
        {a.kind === "info" ? <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />}
        <span className="flex-1">{a.text}</span>
        {go && <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 opacity-60" aria-hidden />}
      </div>
    );
  };
  const side = status.upcoming.length > 0;
  return (
    <div className="grid lg:grid-cols-5 gap-3" data-testid="payroll-status-panel">
      {(action.length > 0 || info.length > 0) && (
        <div className={`space-y-3 ${side ? "lg:col-span-3" : "lg:col-span-5"}`}>
          {action.length > 0 && (
            <div className={`${card} p-4 space-y-2`} data-testid="payroll-status-action">
              <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><Bell className="w-4 h-4 text-amber-600" />Kræver handling</div>
              {action.map(row)}
            </div>
          )}
          {info.length > 0 && (
            <div className={`${card} p-4 space-y-2`} data-testid="payroll-status-info">
              <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><Info className="w-4 h-4 text-sky-600" />Til orientering</div>
              {info.map(row)}
            </div>
          )}
        </div>
      )}
      {side && (
        <div className={`${card} p-4 space-y-2 ${status.alerts.length ? "lg:col-span-2" : "lg:col-span-5"}`}>
          <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><CalendarClock className="w-4 h-4 text-sky-600" />Fravær de næste 14 dage</div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {status.upcoming.map((u, i) => (
              <div key={i} className="py-1.5 text-sm flex justify-between gap-3">
                <span><b>{u.driver_name}</b> · {u.label}</span>
                <span className="text-slate-500 tabular-nums whitespace-nowrap">{u.date_from === u.date_to ? dkShort(u.date_from) : `${dkShort(u.date_from)} – ${dkShort(u.date_to)}`}{u.days > 1 ? ` (${u.days} dage)` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalChip({ ap }) {
  if (!ap) return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600" data-testid="payroll-status">Ikke godkendt</span>;
  const when = new Date(ap.approved_at).toLocaleDateString("da-DK", { day: "numeric", month: "short" });
  if (ap.trial) return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800" data-testid="payroll-status">Prøve {when}</span>;
  return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 inline-flex items-center gap-1" data-testid="payroll-status"><Check className="w-3.5 h-3.5" />Godkendt {when}{ap.approved_by ? ` · ${ap.approved_by}` : ""}</span>;
}

function Kpi({ label: l, value, sub, tone = "slate" }) {
  const tones = {
    emerald: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200",
    indigo: "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200",
    sky: "bg-sky-50 dark:bg-sky-900/20 text-sky-800 dark:text-sky-200",
    red: "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
    slate: "bg-slate-50 dark:bg-slate-900/40 text-slate-800 dark:text-slate-200",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ${tones[tone]}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-70">{l}</div>
      <div className="text-xl font-extrabold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] opacity-70">{sub}</div>}
    </div>
  );
}

function GridCell({ x, emp, onClick, free = false, monday = false }) {
  if (!x) return <td className={monday ? "border-l border-slate-100 dark:border-slate-700" : ""} />;
  const red = x.flags.some((f) => f.level === "red");
  const orange = x.flags.some((f) => f.level === "orange");
  const blue = x.flags.some((f) => f.level === "blue");
  const ln = x.lines || {};
  const val = emp === "ingen" ? x.work_min : (ln.timer || ln.overtimer || (emp === "fast" ? x.work_min : 0));
  const ab = x.absence;
  const both = ab && x.work_min > 0;            // arbejde + fravær resten af dagen
  let cls = `text-slate-700 dark:text-slate-200 ${free ? FREE_BG : ""}`;
  if (red) cls = "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
  // 2026-10-25: Fravær på en fridag (tæller ikke) vises som fridagen (skraveret, grå tekst) - før gråt uden forklaring.
  else if (ab && !x.work_min) cls = ab.counts === false ? `${FREE_BG} text-slate-400 dark:text-slate-500` : "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200";
  else if (orange) cls = "bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100";
  else if (both) cls = "bg-sky-50 text-slate-800 dark:bg-sky-900/20 dark:text-slate-100";
  const empty = x.flags.some((f) => f.code === "empty_day");
  const main = x.open && !x.work_min ? (red ? "Ind" : "…") : val ? hrs1(val) : ab ? absShort(ab) : empty ? "–" : "";
  const tip = [
    both ? `${hrs(x.work_min)} t arbejde + ${ab.label} resten af dagen${ab.counts === false ? " (tæller ikke)" : ab.reg_minutes ? ` (${hrs(ab.reg_minutes)} t)` : ""}` : "",
    ...x.flags.map((f) => f.text),
    free && !x.work_min && !ab ? (x.holiday ? `${x.holiday} (fri)` : "Fridag efter skemaet") : "",
    ab && !x.work_min ? (ab.counts === false ? `${ab.label} på en fridag (efter skemaet eller helligdag) - tæller ikke` : `${ab.label}`) : "",
    // 2026-10-25: Egne lønlinjer på dagen (fx udetillæg) - før så man dem kun ved at åbne dagen.
    ...(x.extra || []).map((l) => `${l.name}: ${String(l.qty).replace(".", ",")} ${l.unit}`),
  ].filter(Boolean).join("\n");
  return (
    <td className={`p-0.5 ${monday ? "border-l border-slate-100 dark:border-slate-700" : ""}`}>
      <button onClick={onClick} className={`relative w-full h-9 rounded-md text-[11px] font-semibold tabular-nums leading-none hover:ring-2 hover:ring-emerald-400 flex flex-col items-center justify-center gap-0.5 ${cls}`}
        data-testid={`payroll-cell-${x.date}`} title={tip}>
        <span>{main}</span>
        {both && <span className={`text-[9px] font-bold ${ab.counts === false ? "text-slate-400 line-through" : "text-sky-700 dark:text-sky-300"}`}>+{absShort(ab)}</span>}
        {blue && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-violet-500" />}
        {orange && !red && <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />}
      </button>
    </td>
  );
}

function Drawer({ title, sub, onClose, children, testid, head, footer }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid={testid}>
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col">
        <div className="z-10 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-700 px-5 py-3 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-slate-900 dark:text-white truncate">{title}</div>
            {sub && <div className="text-xs text-slate-500">{sub}</div>}
          </div>
          {head}
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Luk"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">{children}</div>
        {footer && <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 bg-white dark:bg-slate-900">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

function Section({ title, children, right }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{title}</h4>
        {right}
      </div>
      {children}
    </section>
  );
}

function LineChips({ lines, labels }) {
  const ks = Object.keys(lines || {}).filter((k) => lines[k]);
  if (!ks.length) return <div className="text-sm text-slate-500">Ingen timer til løn.</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {ks.map((k) => (
        <span key={k} className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold">
          {labels[k] || k}: <span className="tabular-nums">{hrs(lines[k])}</span>
        </span>
      ))}
    </div>
  );
}

const hm = (t) => { const [a, b] = String(t || "").split(":").map(Number); return Number.isFinite(a) && Number.isFinite(b) ? a * 60 + b : null; };
const fmtShort = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("da-DK", { day: "numeric", month: "short" }) : "");
const whenTxt = (iso) => (iso ? new Date(iso).toLocaleString("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// Vagterne på dagen, som de kan rettes: én række pr. vagt (første start, sidste slut).
// 2026-10-25: Dagsvinduet viser de stemplinger, der faktisk er gemt (én række pr.
// stempling) - før viste det de beregnede vagter, så 00:07-09:54 og 11:22-11:23 stod
// som én vagt 00:07-11:23, og en stempling på 1 minut kunne ikke slettes alene.
function shiftsOf(day) {
  const work = (day.entries || []).filter((e) => (e.slice_type || "work") === "work");
  if (work.length) {
    return work.map((e) => ({ key: e.id, ids: [e.id], firstId: e.id, lastId: e.id, open: !e.end_time,
      start: e.start_time || "", end: e.end_time || "", nextDay: !!e.end_next_day,
      orig: { start: e.start_time || "", end: e.end_time || "", nextDay: !!e.end_next_day } }))
      .sort((a, b) => (hm(a.start) ?? 0) - (hm(b.start) ?? 0));
  }
  const list = day.blocks.map((b) => ({
    key: b.ids.join(","), ids: b.ids, firstId: b.ids[0], lastId: b.ids[b.ids.length - 1],
    start: b.raw_start, end: b.raw_end, nextDay: !!b.end_next_day, orig: { start: b.raw_start, end: b.raw_end, nextDay: !!b.end_next_day },
  }));
  for (const e of day.open_entries || []) {
    if (e.slice_type === "pause") continue;
    list.push({ key: e.id, ids: [e.id], firstId: e.id, lastId: e.id, open: true, start: e.start_time || "", end: "", nextDay: false,
      orig: { start: e.start_time || "", end: "", nextDay: false } });
  }
  return list.sort((a, b) => (hm(a.start) ?? 0) - (hm(b.start) ?? 0));
}

// Hvem har oprettet/rettet stemplerne (vises under vagten).
// 2026-10-24 (B17a): Chaufførens egen rettelse stod som "Oprettet af en
// administrator" (manual_type blev ændret). Nu står den, der faktisk gjorde det.
function auditOf(day, ids) {
  const out = [];
  for (const e of (day.entries || []).filter((x) => ids.includes(x.id))) {
    const byAdmin = e.created_by_role === "logistics_admin" || e.created_by_role === "super_admin";
    const when = (t) => (t ? ` ${whenTxt(t)}` : "");
    if (e.manual && e.created_by_role === "driver") out.push(`Stemplet manuelt af chaufføren${when(e.created_at)}${e.reason ? `: ${e.reason}` : ""}`);
    else if (e.manual && byAdmin) out.push(`Oprettet af ${e.created_by_name || "en administrator"}${when(e.created_at)}${e.reason ? `: ${e.reason}` : ""}`);
    if (e.ud_created_by_role && e.ud_reason) out.push(`Udstemplet manuelt af ${e.ud_created_by_role === "driver" ? "chaufføren" : (e.ud_created_by_name || "en administrator")}: ${e.ud_reason}`);
    if (e.edited_by_driver) {
      const o = e.driver_original;
      out.push(`Rettet af chaufføren${when(e.edited_at)}${o ? ` (før ${o.start_time}–${o.end_time || "åben"}${o.date && o.date !== day.date ? ` ${o.date}` : ""})` : ""}`);
    }
    if (e.edited_by_admin) {
      const o = e.admin_original;
      out.push(`Rettet af ${e.edited_by_admin_name || "en administrator"}${when(e.edited_at)}${o ? ` (før ${o.start_time}–${o.end_time || "åben"}${o.end_next_day ? " +1" : ""}${o.date && o.date !== day.date ? ` ${o.date}` : ""})` : ""}${e.reason ? `: ${e.reason}` : ""}`);
    }
    if (e.overnight_confirmed) out.push("Nattevagt bekræftet af chaufføren");
    if (e.queued || e.ud_queued) out.push("Stemplet uden net (sendt senere)");
  }
  return [...new Set(out)];
}

function DayDrawer({ period, driver, day, onClose, onChanged, nav }) {
  const labels = Object.fromEntries(period.lines);
  const emp = driver.profile.employment;
  // 2026-10-24 (F3): En afløser med et skema ("fast afløser") har en normal dag.
  const noNorm = emp === "ingen" || (emp === "afloeser" && !driver.profile.schedule_id);
  const types = (period.absence_types || []).filter((x) => x.active || x.code === day.absence?.code);
  const locked = period.approval && !period.approval.trial;
  const [shifts, setShifts] = useState(() => shiftsOf(day));
  const adj0 = { extra: day.adjustment?.extra_pause_min || 0, tilkald: !!day.adjustment?.tilkald };
  const [adj, setAdj] = useState(adj0);
  // 2026-10-24 (C1): Firmaets egne lønlinjer på dagen (udetillæg, kursus ...).
  const xdefs = (period.rules.extra_lines || []).filter((x) => !x.auto && (x.active || (day.adjustment?.lines || []).some((l) => l.code === x.code)));
  const lines0 = (day.adjustment?.lines || []).map((l) => ({ code: l.code, qty: String(l.qty).replace(".", ","), note: l.note || "" }));
  const [xl, setXl] = useState(lines0);
  // Fravær: keep = som det er, remove = fjernes, set = registreres/ændres
  const [abs, setAbs] = useState({ mode: "keep", code: day.absence?.code || "", until: day.date, hours: day.absence?.hours ?? "", ownHours: day.absence?.hours != null });
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const live = shifts.filter((x) => !x.deleted);
  const shiftsDirty = shifts.some((x) => x.deleted || x.isNew || x.start !== x.orig.start || x.end !== x.orig.end || x.nextDay !== x.orig.nextDay);
  const linesDirty = JSON.stringify(xl.filter((l) => l.code && l.qty !== "")) !== JSON.stringify(lines0);
  const adjDirty = Number(adj.extra || 0) !== Number(adj0.extra) || adj.tilkald !== adj0.tilkald || linesDirty;
  const absDirty = abs.mode === "remove" || (abs.mode === "set" && !!abs.code);
  const dirty = shiftsDirty || adjDirty || absDirty;
  // 2026-10-24: I en godkendt periode kræver også fravær en begrundelse
  // (serveren kræver den nu alle steder, rettelsen kan laves).
  const needReason = shiftsDirty || adjDirty || (locked && absDirty);

  // Arbejde efter rettelserne (rå minutter, før pause) - til forklaringen af fraværet
  const plannedWork = live.reduce((sum, x) => {
    const a = hm(x.start), b = hm(x.end);
    return a == null || b == null ? sum : sum + ((b - a + (x.nextDay ? 1440 : 0)) || 0);
  }, 0);
  const willHaveWork = live.some((x) => x.start && x.end) || (shiftsDirty ? false : day.work_min > 0);
  const hasFullAbsence = day.absence && day.absence.part !== "rest" && abs.mode !== "remove";

  const problems = [];
  for (const x of live) {
    if (!x.start || (!x.end && !x.open)) problems.push("Udfyld start og slut på alle vagter");
    else if (x.end && hm(x.end) <= hm(x.start) && !x.nextDay) problems.push(`Vagten ${x.start}–${x.end} slutter før den starter – sæt "slutter næste dag" ved natvagt`);
  }
  if (needReason && !reason.trim()) problems.push("Skriv en begrundelse");
  for (const l of xl) {
    if (l.code && l.qty !== "" && !Number.isFinite(Number(String(l.qty).replace(",", ".")))) problems.push(`Ugyldigt antal på lønlinjen "${(xdefs.find((x) => x.code === l.code) || {}).name || l.code}"`);
  }
  const abType = types.find((t) => t.code === abs.code);
  const norm = day.day_norm_min || 0;
  // 2026-10-25: Skemaet med navn og arbejdsdage - så "fri" kan forklares (før: "normal dag fri").
  const sched = (period.rules.schedules || []).find((x) => x.id === (driver.profile.schedule_id || period.rules.default_schedule)) || (period.rules.schedules || [])[0];
  const workDays = sched ? WEEKDAYS.filter(([k]) => Number(sched.hours?.[k]) > 0).map(([, l]) => l.toLowerCase()).join(", ") : "";
  const weekdayName = longDate(day.date).split(" ")[0].replace(/^./, (c) => c.toUpperCase());
  const freeDay = !noNorm && !norm;
  const freeWhy = day.holiday ? `${day.holiday} er en helligdag` : `${weekdayName} er ikke en arbejdsdag i ${driver.name}s skema${sched ? ` («${sched.name}»: ${workDays || "ingen dage"})` : ""}`;
  const workForAbs = shiftsDirty ? Math.max(0, plannedWork - pauseNeed(period.rules.pause, plannedWork)) : day.work_min;
  let absInfo = null;
  let absBlocked = "";
  if (abs.mode === "set" && abType) {
    if (noNorm) {
      absInfo = `${driver.employment_label} har ingen normal dag – fraværet registreres kun (ingen timer).`;
      if (willHaveWork) absBlocked = "Der er arbejde denne dag – fravær kan kun registreres på dage uden arbejde.";
    } else if (abs.ownHours && abs.hours !== "") {
      absInfo = `${abType.name}: ${String(abs.hours).replace(".", ",")} t (aftalt antal timer).`;
    } else if (!willHaveWork) {
      absInfo = norm ? `Hele dagen: ${hrs(norm)} t efter chaufførens skema.` : "Dagen er ikke en arbejdsdag i skemaet (eller en helligdag) – fraværet registreres, men giver ingen timer.";
    } else if (norm - workForAbs > 0) {
      absInfo = `Resten af dagen: ${hrs(norm - workForAbs)} t (der er arbejdet ca. ${hrs(workForAbs)} t af ${hrs(norm)} t).`;
    } else {
      absBlocked = `Hele den normale dag er arbejdet (${hrs(workForAbs)} af ${hrs(norm)} t) – der er ikke noget fravær at registrere. Ret vagten, eller skriv et aftalt antal timer.`;
    }
  }
  if (absBlocked) problems.push(absBlocked);

  const guard = (fn) => () => {
    if (dirty && !window.confirm("Du har ændringer, der ikke er gemt. Fortsæt uden at gemme?")) return;
    fn();
  };

  const save = async () => {
    if (problems.length) { toast.error(problems[0]); return; }
    setSaving(true);
    const why = reason.trim();
    const step = async (label, fn) => {
      try { await fn(); } catch (e) { throw new Error(`${label}: ${errText(e, "fejlede")}`); }
    };
    try {
      // 1) Fravær der fjernes først (så arbejde kan tilføjes)
      if (abs.mode === "remove" && day.absence?.id) await step("Fjern fravær", () => axios.delete(`${API}/payroll/absences/${day.absence.id}`, { params: { reason: why } }));
      // 2) Vagter. Har dagen fravær hele dagen, bliver det til resten af dagen.
      let convert = hasFullAbsence;
      for (const x of shifts) {
        const flag = convert && !x.deleted ? { absence_to_rest: true } : {};
        if (x.deleted && !x.isNew) {
          for (const id of x.ids) await step(`Slet vagt ${x.orig.start}`, () => axios.delete(`${API}/admin/time-entries/${id}`, { params: { reason: why } }));
        } else if (x.isNew && !x.deleted) {
          await step(`Ny vagt ${x.start}`, () => axios.post(`${API}/driver/time-entries`, { driver_id: driver.id, date: day.date, start_time: x.start, end_time: x.end,
            end_next_day: x.nextDay, reason: why, slice_type: "work", ...flag }));
          if (flag.absence_to_rest) convert = false;
        } else if (!x.deleted) {
          const sChanged = x.start !== x.orig.start;
          const eChanged = x.end !== x.orig.end || x.nextDay !== x.orig.nextDay;
          if (sChanged && (x.firstId !== x.lastId || !eChanged)) {
            await step(`Start ${x.start}`, () => axios.patch(`${API}/admin/time-entries/${x.firstId}`, { start_time: x.start, reason: why, ...flag }));
          }
          if (eChanged) {
            await step(`Slut ${x.end}`, () => axios.patch(`${API}/admin/time-entries/${x.lastId}`, {
              ...(sChanged && x.firstId === x.lastId ? { start_time: x.start } : {}), end_time: x.end, end_next_day: x.nextDay, reason: why, ...flag }));
          }
          if ((sChanged || eChanged) && flag.absence_to_rest) convert = false;
        }
      }
      // 3) Fravær der registreres/ændres (serveren afgør hele dagen / resten af dagen)
      if (abs.mode === "set" && abs.code) {
        const multi = (abs.until || day.date) > day.date;
        await step("Fravær", () => axios.post(`${API}/payroll/absences`, { driver_id: driver.id, date_from: day.date, date_to: abs.until || day.date,
          code: abs.code, hours: abs.ownHours && !multi ? abs.hours : "", note: why }));
      }
      // 4) Rettelse af dagen (ekstra pause, tilkald, egne lønlinjer)
      if (adjDirty) {
        await step("Rettelse", () => axios.put(`${API}/payroll/adjustments`, { driver_id: driver.id, date: day.date,
          extra_pause_min: Number(adj.extra) || 0, tilkald: adj.tilkald, note: why || day.adjustment?.note || "",
          lines: xl.filter((l) => l.code && l.qty !== "").map((l) => ({ code: l.code, qty: Number(String(l.qty).replace(",", ".")), note: l.note || "" })) }));
      }
      toast.success("Dagen er gemt");
      await onChanged();
    } catch (e) {
      toast.error(e.message);
      await onChanged();   // vis det, der nåede at blive gemt
    } finally { setSaving(false); }
  };

  // Hurtige rettelser fra markeringerne (gemmes med det samme)
  const quick = async (fn, ok) => {
    if (dirty && !window.confirm("Dine andre ændringer gemmes ikke. Fortsæt?")) return;
    setSaving(true);
    try { await fn(); toast.success(ok); await onChanged(); }
    catch (e) { toast.error(errText(e, "Kunne ikke gemme")); }
    finally { setSaving(false); }
  };
  // I en godkendt periode skal der en begrundelse med (den står i rettelsen).
  const lockedWhy = () => {
    if (!locked) return "";
    const w = (window.prompt("Perioden er godkendt. Skriv en begrundelse (sendes med som rettelse i næste periode):") || "").trim();
    return w || null;
  };
  const absToRest = () => { const w = lockedWhy(); if (w === null) return; quick(() => axios.post(`${API}/payroll/absences`, { driver_id: driver.id, date_from: day.date, code: day.absence.code,
    hours: day.absence.hours ?? "", note: w || day.absence.note || "" }), "Fraværet dækker nu resten af dagen"); };
  const absRemove = () => { const w = lockedWhy(); if (w === null) return; quick(() => axios.delete(`${API}/payroll/absences/${day.absence.id}`, { params: { reason: w } }), "Fraværet er fjernet"); };
  const setShift = (key, patch) => setShifts((ls) => ls.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const tours = day.tours;
  const useTourTime = () => {
    if (!tours?.first || !tours?.last) return;
    if (!live.length) {
      setShifts((ls) => [...ls, { key: `new-${Date.now()}`, isNew: true, start: tours.first, end: tours.last, nextDay: false, orig: { start: "", end: "", nextDay: false }, ids: [] }]);
    } else {
      const first = live[0], last = live[live.length - 1];
      setShifts((ls) => ls.map((x) => (x.key === first.key ? { ...x, start: tours.first, ...(x.key === last.key ? { end: tours.last } : {}) }
        : x.key === last.key ? { ...x, end: tours.last } : x)));
    }
    if (!reason) setReason("Efter tur-tid");
  };
  const addShift = () => setShifts((ls) => [...ls, { key: `new-${Date.now()}`, isNew: true, start: "", end: "", nextDay: false, orig: { start: "", end: "", nextDay: false }, ids: [] }]);

  const flags = day.flags.filter((f) => f.code !== "corrected");
  const corrected = day.flags.some((f) => f.code === "corrected");

  return (
    <Drawer title={`${driver.name} · ${longDate(day.date)}`} testid="payroll-day-drawer"
      sub={[driver.employment_label, noNorm ? "" : norm ? `arbejdsdag ${hrs(norm)} t${sched ? ` («${sched.name}»)` : ""}` : `fridag${day.holiday ? "" : " efter skemaet"}${sched && !day.holiday ? ` («${sched.name}»)` : ""}`, day.holiday].filter(Boolean).join(" · ")}
      onClose={guard(onClose)}
      head={nav && (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" disabled={!nav.prev} onClick={guard(nav.prev)} aria-label="Dagen før"><ChevronLeft className="w-5 h-5" /></button>
          <button className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" disabled={!nav.next} onClick={guard(nav.next)} aria-label="Dagen efter"><ChevronRight className="w-5 h-5" /></button>
        </div>
      )}
      footer={
        <div className="space-y-2">
          <input className={input} placeholder={needReason ? "Begrundelse (skal udfyldes) – fx GPS viser slut 16:00" : "Begrundelse / bemærkning"} value={reason}
            onChange={(e) => setReason(e.target.value)} data-testid="payroll-edit-reason" />
          {dirty && problems.length > 0 && <div className="text-xs text-amber-700">{problems[0]}</div>}
          <div className="flex items-center gap-2">
            <button className={btnPrimary} disabled={saving || !dirty} onClick={save} data-testid="payroll-day-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem ændringer
            </button>
            {dirty && <button className={btnGhost} disabled={saving} onClick={() => { setShifts(shiftsOf(day)); setAdj(adj0); setXl(lines0); setAbs({ mode: "keep", code: day.absence?.code || "", until: day.date, hours: day.absence?.hours ?? "", ownHours: day.absence?.hours != null }); setReason(""); }}>Fortryd</button>}
            {nav?.nextCheck && <button className="ml-auto text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1" onClick={guard(nav.nextCheck)} data-testid="payroll-next-check">Næste der skal tjekkes <ChevronRight className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
      }>
      {locked && <div className="rounded-lg bg-sky-50 text-sky-900 text-xs px-3 py-2">Perioden er godkendt. Retter du noget, kommer forskellen med som rettelse i næste periode.</div>}

      {flags.length > 0 && (
        <div className="space-y-1.5">
          {flags.map((f, i) => (
            <div key={i} className={`text-sm rounded-lg px-3 py-2 ${f.level === "red" ? "bg-red-50 text-red-800" : f.level === "orange" ? "bg-amber-50 text-amber-900" : "bg-sky-50 text-sky-900"}`}>
              <div>{f.text}</div>
              {(f.code === "absence_conflict" || f.code === "absence_zero") && day.absence && (
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {f.code === "absence_conflict" && !noNorm && <button className="text-xs font-semibold underline" disabled={saving} onClick={absToRest}>Fraværet er kun resten af dagen</button>}
                  <button className="text-xs font-semibold underline" disabled={saving} onClick={absRemove}>Fjern fraværet</button>
                </div>
              )}
              {/* 2026-10-25: Kun når tur-tiden kan bruges: én vagt, og turene har en start og en senere slut (før kunne 22:32–22:32 lægges ind). */}
              {f.code === "tour_time" && tours?.first && tours?.last && tours.first < tours.last && live.length <= 1 && !/ingen ture/.test(f.text) &&
                <button className="text-xs font-semibold underline mt-1" onClick={useTourTime}>Brug tur-tid ({tours.first}–{tours.last})</button>}
              {f.code === "empty_day" && (
                <div className="flex flex-wrap gap-3 mt-1.5">
                  <button className="text-xs font-semibold underline" onClick={() => setAbs((a) => ({ ...a, mode: "set", code: "" }))} data-testid="payroll-empty-absence">Registrér fravær</button>
                  <button className="text-xs font-semibold underline" onClick={addShift}>Tilføj vagt</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Section title="Arbejdstid" right={<button className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1" onClick={addShift} data-testid="payroll-add-shift"><Plus className="w-3.5 h-3.5" />Tilføj vagt</button>}>
        {hasFullAbsence && live.length > 0 && (shiftsDirty) && (
          <div className="text-xs rounded-lg bg-sky-50 text-sky-900 px-3 py-2">Dagen er registreret som {day.absence.label} hele dagen. Når du gemmer arbejdstid, dækker fraværet kun resten af dagen.</div>
        )}
        {/* 2026-10-25: En vagt fra i går, der fortsatte efter midnat, hører til i går (overtid,
            tillæg og pause regnes dér) - her vises den kun, så dagen giver mening. */}
        {(() => {
          const pd = new Date(day.date + "T12:00:00"); pd.setDate(pd.getDate() - 1);
          const prev = (driver.days || []).find((x) => x.date === localDate(pd));
          return (prev?.blocks || []).filter((b) => b.end_next_day).map((b, k) => (
            <div key={k} className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20 px-3 py-2 text-xs text-emerald-900 dark:text-emerald-200" data-testid="payroll-cont-note">
              ← Vagten fra {dkShort(prev.date)} kl. {b.raw_start} fortsætter til kl. {b.raw_end} i dag. Den hører til {dkShort(prev.date)} - overtid, tillæg og pause regnes dér, og den rettes dér.{" "}
              {nav?.prev && <button type="button" className="underline font-semibold" onClick={nav.prev}>Gå til {dkShort(prev.date)}</button>}
            </div>
          ));
        })()}
        {shifts.length === 0 && (
          <div className="text-sm text-slate-500">Ingen stempling denne dag.{tours?.first && tours?.last ? "" : " Tilføj en vagt, hvis chaufføren har arbejdet."}</div>
        )}
        {shifts.map((x, n) => (
          <React.Fragment key={x.key}>
          {n > 0 && !x.deleted && (() => {
            // Hvad betyder hullet? Under 3 t = pause i samme vagt, ellers en ny vagt (som beregningen).
            const prev = [...shifts.slice(0, n)].reverse().find((y) => !y.deleted && y.end);
            const a = prev ? hm(prev.end) + (prev.nextDay ? 1440 : 0) : null, b = hm(x.start);
            if (a == null || b == null) return null;
            const gap = b - a;
            const txt = gap < 0 ? "Overlapper stemplingen før - ret tiderne" : gap === 0 ? "Fortsætter i samme minut"
              : gap < 180 ? `${fmtGap(gap)} imellem - tæller som pause (samme vagt)` : `${fmtGap(gap)} imellem - en ny vagt`;
            return <div className={`text-[11px] px-1 ${gap < 0 ? "text-red-700 font-semibold" : "text-slate-500"}`} data-testid="payroll-shift-gap">↕ {txt}</div>;
          })()}
          <div className={`rounded-xl border p-3 space-y-2 ${x.deleted ? "opacity-50 border-dashed border-slate-300" : x.open && !x.end ? "border-red-300 bg-red-50/40" : "border-slate-200 dark:border-slate-700"}`} data-testid="payroll-shift">
            <div className="flex items-end gap-2">
              <div className="flex-1"><label className={label}>{shifts.length > 1 ? `Stempling ${n + 1} – ind` : "Ind"}</label>
                <input type="time" className={input} value={x.start} disabled={x.deleted} onChange={(e) => setShift(x.key, { start: e.target.value })} data-testid="payroll-edit-start" /></div>
              <div className="flex-1"><label className={label}>Ud{x.open && !x.end ? " (mangler)" : ""}</label>
                <input type="time" className={input} value={x.end} disabled={x.deleted} onChange={(e) => setShift(x.key, { end: e.target.value })} data-testid="payroll-edit-end" /></div>
              <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" onClick={() => (x.isNew ? setShifts((ls) => ls.filter((y) => y.key !== x.key)) : setShift(x.key, { deleted: !x.deleted }))}
                aria-label={x.deleted ? "Fortryd slet" : "Slet stemplingen"} title={x.deleted ? "Fortryd slet" : "Slet stemplingen"}>
                {x.deleted ? <RotateCcw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"><input type="checkbox" checked={x.nextDay} disabled={x.deleted} onChange={(e) => setShift(x.key, { nextDay: e.target.checked })} /> Slutter næste dag (natvagt)</label>
            {x.deleted && <div className="text-xs text-red-700">Slettes, når du gemmer.</div>}
            {!x.isNew && auditOf(day, x.ids).map((t, i) => <div key={i} className="text-[11px] text-slate-500">{t}</div>)}
          </div>
          </React.Fragment>
        ))}
        {/* Tur-tid passer kun på én stempling - ved flere ville første ind og sidste ud blive flyttet hen over de andre. */}
        {tours?.first && tours?.last && tours.first < tours.last && live.length <= 1 && (
          <button type="button" className="text-xs font-semibold text-emerald-700 hover:underline text-left" onClick={useTourTime} data-testid="payroll-use-tour-time">
            {live.length ? `Sæt arbejdstiden til tur-tid (${tours.first}–${tours.last})` : `Opret vagt efter tur-tid (${tours.first}–${tours.last})`}
          </button>
        )}
        {tours?.first && tours?.last && (
          <div className="text-[11px] text-slate-500">Tur-tid er første tur startet til sidste tur afsluttet - kørsel til første og fra sidste adresse er ikke med. Gemmes først med "Gem ændringer".</div>
        )}
        {corrected && !shifts.some((x) => auditOf(day, x.ids).length) && day.adjustment?.note && <div className="text-[11px] text-slate-500">Rettelse: {day.adjustment.note}</div>}
      </Section>

      <Section title="Fravær">
        {day.absence && abs.mode !== "set" && (
          <div className={`text-sm rounded-lg px-3 py-2 ${abs.mode === "remove" ? "bg-red-50 text-red-800 line-through" : "bg-sky-50 dark:bg-sky-900/20"}`} data-testid="payroll-absence-current">
            <b>{day.absence.label}</b>{day.absence.part === "rest" ? " – resten af dagen" : " – hele dagen"}
            {day.absence.counts === false ? " · tæller ikke (fridag)" : day.absence.minutes ? (emp === "fast" ? ` · ${hrs(day.absence.minutes)} t trækkes` : ` · ${hrs(day.absence.minutes)} t til løn`) : ` · ${payEffect(day.absence.pay, emp, noNorm).toLowerCase()}`}
            {day.absence.note && <div className="text-xs text-slate-500 no-underline">{day.absence.note}</div>}
          </div>
        )}
        {day.absence && day.absence.counts === false && freeDay && abs.mode === "keep" && (
          <div className="text-sm rounded-lg px-3 py-2 bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100" data-testid="payroll-absence-freeday">
            <b>{day.absence.label} på en fridag tæller ikke.</b> {freeWhy}, så dagen giver ingen timer og tælles ikke som fraværsdag.
            {!day.holiday && <> Er skemaet forkert, så ret det under Løn › Chauffører. Skulle fraværet ikke stå her, så fjern det.</>}
          </div>
        )}
        {day.absence && abs.mode === "keep" && (
          <div className="flex gap-3 text-xs font-semibold">
            <button className="text-emerald-700 hover:underline" onClick={() => setAbs((a) => ({ ...a, mode: "set" }))}>Ændr</button>
            <button className="text-red-700 hover:underline" onClick={() => setAbs((a) => ({ ...a, mode: "remove" }))} data-testid="payroll-absence-remove">Fjern</button>
          </div>
        )}
        {abs.mode === "remove" && <button className="text-xs font-semibold text-slate-600 hover:underline" onClick={() => setAbs((a) => ({ ...a, mode: "keep" }))}>Fortryd</button>}
        {!day.absence && abs.mode === "keep" && (
          noNorm && day.work_min > 0
            ? <div className="text-xs text-slate-500">{driver.employment_label} har ingen normal dag, så fravær kan kun registreres på dage uden arbejde.</div>
            : <button className={btnGhost} onClick={() => setAbs((a) => ({ ...a, mode: "set", code: "" }))} data-testid="payroll-absence-add"><Plus className="w-4 h-4" />Registrér fravær</button>
        )}
        {abs.mode === "set" && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {types.map((t) => (
                <button key={t.code} onClick={() => setAbs((a) => ({ ...a, code: t.code }))} className={`${chipBase} ${abs.code === t.code ? chipOn : chipOff}`}
                  data-testid={`payroll-absence-${t.code}`}>{t.name}</button>
              ))}
            </div>
            {!abs.code && <div className="text-xs text-slate-500">Vælg typen.</div>}
            {abType && (
              <>
                {absInfo && <div className="text-sm text-slate-700 dark:text-slate-200" data-testid="payroll-absence-info">{freeDay && !willHaveWork ? `${freeWhy} - fraværet registreres, men giver ingen timer.` : absInfo}</div>}
                {/* Typens almindelige betydning - ikke på en fridag, hvor den ikke gælder (før stod "giver skemaets timer" også her). */}
                {!(freeDay && !willHaveWork) && <div className="text-xs text-slate-500">{period.pay_modes?.[abType.pay]}: {payEffect(abType.pay, emp, noNorm).toLowerCase()}.</div>}
                <div className="flex flex-wrap items-end gap-3">
                  <div><label className={label}>Til og med</label><input type="date" className={inputFit} value={abs.until} min={day.date} onChange={(e) => setAbs((a) => ({ ...a, until: e.target.value }))} /></div>
                  {/* Aftalt antal timer gælder én dag (2 t til lægen - ikke 2 t hver dag i en periode). */}
                  {!noNorm && !(abs.until > day.date) && (
                    <label className="flex items-center gap-2 text-xs pb-2"><input type="checkbox" checked={abs.ownHours} onChange={(e) => setAbs((a) => ({ ...a, ownHours: e.target.checked }))} /> Aftalt antal timer</label>
                  )}
                  {abs.ownHours && !noNorm && !(abs.until > day.date) && <div><label className={label}>Timer</label><input className={`${inputFit} w-24`} value={abs.hours} onChange={(e) => setAbs((a) => ({ ...a, hours: e.target.value.replace(",", ".") }))} /></div>}
                </div>
                {abs.until > day.date && <div className="text-xs text-slate-500">Dagene efter registreres som hele dage – kun chaufførens arbejdsdage efter skemaet (ikke weekender og helligdage).</div>}
              </>
            )}
            <button className="text-xs font-semibold text-slate-600 hover:underline" onClick={() => setAbs({ mode: "keep", code: day.absence?.code || "", until: day.date, hours: day.absence?.hours ?? "", ownHours: day.absence?.hours != null })}>Annullér</button>
          </div>
        )}
      </Section>

      {xdefs.length > 0 && (
        <Section title="Lønlinjer" right={<button className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1" onClick={() => setXl((ls) => [...ls, { code: xdefs[0].code, qty: "", note: "" }])} data-testid="payroll-line-add"><Plus className="w-3.5 h-3.5" />Tilføj</button>}>
          {xl.length === 0 && <div className="text-xs text-slate-500">Fx udetillæg, kursusdag eller kranbevis - linjen kommer med i lønfilen.</div>}
          {xl.map((l, i) => {
            const dx = xdefs.find((x) => x.code === l.code);
            return (
              <div key={i} className="flex flex-wrap items-end gap-2" data-testid="payroll-line">
                <select className={inputFit} value={l.code} onChange={(e) => setXl((ls) => ls.map((y, j) => (j === i ? { ...y, code: e.target.value } : y)))}>
                  {xdefs.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                </select>
                <input className={`${inputFit} w-20`} value={l.qty} placeholder="Antal" onChange={(e) => setXl((ls) => ls.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} data-testid="payroll-line-qty" />
                <span className="text-xs text-slate-500 pb-2">{dx?.unit || ""}</span>
                <input className={`${inputFit} flex-1 min-w-[120px]`} value={l.note} placeholder="Note (fx Aarhus)" onChange={(e) => setXl((ls) => ls.map((y, j) => (j === i ? { ...y, note: e.target.value } : y)))} />
                <button className="p-2 text-slate-400" onClick={() => setXl((ls) => ls.filter((_, j) => j !== i))} aria-label="Fjern linjen"><X className="w-4 h-4" /></button>
              </div>
            );
          })}
        </Section>
      )}

      <Section title="Pause og tilkald">
        <div className="flex flex-wrap gap-3 items-end">
          <div><label className={label}>Ekstra pause (min)</label><input className={`${inputFit} w-28`} type="number" min="0" value={adj.extra} onChange={(e) => setAdj({ ...adj, extra: e.target.value })} data-testid="payroll-adj-pause" /></div>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={adj.tilkald} onChange={(e) => setAdj({ ...adj, tilkald: e.target.checked })} /> Tilkald (mindst {String(period.rules.guarantee_hours).replace(".", ",")} t)</label>
        </div>
        {day.adjustment?.note && <div className="text-[11px] text-slate-500">Bemærkning: {day.adjustment.note}</div>}
      </Section>

      <Section title="Beregning (som det er gemt)">
        <LineChips lines={day.lines} labels={labels} />
        {day.explain.length > 0 && (
          <ul className="mt-1 text-sm text-slate-600 dark:text-slate-300 space-y-0.5" data-testid="payroll-explain">
            {day.explain.map((e, i) => <li key={i}>· {e}</li>)}
          </ul>
        )}
      </Section>

      {tours && (
        <Section title="Ture">
          <div className="text-sm text-slate-700 dark:text-slate-200">
            Tur-tid <b>{tours.first || "?"}–{tours.last || "?"}</b> · {tours.count} {tours.count === 1 ? "tur" : "ture"}
            {tours.containers ? ` · ${tours.containers} containere` : ""}
            {day.vehicle ? ` · ${day.vehicle}` : ""}
            {period.can_money && tours.revenue ? ` · omsætning ${kr2(tours.revenue)}` : ""}
          </div>
          {tours.customers?.length > 0 && <div className="text-xs text-slate-500">{tours.customers.join(", ")}</div>}
        </Section>
      )}
    </Drawer>
  );
}

/**
 * 2026-10-24 (B10): Dagsvinduet åbnet udefra (Tidsregistrering › Ret). Samme
 * vindue og samme regler som i Lønperiode - stempler rettes kun ét sted.
 */
export function PayrollDayEditor({ driverId, date, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [cur, setCur] = useState(date);
  const [ver, setVer] = useState(0);
  const load = useCallback(async (ref) => {
    try {
      const r = await axios.get(`${API}/payroll/period`, { params: { ref, driver_id: driverId } });
      setData(r.data);
    } catch (e) { toast.error(errText(e, "Kunne ikke hente dagen")); onClose(); }
  }, [driverId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(date); }, [load, date]);
  if (!data) return createPortal(<div className="fixed inset-0 z-[80] bg-slate-900/40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-white" /></div>, document.body);
  const driver = data.drivers[0];
  const day = driver?.days.find((x) => x.date === cur);
  if (!driver || !day) return null;
  const dates = datesBetween(data.start, data.end);
  const i = dates.indexOf(cur);
  return (
    <DayDrawer key={`${cur}-${ver}`} period={data} driver={driver} day={day} onClose={onClose}
      onChanged={async () => { await load(cur); setVer((v) => v + 1); onChanged?.(); }}
      nav={{ prev: i > 0 ? () => setCur(dates[i - 1]) : null, next: i < dates.length - 1 ? () => setCur(dates[i + 1]) : null, nextCheck: null }} />
  );
}

function DriverSheet({ period, driver, onClose, canPayroll = false, onChanged }) {
  const labels = Object.fromEntries(period.lines);
  const t = driver.totals || {};
  const m = driver.money;
  const types = period.absence_types || [];
  const codes = { ...(period.rules.codes || {}), ...Object.fromEntries(types.map((x) => [x.code, x.export_code])) };
  const absDays = Object.entries(t.absence_days || {}).filter(([, n]) => n);
  return (
    <Drawer title={driver.name} sub={`${period.label} · ${driver.employment_label}${driver.profile.employee_no ? ` · nr. ${driver.profile.employee_no}` : ""}`}
      onClose={onClose} testid="payroll-driver-sheet">
      <Section title="Lønarter">
        <table className="w-full min-w-0 text-sm">
          <tbody>
            {period.lines.filter(([k]) => t[k]).map(([k, l]) => (
              <tr key={k} className="border-b border-slate-100 dark:border-slate-700">
                <td className="py-1.5">{l}</td>
                <td className="py-1.5 text-slate-500 text-xs">{codes[k] || ""}</td>
                <td className="py-1.5 text-right font-semibold tabular-nums">{hrs(t[k])} t</td>
              </tr>
            ))}
            {driver.profile.employment === "ingen" && <tr><td className="py-1.5">Arbejdstid (ingen lønberegning)</td><td /><td className="text-right font-semibold">{hrs(t.work)} t</td></tr>}
            {absDays.map(([k, n]) => (
              <tr key={`d-${k}`} className="border-b border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300">
                <td className="py-1.5">{(types.find((x) => x.code === k) || {}).name || k} (registrering)</td>
                <td className="py-1.5 text-slate-500 text-xs">{codes[k] || ""}</td>
                <td className="py-1.5 text-right font-semibold tabular-nums">{n} {n === 1 ? "dag" : "dage"}</td>
              </tr>
            ))}
            {Object.entries(t.absence_unpaid_days || {}).filter(([, n]) => n).map(([k, n]) => (
              <tr key={`u-${k}`} className="border-b border-slate-100 dark:border-slate-700 text-amber-800 dark:text-amber-200">
                <td className="py-1.5">{(types.find((x) => x.code === k) || {}).name || k} uden løn</td>
                <td className="py-1.5 text-slate-500 text-xs">{(types.find((x) => x.code === k) || {}).export_code_unpaid || ""}</td>
                <td className="py-1.5 text-right font-semibold tabular-nums">{n} {n === 1 ? "dag" : "dage"}</td>
              </tr>
            ))}
            {Object.entries(t.extra || {}).map(([k, q]) => {
              const dx = (period.rules.extra_lines || []).find((x) => x.code === k) || { name: k, unit: "" };
              return (
                <tr key={`x-${k}`} className="border-b border-slate-100 dark:border-slate-700">
                  <td className="py-1.5">{dx.name}</td>
                  <td className="py-1.5 text-slate-500 text-xs">{dx.export_code || ""}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{String(q).replace(".", ",")} {dx.unit}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="text-xs text-slate-500">{driver.days.filter((x) => x.work_min).length} arbejdsdage{t.garanti ? ` · heraf ${hrs(t.garanti)} t garanti` : ""}</div>
      </Section>
      {m && (
        <Section title="Kroner (oversigt – ikke en lønseddel)">
          <table className="w-full min-w-0 text-sm">
            <tbody>
              {Object.entries(m.lines).map(([k, v]) => <MoneyRow key={k} l={labels[k] || k} v={v} />)}
              {Object.entries(m.personal).map(([k, v]) => <MoneyRow key={k} l={`Tillæg: ${k}`} v={v} />)}
              <MoneyRow l="Timeløn med tillæg" v={m.subtotal_a} bold />
              {Object.entries(m.pct1).map(([k, v]) => <MoneyRow key={k} l={k} v={v} />)}
              {Object.entries(m.pct2).map(([k, v]) => <MoneyRow key={k} l={k} v={v} />)}
              {Object.entries(m.extra || {}).map(([k, v]) => <MoneyRow key={`x-${k}`} l={k} v={v} />)}
              <MoneyRow l="Lønomkostning i alt" v={m.total} bold />
              <MoneyRow l="Omsætning (ture)" v={driver.revenue} />
              <MoneyRow l="Dækningsbidrag (før bil)" v={driver.db} bold />
            </tbody>
          </table>
          {m.note && <div className="text-xs text-slate-500">{m.note}</div>}
          {driver.unpriced > 0 && <div className="text-xs text-amber-700">{driver.unpriced} ture mangler pris i Økonomi.</div>}
        </Section>
      )}
      {driver.legal.length > 0 && (
        <Section title="Arbejdstidsregler">
          {driver.legal.map((w, i) => <div key={i} className="text-sm rounded-lg bg-amber-50 text-amber-900 px-3 py-2">{new Date(w.date + "T12:00:00").toLocaleDateString("da-DK", { day: "numeric", month: "short" })}: {w.text}</div>)}
        </Section>
      )}
      <button className={btnGhost} onClick={() => buildPaySheetPdf({ period, drivers: [driver] }).save(`timeseddel_${driver.name}_${period.start}.pdf`)}>
        <FileText className="w-4 h-4" /> Timeseddel (PDF)
      </button>
      {canPayroll && driver.profile.employment !== "ingen" && <SettleSection period={period} driver={driver} onChanged={onChanged} />}
    </Drawer>
  );
}

// 2026-10-24 (C3): Afregn én chauffør, der stopper midt i perioden. Timerne
// fryses som ved en godkendelse, og lønfilen for chaufføren alene kan hentes
// med det samme. Periodens lønfil har bagefter kun det, der er ændret siden.
function SettleSection({ period, driver, onChanged }) {
  const st = period.settlements?.[driver.id];
  const ap = period.approval;
  const approved = ap && !ap.trial;
  const today = localDate();
  const lastWork = [...driver.days].reverse().find((x) => x.date <= today && (x.work_min || x.blocks?.length))?.date
    || (today < period.end ? today : period.end);
  const [open, setOpen] = useState(false);
  const [lastDay, setLastDay] = useState(lastWork);
  const [busy, setBusy] = useState(false);
  const settle = async (force = false) => {
    setBusy(true);
    try {
      await axios.post(`${API}/payroll/settle`, { driver_id: driver.id, start: period.start, end: period.end, last_day: lastDay, force });
      toast.success(`${driver.name} er afregnet - hent lønfilen nedenfor`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setBusy(false);
      if (e?.response?.status === 409 && d?.code === "red_days") {
        if (window.confirm(`${d.message}\n\nAfregn alligevel?`)) return settle(true);
        return;
      }
      toast.error(errText(e, "Kunne ikke afregne"));
    } finally { setBusy(false); }
  };
  const unsettle = async (confirmExported = false) => {
    if (!confirmExported && !window.confirm(`Fortryd afregningen af ${driver.name}? Timerne kommer så med i periodens almindelige lønfil.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/payroll/settle/${st.id}`, { params: confirmExported ? { confirm_exported: true } : {} });
      toast.success("Afregningen er fortrudt");
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setBusy(false);
      if (e?.response?.status === 409 && d?.code === "exported") {
        if (window.confirm(`${d.message}\n\nFortryd alligevel?`)) return unsettle(true);
        return;
      }
      toast.error(errText(e, "Kunne ikke fortryde"));
    } finally { setBusy(false); }
  };
  const dl = async (kind, force = false) => {
    try {
      await download(`${API}/payroll/period/export?start=${period.start}&end=${period.end}&kind=${kind}&driver_id=${driver.id}${force ? "&force=true" : ""}`, `${kind}.dat`);
      onChanged?.();
    } catch (e) {
      let d = e?.response?.data?.detail;
      if (!d && e?.response?.data instanceof Blob) { try { d = JSON.parse(await e.response.data.text()).detail; } catch (_) { d = null; } }
      if (e?.response?.status === 409 && d?.code === "missing_employee_no") {
        if (window.confirm(`${d.message}\n\nHent filen alligevel?`)) return dl(kind, true);
        return;
      }
      toast.error((d && (typeof d === "string" ? d : d.message)) || errText(e, "Download fejlede"), d?.code === "export_setup" ? { duration: 30000 } : undefined);
    }
  };
  if (approved && !st) return null;
  return (
    <Section title="Fratrædelse">
      {st ? (
        <div className="space-y-2" data-testid="payroll-settled-box">
          <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 px-3 py-2 text-sm text-sky-900 dark:text-sky-100">
            <b>Afregnet {whenTxt(st.at)}</b>{st.by ? ` af ${st.by}` : ""}{st.last_day ? ` · sidste arbejdsdag ${fmtShort(st.last_day)}` : ""}.
            {" "}Ændres en dag bagefter (fx en sygedag registreret senere), kommer forskellen med i periodens almindelige lønfil - ligesom rettelser til tidligere perioder.
          </div>
          <div className="flex flex-wrap gap-2">
            <button className={btnGhost} onClick={() => dl("lines")} data-testid="payroll-settle-dl-lines"><Download className="w-4 h-4" /> {payFileLabel(period.rules?.export?.system)} for {driver.name}</button>
            <button className={btnGhost} onClick={() => dl("times")} data-testid="payroll-settle-dl-times"><Download className="w-4 h-4" /> Godkendte tider (CSV)</button>
            {!approved && <button className={btnGhost} disabled={busy} onClick={() => unsettle()} data-testid="payroll-unsettle"><RotateCcw className="w-4 h-4" /> Fortryd afregning</button>}
          </div>
          {st.exported && <div className="text-[11px] text-slate-500">Lønfilen for {driver.name} er hentet.</div>}
          {period.rules?.export?.system === "danlon" && <div className="text-[11px] text-amber-700" data-testid="payroll-settle-danlon-note">Danløn erstatter tallene på en løntype, når en fil indlæses. Indlæs derfor fratrædelsens fil i en separat lønkørsel - ikke i den samme som periodens fil.</div>}
        </div>
      ) : !open ? (
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500">Stopper {driver.name} midt i perioden? Afregn nu, så lønnen kan laves uden at vente på, at perioden slutter.</p>
          <button className={btnGhost} onClick={() => setOpen(true)} data-testid="payroll-settle-open"><Lock className="w-4 h-4" /> Afregn nu (fratrædelse)</button>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid="payroll-settle-form">
          <label className="block">
            <span className={label}>Sidste arbejdsdag <span className="normal-case font-normal">(står på afregningen)</span></span>
            <input type="date" className={`${input} max-w-[200px]`} value={lastDay} min={period.start} max={period.end} onChange={(e) => setLastDay(e.target.value)} data-testid="payroll-settle-last-day" />
          </label>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Timer, tillæg og fravær for {driver.name} i perioden fryses som ved en godkendelse, og lønfilen for {driver.name} alene kan hentes med det samme.
            Når perioden godkendes, er {driver.name} kun med for det, der er ændret efter afregningen - så intet betales to gange.
            Rettelser til tidligere perioder kommer med i periodens almindelige lønfil.
          </p>
          <div className="flex gap-2">
            <button className={btnPrimary} disabled={busy} onClick={() => settle()} data-testid="payroll-settle"><Lock className="w-4 h-4" /> Afregn</button>
            <button className={btnGhost} onClick={() => setOpen(false)}>Annuller</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function MoneyRow({ l, v, bold }) {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-700">
      <td className={`py-1.5 ${bold ? "font-bold" : ""}`}>{l}</td>
      <td className={`py-1.5 text-right tabular-nums ${bold ? "font-bold" : ""}`}>{kr2(v)}</td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Chauffører (lønnummer, ansættelse, tillæg)
// ---------------------------------------------------------------------------
function PeopleView({ meta }) {
  const [rows, setRows] = useState(null);
  const [importing, setImporting] = useState(false);   // 2026-10-25: lønoplysninger fra Excel
  const [orig, setOrig] = useState({});
  const [dirty, setDirty] = useState({});
  // 2026-10-25: Et personligt tillæg, der er skrevet men ikke trykket "+" på, kommer
  // med, når der trykkes Gem (før var Gem gråt, og det skrevne forsvandt).
  const [supDraft, setSupDraft] = useState({});
  const draftFilled = (id) => !!((supDraft[id]?.name || "").trim() || String(supDraft[id]?.rate || "").trim());
  const load = useCallback(async () => {
    try {
      const d = (await axios.get(`${API}/payroll/drivers`)).data;
      setRows(d); setOrig(Object.fromEntries(d.map((x) => [x.id, x.profile]))); setDirty({});
    }
    catch (e) { toast.error(errText(e, "Kunne ikke hente chaufførerne")); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, profile: { ...r.profile, ...patch } } : r)));
    setDirty((d) => ({ ...d, [id]: true }));
  };
  // 2026-10-25: Vilkår (ansættelse, skema, lønsats, afspadsering) gælder fra en dato.
  const [termsAsk, setTermsAsk] = useState(null);   // {r, changed, mode: "from"|"fix", from}
  // 2026-10-29: Datoen for en ny egen timeløn vælges i en dialog (datovælger, standard i dag) - ikke som fritekst.
  const [rateAsk, setRateAsk] = useState(null);     // {text, date, resolve}
  const askRateDate = (text) => new Promise((resolve) => setRateAsk({ text, date: localDate(), resolve }));
  // 2026-10-29 (effective dating som SF): rækken viser dagens aftale. Fra historikken kan en anden aftale (fx en
  // kommende) lægges i rækken med "Ret" - så gemmes ændringerne som en rettelse af netop den aftale.
  const [editFrom, setEditFrom] = useState({});     // {[driverId]: valid_from}
  const [histOpen, setHistOpen] = useState({});
  const TERM_FIELDS = ["employment", "schedule_id", "pay_group_id", "overtime_mode", "pay_profile_id", "time_profile_id"];
  const startEdit = (r, vf) => {
    const term = (orig[r.id]?.terms || []).find((x) => x.valid_from === vf);
    if (!term) return;
    setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, profile: { ...x.profile, ...Object.fromEntries(TERM_FIELDS.map((k) => [k, term[k] || ""])) } } : x)));
    setEditFrom((x) => ({ ...x, [r.id]: vf }));
    setDirty((d) => ({ ...d, [r.id]: false }));
  };
  const cancelEdit = (r) => {
    const o = orig[r.id] || {};
    setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, profile: { ...x.profile, ...Object.fromEntries(TERM_FIELDS.map((k) => [k, o[k] || ""])) } } : x)));
    setEditFrom((x) => ({ ...x, [r.id]: null }));
    setDirty((d) => ({ ...d, [r.id]: false }));
  };
  const deleteRate = async (r, vf) => {
    if (!window.confirm(`Slet egen timeløn fra ${dkDate(vf)} for ${r.name}? Satsen før gælder så i stedet.`)) return;
    try {
      const res = await axios.delete(`${API}/payroll/drivers/${r.id}/rates/${vf}`);
      setOrig((x) => ({ ...x, [r.id]: res.data.profile }));
      toast.success("Satsen er slettet");
      load();
    } catch (e) { toast.error(errText(e, "Kunne ikke slette")); }
  };
  const deleteTerm = async (r, vf) => {
    if (!window.confirm(`Slet aftalen fra ${dkDate(vf)} for ${r.name}? Aftalen før gælder så i stedet.`)) return;
    try {
      const res = await axios.delete(`${API}/payroll/drivers/${r.id}/terms/${vf}`);
      setOrig((x) => ({ ...x, [r.id]: res.data.profile }));
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, profile: res.data.profile } : x)));
      if (editFrom[r.id] === vf) setEditFrom((x) => ({ ...x, [r.id]: null }));
      toast.success("Aftalen er slettet");
    } catch (e) { toast.error(errText(e, "Kunne ikke slette")); }
  };
  const save = async (r0, termsFrom = null) => {
    let r = r0;
    if (draftFilled(r.id)) {
      const d = supDraft[r.id];
      const rate = parseDk(d.rate);
      if (!(d.name || "").trim() || !(rate > 0)) { toast.error(`${r.name}: skriv både navn og kr/t på det personlige tillæg (fx 12,50) - eller ryd felterne`); return; }
      r = { ...r, profile: { ...r.profile, supplements: [...(r.profile.supplements || []), { name: d.name.trim(), rate }] } };
    }
    // 2026-10-25: Ansættelse, skema, lønsats og afspadsering har "gælder fra" - spørg
    // (før: en ændring gjaldt altid hele den åbne periode, også dagene før).
    const o = orig[r.id] || {};
    const ef = editFrom[r.id] || null;
    const refT = ef ? ((o.terms || []).find((x) => x.valid_from === ef) || o) : o;
    const changed = TERM_FIELDS.filter((k) => (refT[k] || "") !== (r.profile[k] || ""));
    if (changed.length && termsFrom === null && !ef) {
      setTermsAsk({ r, changed, mode: "from", from: localDate() });
      return;
    }
    if ((o.hire_date || "") !== (r.profile.hire_date || "") && !window.confirm(`Ændringen af ${r.name}s ansættelsesdato gælder hele den åbne lønperiode (trin og anciennitet). Godkendte perioder ændres ikke. Gem?`)) return;
    // 2026-10-25: En ny egen timeløn gælder fra en dato - dagene før beholder den gamle.
    let rateFrom = "";
    const oldRate = o.base_rate ?? null;
    const newRate = r.profile.base_rate === "" || r.profile.base_rate == null ? null : Number(String(r.profile.base_rate).replace(",", "."));
    if (meta.can_money && newRate !== oldRate) {
      const ans = await askRateDate(`${r.name}s ${newRate == null ? "ændring (ingen egen timeløn)" : `nye timeløn (${String(newRate).replace(".", ",")} kr)`} - dagene før beholder den gamle.`);
      if (!ans) return;
      rateFrom = ans;
    }
    try {
      const res = await axios.put(`${API}/payroll/drivers/${r.id}`, { profile: r.profile, ...(rateFrom ? { base_rate_from: rateFrom } : {}),
        ...(termsFrom ? { terms_from: termsFrom } : {}), ...(ef && changed.length ? { terms_fix_date: ef } : {}) });
      setDirty((d) => ({ ...d, [r.id]: false }));
      setEditFrom((x) => ({ ...x, [r.id]: null }));
      setSupDraft((x) => ({ ...x, [r.id]: undefined }));
      setOrig((x) => ({ ...x, [r.id]: res.data.profile }));
      toast.success(ef && changed.length ? `${r.name}: aftalen fra ${dkDate(ef)} er rettet` : termsFrom ? `${r.name}: ny aftale fra ${dkDate(termsFrom)} er gemt` : `${r.name} er gemt`);
      if (meta.can_money) load();  // timelønnen i dag kan have ændret sig
      else setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, profile: res.data.profile } : x)));
    } catch (e) { toast.error(errText(e, "Kunne ikke gemme")); }
  };
  const scheds = meta.rules.schedules || [];
  const stdSched = scheds.find((x) => x.id === meta.rules.default_schedule) || scheds[0];
  const groups = meta.rules.money?.groups || [];
  const stdGroup = groups.find((g) => g.id === meta.rules.money?.default_group);
  const weekOf = (h) => WEEKDAYS.reduce((a, [d]) => a + (Number(h?.[d]) || 0), 0).toLocaleString("da-DK");
  if (!rows) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const TERM_LABEL = { employment: "ansættelse", schedule_id: "skema", pay_group_id: "lønsats", overtime_mode: "overtid (udbetales/afspadseres)",
    pay_profile_id: "profil for overtid og tillæg", time_profile_id: "profil for pause og afrunding" };
  const payProfiles = meta.rules.pay_profiles || [], timeProfiles = meta.rules.time_profiles || [];
  const hasProfiles = payProfiles.length > 0 || timeProfiles.length > 0;
  return (
    <>
    {rateAsk && createPortal(
      <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={() => { rateAsk.resolve(null); setRateAsk(null); }}>
        <div className={`${card} w-full max-w-md p-5 space-y-3`} onClick={(e) => e.stopPropagation()} data-testid="payroll-rate-ask">
          <div className="font-bold">Fra hvornår gælder timelønnen?</div>
          <div className="text-sm text-slate-600 dark:text-slate-300">{rateAsk.text}</div>
          <input type="date" className={inputFit} value={rateAsk.date} onChange={(e) => setRateAsk({ ...rateAsk, date: e.target.value })} data-testid="payroll-rate-date" />
          <div className="flex justify-end gap-2">
            <button className={btnGhost} onClick={() => { rateAsk.resolve(null); setRateAsk(null); }}>Annuller</button>
            <button className={btnPrimary} disabled={!/^\d{4}-\d{2}-\d{2}$/.test(rateAsk.date || "")} onClick={() => { const a = rateAsk; setRateAsk(null); a.resolve(a.date); }} data-testid="payroll-rate-ok"><Check className="w-4 h-4" /> OK</button>
          </div>
        </div>
      </div>, document.body)}
    {termsAsk && createPortal(
      <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={() => setTermsAsk(null)}>
        <div className={`${card} w-full max-w-md p-5 space-y-3`} onClick={(e) => e.stopPropagation()} data-testid="payroll-terms-ask">
          <div className="font-bold">Fra hvornår gælder ændringen?</div>
          <div className="text-sm text-slate-600 dark:text-slate-300">{termsAsk.r.name}: {termsAsk.changed.map((k) => TERM_LABEL[k]).join(" og ")} ændres. Vælg, om det er en ny aftale fra en dato - eller en rettelse af den, der gælder nu.</div>
          <label className="flex items-start gap-2 text-sm"><input type="radio" className="mt-1" checked={termsAsk.mode === "from"} onChange={() => setTermsAsk({ ...termsAsk, mode: "from" })} data-testid="payroll-terms-from" />
            <span><b>Ny aftale fra</b> <input type="date" className={`${inputFit} ml-1`} value={termsAsk.from} onChange={(e) => setTermsAsk({ ...termsAsk, mode: "from", from: e.target.value })} data-testid="payroll-terms-date" />
              <span className="block text-xs text-slate-500 mt-0.5">Dagene før beholder den gamle aftale. Ligger datoen i fremtiden, viser rækken dagens aftale indtil da - den nye står under Historik som "Kommende".</span></span></label>
          <label className="flex items-start gap-2 text-sm"><input type="radio" className="mt-1" checked={termsAsk.mode === "fix"} onChange={() => setTermsAsk({ ...termsAsk, mode: "fix" })} data-testid="payroll-terms-fix" />
            <span><b>Rettelse</b> af de vilkår, der gælder nu{(() => { const cf = (orig[termsAsk.r.id]?.terms || []).filter((t) => t.valid_from <= localDate()).slice(-1)[0]?.valid_from; return cf && cf > "2000-01-01" ? ` (fra ${dkShort(cf)})` : ""; })()} - de var forkerte
              <span className="block text-xs text-slate-500 mt-0.5">Gælder også de dage, der er gået i den åbne periode. Godkendte perioder ændres ikke - var de også forkerte, så brug "Regn om med rettede regler" på perioden.</span></span></label>
          <div className="flex justify-end gap-2">
            <button className={btnGhost} onClick={() => setTermsAsk(null)}>Annuller</button>
            <button className={btnPrimary} disabled={termsAsk.mode === "from" && !/^\d{4}-\d{2}-\d{2}$/.test(termsAsk.from || "")} data-testid="payroll-terms-ok"
              onClick={() => { const a = termsAsk; setTermsAsk(null); save(a.r, a.mode === "from" ? a.from : ""); }}><Check className="w-4 h-4" /> Gem</button>
          </div>
        </div>
      </div>, document.body)}
    <div className={`${card} overflow-x-auto`}>
      <div className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700 space-y-1">
        <div className="flex flex-wrap items-start gap-2">
          <span className="flex-1 min-w-[240px]">Lønnummeret skal være det samme som i lønsystemet. <b>Skemaet</b> er chaufførens normale arbejdsdag: overtid er tiden ud over skemaet, og en syge- eller feriedag giver skemaets timer.</span>
          <button className={btnGhost} onClick={() => setImporting(true)} data-testid="payroll-people-import"><FileSpreadsheet className="w-4 h-4" /> Importér fra Excel</button>
        </div>
        {importing && <ImportDialog kind="pay_profiles" title="Importér lønoplysninger" onClose={() => setImporting(false)} onDone={load} />}
        <div><b>Afløser</b>: mindst {String(meta.rules.guarantee_hours).replace(".", ",")} timer pr. indkald og ingen "mangler"-dage. Vælg et skema på en fast afløser, så syge- og feriedage giver skemaets timer.
          <b> Deltid</b>: overtid regnes efter fuld tid (timer op til fuld tid er merarbejde). <b>Fast løn</b>: kun overtid, tillæg og fradrag sendes.
          <b> Ingen lønberegning</b>: ejer eller disponent – tiden registreres bare.</div>
        {meta.can_money && <div><b>Lønsats</b> giver timelønnen (og trin efter ansættelsestid, hvis satsen har det). En <b>egen timeløn</b> går forud for lønsatsen.</div>}
        <div><b>Overtid afspadseres</b>: timerne ud over den normale dag sendes på lønarten "Overtimer til afspadsering" i stedet for "Timer" (lønsystemet fører saldoen).</div>
        <div><b>Gælder fra</b>: rækken viser den aftale, der gælder i dag. Ændrer du ansættelse, skema, overtid, lønsats eller profil og trykker Gem, spørger systemet, om det er en <b>ny aftale fra en dato</b> (fx deltid fra 1. januar - dagene før beholder den gamle) eller en <b>rettelse</b>. Under <b>Historik</b> ses alle aftaler - tidligere, nu og kommende - og en kommende aftale kan rettes eller slettes dér.</div>
      </div>
      <table className="w-full text-sm" data-testid="payroll-people">
        <thead className="bg-slate-50 dark:bg-slate-900 text-left text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2">Chauffør</th><th className="px-2 py-2">Lønnummer</th><th className="px-2 py-2">Ansættelse</th>
            <th className="px-2 py-2">Skema</th><th className="px-2 py-2">Ansat</th><th className="px-2 py-2">Overtid</th>
            {meta.can_money && <th className="px-2 py-2">Lønsats</th>}
            {meta.can_money && <th className="px-2 py-2">Egen timeløn</th>}
            {meta.can_money && <th className="px-2 py-2">Personlige tillæg (kr/t)</th>}
            {hasProfiles && <th className="px-2 py-2">Profiler</th>}
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const p = r.profile;
            const legacyDeltid = p.employment === "deltid" && !p.schedule_id && p.hours_per_day > 0;
            return (
              <React.Fragment key={r.id}>
              <tr className={`border-t border-slate-100 dark:border-slate-700 align-top ${r.active ? "" : "opacity-60"}`}>
                <td className="px-4 py-2 font-semibold">{r.name}{!r.active && <span className="text-xs text-slate-500"> (deaktiveret)</span>}
                  {r.pay_pending && <span className="block text-[10px] font-bold text-amber-700" data-testid={`payroll-pending-${r.id}`}>Mangler lønoplysninger</span>}
                  {editFrom[r.id] ? (
                    <span className="block text-[11px] font-bold text-sky-700 mt-0.5" data-testid={`payroll-editing-${r.id}`}>Retter aftalen fra {dkDate(editFrom[r.id])} · <button type="button" className="underline font-semibold" onClick={() => cancelEdit(r)}>annuller</button></span>
                  ) : (() => {
                    const o = orig[r.id] || {};
                    const all = [...(o.terms || []), ...(meta.can_money ? (o.base_rates || []) : [])];
                    const fut = all.map((x) => x.valid_from).filter((d) => d > localDate()).sort();
                    const n = new Set(all.map((x) => x.valid_from)).size;
                    return (
                      <button type="button" className={`block text-[11px] font-semibold mt-0.5 hover:underline ${fut.length ? "text-sky-700" : "text-slate-500"}`}
                        onClick={() => setHistOpen((h) => ({ ...h, [r.id]: !h[r.id] }))} data-testid={`payroll-hist-toggle-${r.id}`}>
                        {histOpen[r.id] ? "Skjul historik" : `Historik${n > 1 ? ` (${n})` : ""}${fut.length ? ` · kommende ${dkDate(fut[0])}` : ""}`}
                      </button>
                    );
                  })()}</td>
                <td className="px-2 py-2"><input className={`${inputFit} w-24`} value={p.employee_no} onChange={(e) => set(r.id, { employee_no: e.target.value })} data-testid={`payroll-no-${r.id}`} /></td>
                <td className="px-2 py-2">
                  <select className={inputFit} value={p.employment} onChange={(e) => set(r.id, { employment: e.target.value })} data-testid={`payroll-emp-${r.id}`}>
                    {Object.entries(meta.employment).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </td>
                <td className="px-2 py-2">
                  {p.employment === "ingen" ? <span className="text-slate-400 text-xs">–</span> : (
                    <>
                      <select className={`${inputFit} max-w-[200px]`} value={p.schedule_id || ""} onChange={(e) => set(r.id, { schedule_id: e.target.value })} data-testid={`payroll-sched-${r.id}`}>
                        <option value="">Standard ({stdSched?.name})</option>
                        {scheds.filter((x) => x.id !== stdSched?.id).map((x) => <option key={x.id} value={x.id}>{x.name} · {weekOf(x.hours)} t</option>)}
                      </select>
                      {legacyDeltid && <div className="text-[11px] text-amber-700 mt-0.5">Bruger {String(p.hours_per_day).replace(".", ",")} t/dag – vælg hellere et deltidsskema</div>}
                      {p.employment === "afloeser" && <div className="text-[11px] text-slate-500 mt-0.5">{p.schedule_id ? "Fast afløser: fravær giver skemaets timer" : "Uden skema: fravær giver ingen timer"}</div>}
                    </>
                  )}
                </td>
                <td className="px-2 py-2"><input type="date" className={inputFit} value={p.hire_date || ""} onChange={(e) => set(r.id, { hire_date: e.target.value })} data-testid={`payroll-hire-${r.id}`} /></td>
                <td className="px-2 py-2">
                  {["fuldtid", "deltid", "afloeser"].includes(p.employment) ? (
                    <select className={inputFit} value={p.overtime_mode || "pay"} onChange={(e) => set(r.id, { overtime_mode: e.target.value })} data-testid={`payroll-ot-mode-${r.id}`}>
                      <option value="pay">Udbetales</option>
                      <option value="bank">Afspadseres</option>
                    </select>
                  ) : <span className="text-slate-400 text-xs">–</span>}
                </td>
                {meta.can_money && (
                  <td className="px-2 py-2">
                    {groups.length ? (
                      <select className={`${inputFit} max-w-[200px]`} value={p.pay_group_id || ""} onChange={(e) => set(r.id, { pay_group_id: e.target.value })} data-testid={`payroll-group-${r.id}`}>
                        <option value="">{stdGroup ? `Standard (${stdGroup.name})` : "Ingen"}</option>
                        {groups.filter((g) => g.id !== stdGroup?.id).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    ) : <span className="text-slate-400 text-xs">Opret lønsatser under Regler</span>}
                    {r.rate_today != null && !dirty[r.id] && <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`payroll-rate-${r.id}`}>{kr2(r.rate_today)}/t i dag</div>}
                  </td>
                )}
                {meta.can_money && (
                  <td className="px-2 py-2">
                    <input className={`${inputFit} w-24`} value={p.base_rate ?? ""} placeholder="lønsatsen" onChange={(e) => set(r.id, { base_rate: e.target.value })} data-testid={`payroll-own-rate-${r.id}`} />
                    {(() => { const fut = (p.base_rates || []).filter((x) => x.valid_from > localDate()); const f = fut[0];
                      return f ? <div className="text-[10px] text-sky-700 mt-0.5 whitespace-nowrap" data-testid={`payroll-own-rate-next-${r.id}`}>Kommende: {f.rate == null ? "lønsatsen" : dk(f.rate)} fra {dkShort(f.valid_from)}</div> : null; })()}
                  </td>
                )}
                {meta.can_money && (
                  <td className="px-2 py-2">
                    <Supplements list={p.supplements} onChange={(supplements) => set(r.id, { supplements })}
                      draft={supDraft[r.id] || {}} setDraft={(d) => setSupDraft((x) => ({ ...x, [r.id]: d }))} testid={`payroll-sup-${r.id}`} />
                  </td>
                )}
                {hasProfiles && (
                  <td className="px-2 py-2 space-y-1">
                    {/* 2026-10-28: Profilerne - som skema og lønsats med "gælder fra" */}
                    {/* 2026-10-28: Begge vises altid (hver profil har sin egen del af reglerne). Deaktiverede kun, når de er valgt. */}
                    <select className={`${inputFit} max-w-[200px] block`} value={p.pay_profile_id || ""} onChange={(e) => set(r.id, { pay_profile_id: e.target.value })}
                      title={payProfiles.length ? "Overtid, helligdage og tillæg" : "Ingen profiler for overtid endnu - opret under Regler › 3"} data-testid={`payroll-payprof-${r.id}`}>
                      <option value="">Overtid: Standard</option>
                      {payProfiles.filter((x) => x.active !== false || x.id === p.pay_profile_id).map((x) => <option key={x.id} value={x.id}>Overtid: {x.name}{x.active === false ? " (deaktiveret)" : ""}</option>)}
                    </select>
                    <select className={`${inputFit} max-w-[200px] block`} value={p.time_profile_id || ""} onChange={(e) => set(r.id, { time_profile_id: e.target.value })}
                      title={timeProfiles.length ? "Pause, hvile og afrunding" : "Ingen profiler for pause endnu - opret under Regler › 4"} data-testid={`payroll-timeprof-${r.id}`}>
                      <option value="">Pause: Standard</option>
                      {timeProfiles.filter((x) => x.active !== false || x.id === p.time_profile_id).map((x) => <option key={x.id} value={x.id}>Pause: {x.name}{x.active === false ? " (deaktiveret)" : ""}</option>)}
                    </select>
                  </td>
                )}
                <td className="px-2 py-2 text-right">
                  <button className={dirty[r.id] || draftFilled(r.id) ? btnPrimary : btnGhost} disabled={!dirty[r.id] && !draftFilled(r.id)} onClick={() => save(r)} data-testid={`payroll-save-${r.id}`}
                    title={editFrom[r.id] ? `Gemmer som rettelse af aftalen fra ${dkDate(editFrom[r.id])}` : "Ændrer du vilkår, spørger systemet om det er en ny aftale fra en dato eller en rettelse"}>Gem</button>
                </td>
              </tr>
              {/* 2026-10-29 (effective dating): historikken i sin egen linje - hvert udsnit med Gælder nu / Kommende /
                  Historisk og HVAD der er ændret, så en kommende aftale aldrig ligner "ingenting skete". Felterne i
                  rækken ovenfor viser altid dagens udsnit. */}
              {histOpen[r.id] && (
                <tr className={r.active ? "" : "opacity-60"} data-testid={`payroll-history-${r.id}`}>
                  <td colSpan={20} className="px-4 pb-3 pt-0">
                    <TermsTimeline r={r} terms={orig[r.id]?.terms || []} rates={meta.can_money ? (orig[r.id]?.base_rates || []) : []}
                      meta={meta} scheds={scheds} stdSched={stdSched} groups={groups} payProfiles={payProfiles} timeProfiles={timeProfiles} hasProfiles={hasProfiles}
                      editFrom={editFrom[r.id] || null} onEdit={(vf) => startEdit(r, vf)} onDeleteTerm={(vf) => deleteTerm(r, vf)} onDeleteRate={(vf) => deleteRate(r, vf)} />
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function Supplements({ list, onChange, draft, setDraft, testid }) {
  const name = draft.name || "", rate = draft.rate || "";
  const add = () => {
    const v = parseDk(rate);
    if (!name.trim() || !(v > 0)) { toast.error("Skriv navn og kr/t, fx 12,50"); return; }
    onChange([...(list || []), { name: name.trim(), rate: v }]); setDraft({});
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(list || []).map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-xs font-semibold">
          {s.name} {String(s.rate).replace(".", ",")}
          <button onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Fjern"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input className={`${inputFit} w-24 !py-1`} placeholder="Navn" value={name} onChange={(e) => setDraft({ name: e.target.value, rate })} data-testid={`${testid}-name`} />
      <input className={`${inputFit} w-16 !py-1`} placeholder="kr/t" inputMode="decimal" value={rate} onChange={(e) => setDraft({ name, rate: e.target.value })}
        onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid={`${testid}-rate`} />
      <button className="p-1 rounded-md hover:bg-slate-100" aria-label="Tilføj tillæg" title="Tilføj (eller tryk Gem)" disabled={!name.trim() || !rate} onClick={add}>
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Regler
// ---------------------------------------------------------------------------
const SCHEDULE_NAMES = { atl_74: "7,4 t alle hverdage", atl_75: "7,5 t + fredag 7,0", "4dage": "4-dages uge", deltid30: "Deltid 30 t" };
const RULE_SECTIONS = [
  ["period", "Lønperiode"], ["schedules", "Arbejdstidsskemaer"], ["overtime", "Overtid, helligdage og tillæg"], ["pause", "Pause, hvile og afrunding"],
  ["absence", "Fraværstyper"], ["money", "Lønsatser og kroner"], ["export", "Lønartkoder og kontrol"],
];
// 2026-10-28: Profiler (som "time recording profiles" i SuccessFactors) - navngivne udgaver af de
// to afsnit, der vælges pr. chauffør med "gælder fra". Standard = firmaets egne regler.
const PAY_PROFILE_KEYS = ["ot1_hours", "ot_basis", "ot_fixed_scope", "ot_fixed", "ot1_by_day", "evening", "night", "bands_apply",
  "bands_days", "sun_ot2", "sun_ot2_except_schedule", "hol_ot2", "half_holidays", "guarantee_hours", "bank_premiums", "auto_lines"];
const TIME_PROFILE_KEYS = ["pause", "pause_button", "rounding", "legal"];
const PROFILE_GROUP = { overtime: ["pay_profiles", PAY_PROFILE_KEYS, "pay_profile_id"], pause: ["time_profiles", TIME_PROFILE_KEYS, "time_profile_id"] };
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const dk = (v) => String(v ?? "").replace(".", ",");
// 2026-10-27: Pausen i trin efter vagtens arbejdstid - samme regler som serveren
// (paybasis.pause_tier / pause_need / pause_allowed).
const pauseTiers = (p) => (p?.tiers || []).map((t) => ({ over: num(t.over_hours), min: num(t.minutes) }))
  .filter((t) => t.min > 0).sort((a, b) => a.over - b.over);
function pauseTier(p, workMin) {
  let m = 0, lim = 0, prev = 0;
  for (const t of pauseTiers(p)) { const x = Math.round(t.over * 60); if (workMin > x) { prev = m; m = t.min; lim = x; } }
  return { m, lim, prev };
}
function pauseNeed(p, workMin, stamped = 0) {
  if (!p || p.paid || p.mode === "registered") return 0;
  const { m, lim, prev } = pauseTier(p, workMin);
  let n = Math.max(0, m - stamped);
  if (p.cap_to_threshold) n = Math.min(n, Math.max(0, workMin - lim + Math.max(0, prev - stamped)));
  return n;
}
function pauseAllowed(p, workMin) {
  const { m, lim, prev } = pauseTier(p, workMin);
  return p?.cap_to_threshold ? Math.min(m, prev + Math.max(0, workMin - lim)) : m;
}
const minHM = (mins) => `${Math.floor(mins / 60)}:${String(Math.round(mins % 60)).padStart(2, "0")}`;
// 2026-10-24 (N44): "7,5x" blev stille til 0 (hele dagen = overtid). Tjek før gem.
const badNum = (v) => String(v ?? "").trim() !== "" && !Number.isFinite(Number(String(v).replace(",", ".")));
function invalidFields(r) {
  const out = [];
  for (const sc of r.schedules || []) for (const [d, l] of WEEKDAYS) if (badNum(sc.hours[d])) out.push(`${sc.name} ${l}`);
  [["guarantee_hours", "Garanti"], ["tour_tolerance_min", "Kontrol mod turene"]].forEach(([k, l]) => { if (badNum(r[k])) out.push(l); });
  for (const [d, l] of WEEKDAYS) {
    if (badNum((r.ot1_by_day || {})[d])) out.push(`Overtid 1 ${l}`);
    if (r.ot_basis === "fixed" && badNum((r.ot_fixed || {})[d])) out.push(`Overtid efter ${l}`);
  }
  (r.pause?.tiers || []).forEach((t, i) => {
    if (badNum(t.over_hours)) out.push(`Pause trin ${i + 1}: timer`);
    if (badNum(t.minutes)) out.push(`Pause trin ${i + 1}: minutter`);
  });
  if (badNum(r.legal?.night_max_h)) out.push("Natarbejde (timer)");
  for (const g of r.money?.groups || []) for (const x of g.rates || []) if (badNum(x.rate)) out.push(`Lønsats ${g.name}`);
  for (const v of r.money?.versions || []) {
    for (const [k, x] of Object.entries(v.rates || {})) if (badNum(x)) out.push(`Sats ${k} fra ${v.valid_from}`);
    for (const p of [...(v.pct1 || []), ...(v.pct2 || [])]) if (badNum(p.pct)) out.push(`Procent ${p.name}`);
  }
  for (const t of r.absence_types || []) { if (badNum(t.min_seniority_months)) out.push(`${t.name}: anciennitet`); if (badNum(t.max_days_per_spell)) out.push(`${t.name}: dage pr. forløb`); }
  for (const x of r.extra_lines || []) if (badNum(x.rate)) out.push(`Lønlinje ${x.name}: sats`);
  for (const pk of ["pay_profiles", "time_profiles"]) {
    for (const pr of r[pk] || []) {
      const sub = invalidFields({ ...r, schedules: [], money: {}, absence_types: [], extra_lines: [], pay_profiles: [], time_profiles: [], ...pr.settings });
      sub.forEach((x) => out.push(`${pr.name}: ${x}`));
    }
  }
  return out;
}

// 2026-10-25: "Tilføj"-felterne (ekstra fridag, ny fraværstype, egen lønlinje)
// ligger i RulesView, så det skrevne ikke forsvinder, når man skifter fane - og
// "Gem regler" tager det med, selv om der ikke er trykket Tilføj.
const DraftCtx = React.createContext(null);
const fmtGap = (m) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} t${m % 60 ? ` ${m % 60} min` : ""}`);
function useRuleDraft(key, init) {
  const ctx = React.useContext(DraftCtx);
  const v = { ...init, ...(ctx?.drafts[key] || {}) };
  const set = (patch) => ctx.setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));
  const clear = () => ctx.setDrafts((d) => ({ ...d, [key]: undefined }));
  return [v, set, clear];
}
const uniqueCode = (name, taken, fallback) => {
  const base = slugify(name) || fallback;
  let code = base;
  for (let n = 2; taken.has(code); n++) code = `${base}_${n}`;
  return code;
};
const withAbsenceType = (types, meta, name, pay) => [...types, {
  code: uniqueCode(name, new Set([...types.map((x) => x.code), ...(meta.base_lines || []).map(([k]) => k), "fri"]), "fravaer"),
  name: name.trim(), pay, export_code: "", active: true, custom: true, min_seniority_months: 0, max_days_per_spell: 0, export_hours: false }];
const withExtraLine = (extras, meta, name, unit) => [...extras, {
  code: uniqueCode(name, new Set([...extras.map((x) => x.code), ...(meta.base_lines || []).map(([k]) => k)]), "linje"),
  name: name.trim(), unit, rate: "", export_code: "", active: true }];
const holidayEdit = (hol, edit, patch) => {
  const st = { ...hol.settings, ...(edit || {}) };
  return { holiday_optional: st.holiday_optional || [], holiday_excluded: st.holiday_excluded || [], holiday_extra: st.holiday_extra || [], ...patch };
};

const rulesInit = (rules) => {
  const x = JSON.parse(JSON.stringify(rules));
  x.money = { versions: [], groups: [], default_group: "", ...(x.money || {}) };
  x.extra_lines = x.extra_lines || [];
  return x;
};

function RulesView({ meta, onSaved, onMeta, initialSec = "" }) {
  const [r, setR] = useState(() => rulesInit(meta.rules));
  // 2026-10-28: Det gemte - så "Ændring fra en dato" kan sige, at der er ugemte ændringer.
  const [savedJson, setSavedJson] = useState(() => JSON.stringify(rulesInit(meta.rules)));
  const reloadRules = async () => {
    const d = (await axios.get(`${API}/payroll/rules`)).data;
    setR(rulesInit(d.rules)); setSavedJson(JSON.stringify(rulesInit(d.rules)));
    axios.get(`${API}/payroll/drivers`).then((x) => setDrivers(x.data || [])).catch(() => {});
    onMeta && onMeta();
  };
  const [sec, setSec] = useState(initialSec || "schedules");
  const [saving, setSaving] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [hol, setHol] = useState(null);          // {years, settings, ...}
  const [holEdit, setHolEdit] = useState(null);  // ændrede helligdagsindstillinger
  const [cyc, setCyc] = useState(null);          // lønperiode-indstillinger
  const [cycEdit, setCycEdit] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [rerateAsk, setRerateAsk] = useState(null);   // T9: efter gem - var den gamle regel forkert?
  useEffect(() => {
    axios.get(`${API}/payroll/drivers`).then((x) => setDrivers(x.data || [])).catch(() => {});
    axios.get(`${API}/payroll/holidays`).then((x) => setHol(x.data)).catch(() => setHol(null));
    axios.get(`${API}/company/payroll-settings`).then((x) => setCyc(x.data)).catch(() => setCyc(null));
  }, []);
  const up = (patch) => setR((x) => ({ ...x, ...patch }));
  // 2026-10-28: Valgt profil pr. afsnit ("" = Standard). Afsnittets kort viser og retter profilens
  // udgave; nøgler uden for afsnittet (fx helligdagskalenderen) er fælles for firmaet.
  const [profSel, setProfSel] = useState({ overtime: "", pause: "" });
  // 2026-10-29 (effective dating som SuccessFactors): hvert afsnit - Standard og hver profil - har versioner
  // (tidsudsnit) {from, status, settings}. Kortet viser og retter det udsnit, der gælder den valgte dato
  // (standard i dag). Spejlet (de flade felter / profilens settings) følger med, når dagens udsnit rettes.
  const [asOf, setAsOf] = useState({ overtime: localDate(), pause: localDate() });
  const grp = PROFILE_GROUP[sec];
  const selProf = grp && (r[grp[0]] || []).find((x) => x.id === profSel[sec]);
  const verList = grp ? (selProf ? (selProf.versions || []) : ((r.versions || {})[sec] || [])) : [];
  const verIdx = verList.length ? versionIndexOn(verList, asOf[sec]) : -1;
  const verCur = verIdx >= 0 ? verList[verIdx] : null;
  const verIsToday = verIdx >= 0 && verIdx === versionIndexOn(verList, localDate());
  const rv = verCur ? { ...r, ...verCur.settings } : (selProf ? { ...r, ...selProf.settings } : r);
  const upv = grp ? (patch) => {
    const [pk, keys] = grp;
    const inG = Object.fromEntries(Object.entries(patch).filter(([k]) => keys.includes(k)));
    const rest = Object.fromEntries(Object.entries(patch).filter(([k]) => !keys.includes(k)));
    setR((x) => {
      const setVer = (list) => (list || []).map((v, i) => (i === verIdx ? { ...v, settings: { ...v.settings, ...inG } } : v));
      if (selProf) {
        return { ...x, ...rest, [pk]: (x[pk] || []).map((pr) => (pr.id !== selProf.id ? pr
          : { ...pr, versions: setVer(pr.versions), ...(verIsToday ? { settings: { ...pr.settings, ...inG } } : {}) })) };
      }
      if (!Object.keys(inG).length) return { ...x, ...rest };
      return { ...x, ...rest, ...(verIsToday ? inG : {}), versions: { ...(x.versions || {}), [sec]: setVer((x.versions || {})[sec]) } };
    });
  } : up;
  const save = async () => {
    // Skrevet, men ikke trykket Tilføj: tages med (en ekstra fridag uden dato kan ikke).
    let rr = r, hh = holEdit;
    const dAbs = drafts.absence, dX = drafts.extra, dH = drafts.holiday;
    if ((dH?.name || "").trim() && !dH?.date) { setSec("overtime"); toast.error("Ekstra fridag: vælg en dato - eller ryd navnet"); return; }
    if ((dAbs?.name || "").trim()) rr = { ...rr, absence_types: withAbsenceType(rr.absence_types || [], meta, dAbs.name, dAbs.pay || "unpaid") };
    if ((dX?.name || "").trim()) rr = { ...rr, extra_lines: withExtraLine(rr.extra_lines || [], meta, dX.name, dX.unit || "stk") };
    if (dH?.date && hol) {
      const cur = holidayEdit(hol, holEdit, {});
      hh = holidayEdit(hol, holEdit, { holiday_extra: [...cur.holiday_extra.filter((x) => x.date !== dH.date), { date: dH.date, name: (dH.name || "").trim() || "Ekstra fridag" }] });
    }
    const bad = invalidFields(rr);
    if (bad.length) { toast.error(`Ugyldigt tal: ${bad.slice(0, 4).join(", ")}${bad.length > 4 ? " …" : ""}`); return; }
    // 2026-10-24 (N36): Reglerne er ikke versioneret - de gælder hele den åbne periode.
    if (!window.confirm("Rettelser af det viste udsnit gælder alle dage i udsnittet - også dage, der allerede er gået i den åbne lønperiode. Godkendte perioder ændres ikke.\n\nSkal en ændring først gælde fra en dato (fx en ny overenskomst), så tryk Annuller, lav \"Ny version fra dato\" ved profilen, og ret den.\n\nGem?")) return;
    setSaving(true);
    try {
      if (cycEdit) await axios.patch(`${API}/company/payroll-settings`, cycEdit);
      if (hh) await axios.put(`${API}/payroll/holidays`, hh);
      const res = await axios.put(`${API}/payroll/rules`, { rules: rr });
      setR(rr); setHolEdit(hh); setDrafts({});
      toast.success(res.data?.open_period ? `Lønreglerne er gemt - gælder for perioden ${res.data.open_period.label}` : "Lønreglerne er gemt");
      // 2026-10-25 (T9): Ændringen gælder fremover. Var den gamle regel forkert,
      // kan de godkendte perioder rettes bagud - men kun hvis man selv vælger det.
      const hint = res.data?.rerate_hint;
      if (hint) toast.info(`Ændret: ${hint.sections.join(", ")}. Det gælder fra den åbne periode.`, {
        description: `Var den gamle regel forkert? Så kan ${hint.periods} ${hint.periods === 1 ? "godkendt periode" : "godkendte perioder"} rettes bagud.`, duration: 20000,
        action: { label: "Ret bagud", onClick: () => setRerateAsk({ sections: hint.keys }) } });
      onSaved();
    } catch (e) { toast.error(errText(e, "Kunne ikke gemme")); }
    finally { setSaving(false); }
  };
  const sections = RULE_SECTIONS.filter(([k]) => k !== "money" || meta.can_money);

  return (
    <DraftCtx.Provider value={{ drafts, setDrafts }}>
    {rerateAsk && <RerateDialog initialSections={rerateAsk.sections} onClose={() => setRerateAsk(null)} onDone={() => { setRerateAsk(null); onSaved(); }} />}
    <div className="space-y-4" data-testid="payroll-rules">
      <div className="flex flex-wrap gap-1.5">
        {sections.map(([k, l], n) => (
          <button key={k} onClick={() => setSec(k)} className={`${chipBase} ${sec === k ? "bg-slate-900 border-slate-900 text-white dark:bg-white dark:text-slate-900" : chipOff}`}
            data-testid={`payroll-rules-${k}`}>
            {n + 1}. {l}
          </button>
        ))}
      </div>
      {sec === "period" && <PeriodSection cyc={cyc} edit={cycEdit} setEdit={setCycEdit} r={r} up={up} />}
      {sec === "schedules" && <SchedulesSection r={r} up={up} meta={meta} drivers={drivers} />}
      {grp && <ProfileBar sec={sec} r={r} setR={setR} rv={rv} asOf={asOf[sec]} setAsOf={(d) => setAsOf((x) => ({ ...x, [sec]: d }))} sel={profSel[sec]} onSel={(id) => setProfSel((x) => ({ ...x, [sec]: id }))} drivers={drivers}
        dirty={JSON.stringify(r) !== savedJson} onReload={reloadRules} />}
      {sec === "overtime" && <OvertimeSection r={rv} up={upv} meta={meta} hol={hol} holEdit={holEdit} setHolEdit={setHolEdit} profile={selProf} />}
      {sec === "pause" && <PauseSection r={rv} up={upv} />}
      {sec === "absence" && <AbsenceSection r={r} up={up} meta={meta} />}
      {sec === "money" && meta.can_money && <MoneySection r={r} up={up} drivers={drivers} agreements={meta.agreements || []} />}
      {sec === "export" && <ExportSection r={r} up={up} meta={meta} />}
      <div className="sticky bottom-3 flex justify-end">
        <button className={`${btnPrimary} shadow-lg`} disabled={saving} onClick={save} data-testid="payroll-rules-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem regler
        </button>
      </div>
    </div>
    </DraftCtx.Provider>
  );
}

// 2026-10-24 (B11): Lønperioden (før kun under Indstillinger, hvor bogholderen ikke ledte).
// 2026-10-25: Kun her - Indstillinger viser den og linker hertil. Krav om årsag ved
// manuel stempling er drift (chaufførappen) og sættes kun under Indstillinger.
function PeriodSection({ cyc, edit, setEdit, r, up }) {
  if (!cyc) return <RuleCard title="Lønperiode"><div className="text-sm text-slate-500">Henter…</div></RuleCard>;
  const v = { ...cyc, ...(edit || {}) };
  const set = (patch) => setEdit({ payroll_cycle_type: v.payroll_cycle_type, payroll_start_day: v.payroll_start_day, payroll_anchor_date: v.payroll_anchor_date, ...patch });
  return (
    <RuleCard title="Lønperiode" help="Perioden, der godkendes og sendes til lønsystemet. En ny periode skal starte dagen efter den sidst godkendte - ellers tælles dage to gange.">
      <div className="flex flex-wrap gap-2">
        {[["monthly", "Månedlig"], ["biweekly", "14 dage"], ["weekly", "Ugentlig"]].map(([k, l]) => (
          <button key={k} className={`${chipBase} ${v.payroll_cycle_type === k ? chipOn : chipOff}`} onClick={() => set({ payroll_cycle_type: k })} data-testid={`payroll-cycle-${k}`}>{l}</button>
        ))}
      </div>
      {v.payroll_cycle_type === "monthly" ? (
        <div className="flex items-center gap-2 text-sm mt-2">Starter den <input className={`${inputFit} w-16`} type="number" min="1" max="28" value={v.payroll_start_day}
          onChange={(e) => set({ payroll_start_day: Number(e.target.value) || 1 })} data-testid="payroll-cycle-day" /> i måneden (fx 20 = 20.–19.)</div>
      ) : (
        <div className="flex items-center gap-2 text-sm mt-2">Første periode starter <input type="date" className={inputFit} value={v.payroll_anchor_date || ""}
          onChange={(e) => set({ payroll_anchor_date: e.target.value })} data-testid="payroll-cycle-anchor" />
          {!v.payroll_anchor_date && <span className="text-xs text-amber-700">Vælg en startdato (ellers sættes den til mandag i denne uge)</span>}</div>
      )}
      {/* 2026-10-25: Frist for lønfilen - vises i perioden og under "Kræver handling". */}
      <div className="flex flex-wrap items-center gap-2 text-sm mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
        Lønfilen skal være hos lønsystemet senest
        <input className={`${inputFit} w-14`} value={r?.deadline_days || ""} placeholder="–" inputMode="numeric"
          onChange={(e) => up({ deadline_days: Number(e.target.value.replace(/\D/g, "")) || 0 })} data-testid="payroll-deadline-days" />
        dage efter periodens slut.
        <span className="text-xs text-slate-500">Tom = ingen frist. Fx 3: perioden 20.10.-19.11. skal være sendt 22.11. Fristen står i perioden og under "Kræver handling" fra 2 dage før periodens slut.</span>
      </div>
      {edit && <div className="text-xs text-amber-700 mt-2">Gemmes med "Gem regler".</div>}
    </RuleCard>
  );
}

function SchedulesSection({ r, up, meta, drivers = [] }) {
  const set = (id, patch) => up({ schedules: r.schedules.map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  const add = (preset) => {
    const id = newId("s");
    up({ schedules: [...r.schedules, { id, name: preset ? SCHEDULE_NAMES[preset] || meta.preset_names[preset] : "Nyt skema",
      hours: { ...(meta.presets[preset] || { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 }) } }] });
  };
  const remove = (id) => {
    // 2026-10-24 (N37): Sig hvem det rammer, før skemaet slettes.
    const users = drivers.filter((d) => d.profile?.schedule_id === id).map((d) => d.name);
    if (!window.confirm(users.length ? `${users.length} chauffør${users.length === 1 ? "" : "er"} bruger skemaet (${users.slice(0, 5).join(", ")}${users.length > 5 ? " …" : ""}). De får standardskemaet - også for den åbne periode. Slet?` : "Slet skemaet?")) return;
    up({ schedules: r.schedules.filter((x) => x.id !== id) });
  };
  return (
    <RuleCard title="Arbejdstidsskemaer" help="Skemaet er den normale arbejdsdag for hver ugedag. Overtid er tiden ud over chaufførens skema, og en syge- eller feriedag giver skemaets timer. Opret fx et 4-dages-skema eller et deltidsskema, og vælg det på chaufføren under Chauffører."
      more={<>{NORMAL_TXT}{FRIDAG_TXT}<p>Skemaet er ikke en vagtplan: det siger, hvor mange timer en almindelig uge har pr. ugedag. Kører en chauffør fast weekend, får chaufføren et weekendskema - så er lørdag og søndag ikke fridage for den chauffør.</p></>}>
      <div className="space-y-3">
        {r.schedules.map((sc) => {
          const week = WEEKDAYS.reduce((a, [d]) => a + num(sc.hours[d]), 0);
          const isStd = r.default_schedule === sc.id;
          return (
            <div key={sc.id} className={`rounded-xl border p-3 space-y-2 ${isStd ? "border-emerald-300 dark:border-emerald-700" : "border-slate-200 dark:border-slate-700"}`} data-testid={`payroll-schedule-${sc.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <input className={`${inputFit} font-semibold w-56`} value={sc.name} onChange={(e) => set(sc.id, { name: e.target.value })} aria-label="Skemaets navn" />
                <span className="text-sm text-slate-500 tabular-nums">= {week.toLocaleString("da-DK")} t om ugen</span>
                <label className="flex items-center gap-1.5 text-sm">
                  <input type="radio" name="std-schedule" checked={isStd} onChange={() => up({ default_schedule: sc.id })} />
                  Standard {isStd && <span className="text-xs text-emerald-700 font-semibold">(chauffører uden eget skema)</span>}
                </label>
                {!isStd && <button onClick={() => remove(sc.id)} className="ml-auto text-xs font-semibold text-red-700 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Slet</button>}
              </div>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(([d, l]) => (
                  <div key={d}>
                    <label className={label}>{l}</label>
                    <input className={`${inputFit} w-16`} value={dk(sc.hours[d])} onChange={(e) => set(sc.id, { hours: { ...sc.hours, [d]: e.target.value.replace(",", ".") } })}
                      data-testid={`payroll-norm-${sc.id}-${d}`} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">Nyt skema:</span>
        {Object.keys(meta.presets).map((k) => (
          <button key={k} className={btnGhost} onClick={() => add(k)} data-testid={`payroll-schedule-add-${k}`}><Plus className="w-4 h-4" />{meta.preset_names[k] || k}</button>
        ))}
        <button className={btnGhost} onClick={() => add("")}><Plus className="w-4 h-4" />Tomt</button>
      </div>
      <div className="text-xs text-slate-500">Lørdag og søndag med 0 timer betyder, at alt arbejde de dage er overtid. Deltid på timeløn: overtiden regnes stadig efter standardskemaet (fuld tid) – timer ud over deltidsskemaet op til fuld tid er merarbejde med almindelig timeløn.</div>
    </RuleCard>
  );
}

function OvertimeSection({ r, up, meta, hol, holEdit, setHolEdit, profile = null }) {
  return (
    <>
      {/* 2026-10-25: Hvornår overtiden starter (skemaet eller en fast grænse) og overtid 1 pr. ugedag. */}
      <RuleCard title="Overtid" help="Overtid er tiden ud over normaltiden. Overtid 1 og 2 er tillæg oven i timelønnen (timerne står også under Timer)."
        more={<>{NORMAL_TXT}<p><b>Overtid 1</b> = de første timer af overtiden (ATL: 3 timer, laveste sats). <b>Overtid 2</b> = resten (højeste sats) - og søn- og helligdage fra første minut, hvis det er valgt nedenfor.</p>
          {FRIDAG_TXT}<p><b>Fast grænse</b> er til firmaer uden overenskomst eller med en aftale som "overtid efter 8 timer om dagen". Med en overenskomst (fx ATL) er overtid det, der ligger ud over den aftalte daglige arbejdstid - det er skemaet.</p>
          <p>Overtid pr. uge (fx alt over 37 t om ugen) regnes ikke - overtiden regnes pr. dag.</p></>}>
        <div className="space-y-1.5 text-sm" data-testid="payroll-ot-basis">
          <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={r.ot_basis !== "fixed"} onChange={() => up({ ot_basis: "schedule" })} data-testid="payroll-ot-schedule" />
            <span><b>Efter chaufførens arbejdstidsskema</b> (ATL og de fleste) - normaltiden er skemaets timer den dag. Arbejde på en fridag i skemaet er overtid fra første time.</span></label>
          <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={r.ot_basis === "fixed"} onChange={() => up({ ot_basis: "fixed" })} data-testid="payroll-ot-fixed" />
            <span><b>Efter et fast antal timer pr. dag</b> - samme grænse uanset skemaet. Fx overtid efter 8 timer.</span></label>
          {r.ot_basis === "fixed" && (
            <label className="flex items-center gap-2 ml-6 text-sm">Gælder for
              <select className={inputFit} value={r.ot_fixed_scope || "all"} onChange={(e) => up({ ot_fixed_scope: e.target.value })} data-testid="payroll-ot-scope">
                <option value="all">alle chauffører</option>
                <option value="afloeser">kun afløsere uden fast skema - de faste følger deres skema</option>
              </select>
            </label>
          )}
        </div>
        <div className="overflow-x-auto"><table className="text-sm mt-1" data-testid="payroll-ot-table">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500"><th />{WEEKDAYS.map(([d, l]) => <th key={d} className="px-1 font-semibold">{l.slice(0, 3)}</th>)}</tr></thead>
          <tbody>
            {r.ot_basis === "fixed" && (
              <tr><td className="pr-3 py-1 whitespace-nowrap">Overtid efter (timer)</td>
                {WEEKDAYS.map(([d]) => <td key={d} className={`px-1 py-1 ${d === "sun" && r.sun_ot2 !== false ? "opacity-40" : ""}`} title={d === "sun" && r.sun_ot2 !== false ? "Søndag er overtid 2 fra første minut (Søn- og helligdage) - tallet bruges kun, hvis søndag følger grænsen" : undefined}><input className={`${inputFit} w-14 text-center`} value={dk((r.ot_fixed || {})[d] ?? 0)}
                  onChange={(e) => up({ ot_fixed: { ...(r.ot_fixed || {}), [d]: e.target.value.replace(",", ".") } })} data-testid={`payroll-ot-fixed-${d}`} /></td>)}</tr>
            )}
            <tr><td className="pr-3 py-1 whitespace-nowrap">Overtid 1 (de første timer)</td>
              {WEEKDAYS.map(([d]) => <td key={d} className={`px-1 py-1 ${d === "sun" && r.sun_ot2 !== false ? "opacity-40" : ""}`} title={d === "sun" && r.sun_ot2 !== false ? "Søndag er overtid 2 fra første minut (Søn- og helligdage) - tallet bruges kun for weekendhold med søndag i skemaet, eller hvis søndag følger skemaet" : undefined}><input className={`${inputFit} w-14 text-center`} value={dk((r.ot1_by_day || {})[d] ?? r.ot1_hours)}
                onChange={(e) => up({ ot1_by_day: { ...(r.ot1_by_day || {}), [d]: e.target.value.replace(",", ".") } })} data-testid={`payroll-ot1-${d}`} /></td>)}</tr>
          </tbody>
        </table></div>
        <div className="text-xs text-slate-500">
          Resten af overtiden er overtid 2. 0 i "Overtid 1" = overtid 2 fra første overtime.{" "}
          {r.ot_basis === "fixed" ? `Skemaet bruges stadig til fravær (en sygedag giver skemaets timer) og til at vise fridage. 0 i "Overtid efter" = overtid fra første time.${r.ot_fixed_scope === "afloeser" ? " Afløsere med et skema på profilen følger skemaet." : ""}` : "Normaltiden står under Arbejdstidsskemaer."}
          {" "}Søn- og helligdagsreglen nedenfor går forud.
        </div>
      </RuleCard>
      <RuleCard title="Søn- og helligdage" help="Søndag og helligdag vælges hver for sig, og tillæg kan komme oveni. Overtid og tillæg regnes for timerne på selve søndagen/helligdagen (en natvagt fra lørdag tæller fra kl. 00). Fravær på en helligdag giver ingen timer."
        more={<><p><b>En helligdag i lønnen</b> (kalenderen nederst) betyder: arbejde betales efter valget her; fravær giver ingen timer - timelønnede får helligdagsbetaling fra frihedskontoen (fx i ATL), og fast løn kører videre; garantien gælder; og samme kalender giver helligdagspris i Økonomi.</p>
          <p><b>Juleaftensdag og nytårsaftensdag</b> er ikke helligdage efter loven, men fridage i mange overenskomster (ATL: hele dagen). Sæt flueben i kalenderen, så lønnen regner dem som helligdage. <b>1. maj og Grundlovsdag</b> er i ATL fri fra kl. 12 - vælg "fra kl. 12" her.</p>
          <p><b>Weekendhold:</b> en chauffør med søndag i skemaet arbejder om søndagen som en almindelig dag. Fjern fluebenet, hvis jeres aftale giver overtid 2 alligevel.</p></>}>
        {/* 2026-10-25: Før ét valg for begge dage - nu hver for sig, og tillæg (egne lønlinjer, der kommer af sig selv). */}
        <div className="grid md:grid-cols-2 gap-3 text-sm" data-testid="payroll-sunhol">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-1.5">
            <div className="font-semibold">Søndag</div>
            <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={r.sun_ot2 !== false} onChange={() => up({ sun_ot2: true })} data-testid="payroll-sun-ot2" />
              <span><b>Overtid 2 fra første minut</b> (ATL)</span></label>
            {/* 2026-10-25: Weekendhold-undtagelsen hører til skemaet - ikke med fast grænse for alle. */}
            {r.sun_ot2 !== false && !(r.ot_basis === "fixed" && (r.ot_fixed_scope || "all") === "all") && (
              <label className="flex items-start gap-2 ml-6 text-xs text-slate-600 dark:text-slate-300"><input type="checkbox" className="mt-0.5" checked={r.sun_ot2_except_schedule !== false} onChange={(e) => up({ sun_ot2_except_schedule: e.target.checked })} data-testid="payroll-sun-except" />
                <span>Ikke for chauffører, der har søndag i skemaet (fx weekendhold) - for dem er søndag en almindelig arbejdsdag</span></label>
            )}
            <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={r.sun_ot2 === false} onChange={() => up({ sun_ot2: false })} data-testid="payroll-sun-schedule" />
              <span>{r.ot_basis === "fixed" && (r.ot_fixed_scope || "all") === "all"
                ? <><b>Efter grænsen</b> - søndag følger tabellen under Overtid (søndag-kolonnen)</>
                : <><b>Efter skemaet</b> - søndag er fri i de fleste skemaer, så timerne giver overtid 1 og så overtid 2 (som en lørdag){r.ot_basis === "fixed" ? ". Afløsere uden skema følger tabellen under Overtid" : ""}</>}</span></label>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-1.5">
            <div className="font-semibold">Helligdage</div>
            <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={r.hol_ot2 !== false} onChange={() => up({ hol_ot2: true })} data-testid="payroll-hol-ot2" />
              <span><b>Overtid 2 fra første minut</b> (ATL)</span></label>
            <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={r.hol_ot2 === false} onChange={() => up({ hol_ot2: false })} data-testid="payroll-hol-schedule" />
              <span><b>Som en almindelig dag efter skemaet</b> - fx en onsdag giver skemaets timer, resten er overtid. Giver overenskomsten et helligdagstillæg, så tilføj det nedenfor.</span></label>
            <div className="flex flex-wrap gap-4 pt-1">
              {[["1maj", "1. maj fra kl. 12"], ["grundlovsdag", "Grundlovsdag fra kl. 12"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={r.half_holidays.includes(k)}
                  onChange={(e) => up({ half_holidays: e.target.checked ? [...r.half_holidays, k] : r.half_holidays.filter((x) => x !== k) })} /> {l}</label>
              ))}
            </div>
            <div className="text-xs text-slate-500">"Fra kl. 12" gælder også, selvom dagen ikke står i kalenderen nedenfor.</div>
          </div>
        </div>
        {profile ? <AutoPick r={r} up={up} /> : <AutoPremiums r={r} up={up} meta={meta} />}
        {profile ? <p className="text-xs text-slate-500 mt-2" data-testid="payroll-profile-holidays">Helligdagene (kalenderen) er fælles for hele firmaet - de rettes under Standard. Profilen bestemmer, hvordan de betales.</p>
          : <HolidayList hol={hol} edit={holEdit} setEdit={setHolEdit} half={r.half_holidays} />}
      </RuleCard>
      <RuleCard title="Aften- og nattillæg" help="Timer i tidsrummet giver et tillæg pr. time (oveni timelønnen og en eventuel overtid)."
        more={<><p><b>ATL (§ 4 stk. 5):</b> tillæg for forskudt arbejdstid kl. 18-23 (aften) og 23-06 (nat). Forskudt tid kan kun ligge fra mandag kl. 05 til lørdag kl. 05 - arbejdes der højst 5 dage om ugen, kan det lokalt aftales fra søndag kl. 22 til fredag kl. 22. Overarbejde i tilslutning til forskudt tid får tillægget oveni overtidstillægget.</p>
          <p><b>Alle timer i tidsrummet</b> passer til ATL og de fleste. <b>Kun normaltimer</b> er til aftaler, hvor overtid ikke giver aften- eller nattillæg.</p>
          <p>Weekenden er i forvejen overtid (eller søn-/helligdag) - derfor gælder tillægget i ATL kun på hverdage.</p></>}>
        {[["evening", "Aften"], ["night", "Nat"]].map(([k, l]) => (
          <div key={k} className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-2 w-24 font-semibold"><input type="checkbox" checked={r[k].on} onChange={(e) => up({ [k]: { ...r[k], on: e.target.checked } })} /> {l}</label>
            <input type="time" className={inputFit} value={r[k].from} onChange={(e) => up({ [k]: { ...r[k], from: e.target.value } })} />
            –
            <input type="time" className={inputFit} value={r[k].to} onChange={(e) => up({ [k]: { ...r[k], to: e.target.value } })} />
          </div>
        ))}
        <div className="flex flex-wrap gap-2 mt-2">
          <button className={`${chipBase} ${r.bands_apply === "all" ? chipOn : chipOff}`} onClick={() => up({ bands_apply: "all" })}>Alle timer i tidsrummet</button>
          <button className={`${chipBase} ${r.bands_apply === "normal" ? chipOn : chipOff}`} onClick={() => up({ bands_apply: "normal" })}>Kun normaltimer</button>
          <span className="w-3" />
          <button className={`${chipBase} ${r.bands_days === "all" ? chipOn : chipOff}`} onClick={() => up({ bands_days: "all" })}>Alle dage</button>
          <button className={`${chipBase} ${r.bands_days === "weekdays" ? chipOn : chipOff}`} onClick={() => up({ bands_days: "weekdays" })} title="ATL">Mandag kl. 05 – lørdag kl. 05</button>
          <button className={`${chipBase} ${r.bands_days === "sun22" ? chipOn : chipOff}`} onClick={() => up({ bands_days: "sun22" })} title="ATL, lokal aftale (højst 5 dage om ugen)" data-testid="payroll-bands-sun22">Søndag kl. 22 – fredag kl. 22</button>
        </div>
      </RuleCard>
      <RuleCard title="Garanti" help="Mindst så mange timer betales pr. indkald: for afløsere, for alle på en fridag efter skemaet (fx lørdag og søndag) og helligdag, og når en dag er markeret som tilkald. Starter en ny vagt, mens garantien fra forrige indkald stadig løber, er det samme indkald.">
        <div className="flex items-center gap-2 text-sm">Mindst <input className={`${inputFit} w-16`} value={dk(r.guarantee_hours)} onChange={(e) => up({ guarantee_hours: e.target.value.replace(",", ".") })} /> timer pr. indkald</div>
        <div className="text-xs text-slate-500">Fx tilkald 10:00–10:30 og igen 13:45–14:15 = ét indkald (4 t). 10:00–11:00 og igen 14:30–15:30 = to indkald (2 × 4 t).</div>
      </RuleCard>
      <RuleCard title="Afspadsering" help="Vælg 'Afspadseres' på chaufføren under Løn › Chauffører. Så sendes timerne ud over den normale dag på 'Overtimer til afspadsering' i stedet for 'Timer', og lønsystemet fører saldoen. Afspadsering tages som fraværstypen 'Afspadsering' (timerne sendes med, så saldoen kan trækkes).">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.bank_premiums} onChange={(e) => up({ bank_premiums: e.target.checked })} data-testid="payroll-bank-premiums" /> Overtidstillæggene afspadseres også (ellers udbetales tillæggene)</label>
        {/* 2026-10-25 (L7): Hvad der sendes i de to tilfælde, med et eksempel. */}
        <div className="text-xs text-slate-500 space-y-1" data-testid="payroll-bank-example">
          <p>Eksempel: en timelønnet med "Afspadseres" kører 9,4 t på en dag med 7,4 t normaltid - 2 t er overtid 1.</p>
          <p><b>Uden flueben</b>: 7,4 t på "Timer", 2 t på "Overtimer til afspadsering" og 2 t på "Overtid 1" - tillægget udbetales nu (Danløn fx 6), timerne går til saldoen.</p>
          <p><b>Med flueben</b>: 7,4 t på "Timer", 2 t på "Overtimer til afspadsering" og 2 t på "Overtid 1 til afspadsering". De 2 t er <b>antal timer med tillæg</b> - ikke tillæggets størrelse. Bogholderen lægger tillægget til saldoen efter jeres aftale (fx 2 t × 50 % = 1 t ekstra). Danløn har ingen løntype til det, så aftal koden med bogholderen.</p>
        </div>
      </RuleCard>
    </>
  );
}

// 2026-10-25: Tillæg, der kommer af sig selv på lørdag, søndag og helligdag i et
// tidsrum (fx ATL eksportkørsel: søn- og helligdag kl. 00-20, lørdag kl. 18-24).
// Det er egne lønlinjer med "auto" - samme lønartkode, sats og lønfil som de andre.
const AUTO_DAYS = [["sat", "Lørdag"], ["sun", "Søndag"], ["hol", "Helligdag"]];
const HOURS = Array.from({ length: 25 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
function AutoPremiums({ r, up, meta }) {
  const extras = r.extra_lines || [];
  const autos = extras.filter((x) => x.auto);
  const setX = (code, patch) => up({ extra_lines: extras.map((x) => (x.code === code ? { ...x, ...patch } : x)) });
  const setA = (x, patch) => setX(x.code, { auto: { ...x.auto, ...patch } });
  const add = (name, days) => {
    const xs = withExtraLine(extras, meta, name, "timer");
    xs[xs.length - 1].auto = { days, from: "00:00", to: "24:00" };
    up({ extra_lines: xs });
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2" data-testid="payroll-auto-premiums">
      <div className="text-sm font-semibold">Tillæg på weekend- og helligdage</div>
      <div className="text-xs text-slate-500">Kommer af sig selv for de timer (eller dage), der er arbejdet i tidsrummet - oveni overtiden, eller i stedet for, hvis I vælger "efter skemaet" ovenfor. En helt anden timeløn på helligdage laves som et tillæg: timeløn + tillæg = den nye sats. Hvert tillæg får sin egen lønartkode under "Lønartkoder og kontrol".</div>
      {autos.map((x) => (
        <div key={x.code} className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid={`payroll-auto-${x.code}`}>
          <input className={`${inputFit} w-40 font-semibold`} value={x.name} onChange={(e) => setX(x.code, { name: e.target.value })} aria-label="Tillæggets navn" />
          <span className="flex items-center gap-2">
            {AUTO_DAYS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={x.auto.days.includes(k)}
                disabled={x.auto.days.length === 1 && x.auto.days.includes(k)}
                onChange={(e) => setA(x, { days: e.target.checked ? [...x.auto.days, k] : x.auto.days.filter((d) => d !== k) })} /> {l}</label>
            ))}
          </span>
          <span className="flex items-center gap-1">kl.
            <select className={inputFit} value={x.auto.from} onChange={(e) => setA(x, { from: e.target.value, ...(e.target.value >= x.auto.to ? { to: "24:00" } : {}) })} aria-label="Fra kl.">
              {HOURS.slice(0, 24).map((t) => <option key={t} value={t}>{t.slice(0, 2)}</option>)}
            </select>–
            <select className={inputFit} value={x.auto.to} onChange={(e) => setA(x, { to: e.target.value })} aria-label="Til kl.">
              {HOURS.filter((t) => t > x.auto.from).map((t) => <option key={t} value={t}>{t.slice(0, 2)}</option>)}
            </select>
          </span>
          <select className={inputFit} value={x.unit} onChange={(e) => setX(x.code, { unit: e.target.value })} aria-label="Pr. time eller pr. dag">
            <option value="timer">pr. time</option><option value="stk">pr. dag</option>
          </select>
          <span className="flex items-center gap-1"><input className={`${inputFit} w-20`} value={dk(x.rate ?? "")} placeholder="–" onChange={(e) => setX(x.code, { rate: e.target.value.replace(",", ".") })} aria-label="Kroner" /> kr</span>
          <button onClick={() => up({ extra_lines: extras.filter((y) => y.code !== x.code) })} className="ml-auto text-red-700" aria-label={`Slet ${x.name}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button className={btnGhost} onClick={() => add("Søndagstillæg", ["sun"])} data-testid="payroll-auto-add-sun"><Plus className="w-4 h-4" />Søndagstillæg</button>
        <button className={btnGhost} onClick={() => add("Helligdagstillæg", ["hol"])} data-testid="payroll-auto-add-hol"><Plus className="w-4 h-4" />Helligdagstillæg</button>
        <button className={btnGhost} onClick={() => add("Weekendtillæg", ["sat", "sun"])}><Plus className="w-4 h-4" />Weekendtillæg</button>
      </div>
      <div className="text-xs text-slate-500">Kroner bruges kun i kroneoversigten - lønsystemet har satsen på lønarten. Pr. dag = 1 for hver dag med arbejde i tidsrummet. Garantitimer tæller ikke med.</div>
    </div>
  );
}

// 2026-10-24 (F4): Helligdagene, lønnen regner med - her, hvor bogholderen
// leder (før kun i Økonomi, som et firma uden Økonomi aldrig så).
const OPTIONAL_LABEL = { grundlovsdag: "Grundlovsdag", juleaften: "Juleaften", nytaarsaften: "Nytårsaftensdag", "1maj": "1. maj" };
function HolidayList({ hol, edit, setEdit, half = [] }) {
  const [dh, setDh, clearDh] = useRuleDraft("holiday", { date: "", name: "" });
  if (!hol) return null;
  const st = { ...hol.settings, ...(edit || {}) };
  const opt = st.holiday_optional || [];
  const excl = st.holiday_excluded || [];
  const extra = st.holiday_extra || [];
  const change = (patch) => setEdit(holidayEdit(hol, edit, patch));
  const yrs = Object.keys(hol.years || {});
  return (
    <div className="mt-3 space-y-2" data-testid="payroll-holidays">
      <div className="text-sm font-semibold">Helligdage i lønnen{hol.shared_with_prices ? " (samme kalender som priserne i Økonomi)" : ""}</div>
      <div className="flex flex-wrap gap-3 text-sm">
        {Object.entries(OPTIONAL_LABEL).map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5"><input type="checkbox" checked={opt.includes(k)}
            onChange={(e) => change({ holiday_optional: e.target.checked ? [...opt, k] : opt.filter((x) => x !== k) })} data-testid={`payroll-hol-opt-${k}`} /> {l} (hele dagen)</label>
        ))}
      </div>
      {/* 2026-10-25: Hele dagen i kalenderen og "fra kl. 12" for samme dag - lønnen bruger "fra kl. 12". */}
      {half.filter((k) => opt.includes(k)).map((k) => (
        <div key={k} className="text-xs text-amber-700">{OPTIONAL_LABEL[k]} står både som hele dagen (her) og "fra kl. 12" (ovenfor) - lønnen regner fra kl. 12. Kalenderen bruges også til helligdagspris i Økonomi.</div>
      ))}
      <div className="grid md:grid-cols-2 gap-3">
        {yrs.map((y) => (
          <div key={y} className="rounded-xl border border-slate-200 dark:border-slate-700 p-2">
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">{y}</div>
            {(hol.years[y] || []).filter((d) => d.kind === "official" || d.kind === "extra" || opt.some((k) => OPTIONAL_LABEL[k] === d.name || (k === "nytaarsaften" && d.name.startsWith("Nytårsaften")))).map((d) => {
              const off = excl.includes(d.date);
              return (
                <div key={d.date} className={`flex items-center justify-between text-xs py-0.5 ${off ? "line-through text-slate-400" : ""}`}>
                  <span>{dkShort(d.date)} · {d.name}</span>
                  {d.kind === "official" && <button className="text-[11px] text-slate-500 hover:underline" onClick={() => change({ holiday_excluded: off ? excl.filter((x) => x !== d.date) : [...excl, d.date] })}>{off ? "Gør til helligdag igen" : "Gør til arbejdsdag"}</button>}
                  {d.kind === "extra" && <button className="text-[11px] text-red-700 hover:underline" onClick={() => change({ holiday_extra: extra.filter((x) => x.date !== d.date) })}>Fjern</button>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div><label className={label}>Ekstra helligdag (fx firmaets fridag)</label><input type="date" className={inputFit} value={dh.date} onChange={(e) => setDh({ date: e.target.value })} data-testid="payroll-hol-extra-date" /></div>
        <input className={`${inputFit} w-48`} placeholder="Navn, fx Firmaets fødselsdag" value={dh.name} onChange={(e) => setDh({ name: e.target.value })} />
        <button className={btnGhost} disabled={!dh.date} onClick={() => { change({ holiday_extra: [...extra.filter((x) => x.date !== dh.date), { date: dh.date, name: dh.name.trim() || "Firmaets fridag" }] }); clearDh(); }}><Plus className="w-4 h-4" />Tilføj</button>
      </div>
      {edit && <div className="text-xs text-amber-700">Gemmes med "Gem regler"{hol.shared_with_prices ? " - og gælder så også priserne (helligdagspris)" : ""}.</div>}
    </div>
  );
}

function PauseSection({ r, up }) {
  const ro = r.rounding;
  const example = (hm, upAfter) => {
    if (!ro.interval) return hm;
    const [hh, mm] = hm.split(":").map(Number);
    const tot = hh * 60 + mm;
    const rem = tot % ro.interval;
    const v = rem === 0 ? tot : rem > upAfter ? tot + ro.interval - rem : tot - rem;
    return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
  };
  return (
    <>
      <PauseCard r={r} up={up} />
      <RuleCard title="Afrunding" help="Hvert stempel afrundes for sig. 'Op efter 7 min' ved 15 min = nærmeste kvarter. De rå stempler gemmes altid.">
        <div className="flex flex-wrap gap-2">
          {[0, 5, 15, 30].map((v) => (
            <button key={v} className={`${chipBase} ${ro.interval === v ? chipOn : chipOff}`}
              onClick={() => up({ rounding: { interval: v, start_up_after: Math.floor(v / 2), end_up_after: Math.floor(v / 2) } })}>
              {v ? `${v} min` : "Ingen"}
            </button>
          ))}
        </div>
        {ro.interval > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm mt-2">
            Start rundes op efter <input className={`${inputFit} w-14`} value={ro.start_up_after} onChange={(e) => up({ rounding: { ...ro, start_up_after: Number(e.target.value) || 0 } })} /> min,
            slut rundes op efter <input className={`${inputFit} w-14`} value={ro.end_up_after} onChange={(e) => up({ rounding: { ...ro, end_up_after: Number(e.target.value) || 0 } })} /> min.
            <span className="text-slate-500">Fx ind 04:07 → {example("04:07", ro.start_up_after)}, ud 13:53 → {example("13:53", ro.end_up_after)}</span>
          </div>
        )}
        {ro.interval > 0 && (ro.start_up_after < ro.interval / 2 - 1 || ro.end_up_after > ro.interval / 2) && (
          <div className="text-xs text-amber-700 mt-1">Afrunding der altid går mod medarbejderen kan give sager – overenskomsten betaler for den tid, der er arbejdet.</div>
        )}
      </RuleCard>
      <LegalCard r={r} up={up} />
    </>
  );
}

/**
 * 2026-10-27: Pause i trin efter vagtens arbejdstid - og hvordan den trækkes:
 *  - Fast pause: trinnets pause trækkes altid (stemplet pause tæller med). Stemplet
 *    pause ud over trinnet er ikke arbejdstid.
 *  - Registreret pause (højst): kun den pause, chaufføren registrerer, trækkes - højst
 *    trinnets minutter (fx ATL § 4: pauser inden for normal arbejdstid højst 1 time).
 *    Pause-knappens tid ud over loftet betales; tid stemplet ud betales aldrig.
 *  - Betalt pause: intet trækkes.
 */
const PAUSE_PRESETS = [
  ["30 min over 6 t", [{ over_hours: 6, minutes: 30 }]],
  ["Lovens minimum (30 min over 6 t, 45 min over 9 t)", [{ over_hours: 6, minutes: 30 }, { over_hours: 9, minutes: 45 }]],
  ["Trin: 30 · 60 · 90 min (fra 0, 6 og 8 t)", [{ over_hours: 0, minutes: 30 }, { over_hours: 6, minutes: 60 }, { over_hours: 8, minutes: 90 }]],
];
function PauseCard({ r, up }) {
  const p = r.pause;
  const kind = p.paid ? "paid" : p.mode === "registered" ? "registered" : "fixed";
  const tiers = p.tiers || [];
  const setP = (patch) => {
    const next = { ...p, ...patch };
    const first = pauseTiers(next)[0];
    up({ pause: { ...next, minutes: first ? first.min : 0, after_hours: first ? first.over : num(p.after_hours) || 6 } });
  };
  const setTier = (i, patch) => setP({ tiers: tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const reg = kind === "registered";
  // Eksempler: arbejdstid i vagten -> pause og løn (fast) / loft (registreret).
  const sample = [4, 6, 6.25, 7, 8, 8.25, 9, 10].map((hrs) => {
    const w = Math.round(hrs * 60);
    return { w, pause: reg ? pauseAllowed(p, w) : pauseNeed(p, w), paid: w - (reg ? 0 : pauseNeed(p, w)) };
  });
  // Kanten: lønnen falder, når dagen går over et trin (uden glidende overgang).
  const cliffs = pauseTiers(p).filter((t) => t.over > 0).map((t) => {
    const lim = Math.round(t.over * 60);
    const before = lim - 1 - pauseNeed({ ...p, cap_to_threshold: false }, lim - 1);
    const after = lim + 1 - pauseNeed({ ...p, cap_to_threshold: false }, lim + 1);
    return before > after ? { lim, before, after, smooth: lim + 1 - pauseNeed({ ...p, cap_to_threshold: true }, lim + 1) } : null;
  }).filter(Boolean);
  const law = [[361, 30, "6–9 t"], [541, 45, "over 9 t"]].filter(([w, req]) => (reg ? false : pauseNeed({ ...p, cap_to_threshold: false }, w) < req));
  const overHour = pauseTiers(p).some((t) => t.min > 60);
  return (
    <RuleCard title="Pause" help="Pausen er ikke arbejdstid. Vælg hvordan den trækkes, og hvor lang den er efter vagtens arbejdstid (stemplet tid minus stemplet pause).">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="payroll-pause-mode">
        {[["fixed", "Fast pause", "Trækkes altid efter arbejdstiden. Stemplet pause tæller med."],
          ["registered", "Registreret pause (højst)", "Kun den pause, chaufføren registrerer - højst minutterne nedenfor. Mere end det betales."],
          ["paid", "Betalt pause", "Intet trækkes - pausen er arbejdstid."]].map(([k, t, d]) => (
          <button key={k} type="button" onClick={() => setP({ paid: k === "paid", mode: k === "registered" ? "registered" : "fixed" })}
            className={`text-left rounded-xl border px-3 py-2 ${kind === k ? "border-emerald-500 ring-2 ring-emerald-500/30 bg-white dark:bg-slate-800" : "border-slate-200 dark:border-slate-700"}`}
            data-testid={`payroll-pause-mode-${k}`}>
            <span className="block text-sm font-semibold">{t}</span><span className="block text-[11px] text-slate-500">{d}</span>
          </button>
        ))}
      </div>
      {kind !== "paid" && (
        <>
          <div className="text-sm font-semibold mt-3">{reg ? "Højst så meget pause trækkes" : "Pause der trækkes"}</div>
          <div className="space-y-1.5" data-testid="payroll-pause-tiers">
            {tiers.length === 0 && <div className="text-xs text-slate-500">Ingen pause trækkes. Tilføj et trin.</div>}
            {tiers.map((t, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                Når vagten er over
                <input className={`${inputFit} w-16`} value={dk(t.over_hours)} onChange={(e) => setTier(i, { over_hours: e.target.value.replace(",", ".") })} data-testid={`payroll-pause-over-${i}`} />
                t: {reg ? "højst" : ""}
                <input className={`${inputFit} w-16`} value={t.minutes} onChange={(e) => setTier(i, { minutes: e.target.value })} data-testid={i === 0 ? "payroll-pause-min" : `payroll-pause-min-${i}`} />
                min
                <button type="button" className="p-1 text-slate-400 hover:text-rose-500" aria-label="Fjern trinnet" onClick={() => setP({ tiers: tiers.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1" data-testid="payroll-pause-add"
                onClick={() => { const last = pauseTiers(p).slice(-1)[0]; setP({ tiers: [...tiers, { over_hours: last ? last.over + 2 : 6, minutes: last ? last.min + 15 : 30 }] }); }}>
                <Plus className="w-4 h-4" /> Trin</button>
              <span className="text-[11px] text-slate-500 ml-2">Forslag:</span>
              {PAUSE_PRESETS.map(([l, t]) => (
                <button key={l} type="button" className={`${chipBase} ${chipOff}`} onClick={() => setP({ tiers: t })}>{l}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto mt-2">
            <table className="text-xs" data-testid="payroll-pause-examples">
              <tbody>
                <tr><td className="pr-3 text-slate-500">Arbejde</td>{sample.map((x) => <td key={x.w} className="px-2 text-right font-mono">{minHM(x.w)}</td>)}</tr>
                <tr><td className="pr-3 text-slate-500">{reg ? "Højst pause" : "Pause"}</td>{sample.map((x) => <td key={x.w} className="px-2 text-right font-mono">{x.pause}</td>)}</tr>
                {!reg && <tr><td className="pr-3 text-slate-500">Til løn</td>{sample.map((x) => <td key={x.w} className="px-2 text-right font-mono font-semibold">{minHM(x.paid)}</td>)}</tr>}
              </tbody>
            </table>
            <div className="text-[11px] text-slate-500">{reg ? "Uden registreret pause trækkes intet. Mere pause end loftet betales som arbejdstid - dagen bliver orange, så I kan rette det (Ekstra pause), hvis chaufføren holdt fri." : "Uden stemplet pause, afrunding 0. Stemplet pause tæller med i pausen."}</div>
          </div>
          {pauseTiers(p).length > 0 && (
            <label className="flex items-start gap-2 text-sm mt-2">
              <input type="checkbox" className="mt-0.5" checked={!!p.cap_to_threshold} onChange={(e) => setP({ cap_to_threshold: e.target.checked })} data-testid="payroll-pause-cap" />
              <span>Glidende overgang: længere arbejde giver aldrig mindre løn
                {cliffs[0] && <span className="block text-xs text-slate-500">Fx {minHM(cliffs[0].lim + 1)} t arbejde: {p.cap_to_threshold ? `${minHM(cliffs[0].smooth)} til løn` : `${minHM(cliffs[0].after)} til løn - mindre end ${minHM(cliffs[0].before)} ved ${minHM(cliffs[0].lim - 1)} t`}.</span>}
              </span>
            </label>
          )}
          {!reg && <div className="text-xs text-slate-500">Pausen trækkes i den vagt, der selv er lang nok. Har dagen flere vagter med mere end 3 timer imellem (fx færge eller tilkald), er tiden imellem pausen - der trækkes ikke en fast pause oveni.</div>}
          {law.length > 0 && (
            <div className="text-xs rounded-lg bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 px-3 py-2" data-testid="payroll-pause-law">
              Under lovens minimum ({law.map(([, req, l]) => `${req} min ved ${l}`).join(", ")} arbejde - lov om arbejdstid for mobile arbejdstagere). En fast pause regnes som holdt, så sørg for, at chaufførerne holder mindst det.
            </div>
          )}
          {reg && <div className="text-xs text-slate-500">Dagen bliver orange, hvis den registrerede pause er under lovens minimum (30 min ved 6–9 t, 45 min over 9 t, højst 6 t i træk).</div>}
          {overHour && <div className="text-xs text-slate-500">Tjek overenskomsten: fx ATL § 4 - "pausen eller pauserne inden for normal arbejdstid må højst andrage 1 time".</div>}
        </>
      )}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={r.pause_button} onChange={(e) => up({ pause_button: e.target.checked })} data-testid="payroll-pause-button" /> Vis pause-knap i chaufførappen</label>
      {reg && !r.pause_button && <div className="text-xs text-amber-700">Uden pause-knap registrerer chaufføren pausen ved at stemple ud og ind - den tid er aldrig arbejdstid, så loftet bruges ikke.</div>}
    </RuleCard>
  );
}

// 2026-10-25: Advarslerne efter loven (orange på dagen). Natperioden og grænsen ved
// natarbejde kan ændres ved kollektiv overenskomst - hvile, pause og 60 t-ugen kan ikke.
const NIGHT_FROM = ["00:00", "01:00", "02:00", "03:00"];
const NIGHT_TO = ["04:00", "05:00", "06:00", "07:00"];
function LegalCard({ r, up }) {
  const lg = { night_from: "01:00", night_to: "05:00", night_max_h: 10, ...(r.legal || {}) };
  const set = (patch) => up({ legal: { ...lg, ...patch } });
  const hr = (t) => Number(t.slice(0, 2));
  const changed = lg.night_from !== "01:00" || lg.night_to !== "05:00" || num(lg.night_max_h) !== 10;
  return (
    <RuleCard title="Hviletid og natarbejde" help="Dagen bliver orange, når stemplingerne viser et muligt brud på reglerne. Det er en hjælp til at se det i tide - fartskriveren er den officielle kontrol.">
      <ul className="text-sm text-slate-700 dark:text-slate-200 list-disc pl-5 space-y-0.5" data-testid="payroll-legal-fixed">
        <li>Mindst 11 t hvile inden for 24 t. Højst 3 gange mellem to ugehvil må det være 9 t. Delt hvil (først 3 t, så 9 t) tæller som 11 t.</li>
        <li>Højst 60 t arbejde på en uge.</li>
        <li>Pause mindst 30 min ved 6–9 t arbejde og 45 min over 9 t (pauser på mindst 15 min), højst 6 t i træk (tjekkes, når pausen ikke trækkes fast).</li>
      </ul>
      <div className="text-xs text-slate-500">Står i loven (EU 561/2006 og lov 395/2005) og kan ikke ændres.</div>
      <div className="flex flex-wrap items-center gap-2 text-sm pt-3 mt-1 border-t border-slate-100 dark:border-slate-700">
        Ved natarbejde (kl.
        <select className={inputFit} value={lg.night_from} data-testid="payroll-legal-night-from"
          onChange={(e) => { const f = e.target.value; set({ night_from: f, night_to: hr(lg.night_to) - hr(f) >= 4 ? lg.night_to : NIGHT_TO[hr(f)] }); }}>
          {NIGHT_FROM.map((t) => <option key={t} value={t}>{t.slice(0, 2)}</option>)}
        </select>
        –
        <select className={inputFit} value={lg.night_to} data-testid="payroll-legal-night-to" onChange={(e) => set({ night_to: e.target.value })}>
          {NIGHT_TO.filter((t) => hr(t) - hr(lg.night_from) >= 4).map((t) => <option key={t} value={t}>{t.slice(0, 2)}</option>)}
        </select>
        ) højst
        <input className={`${inputFit} w-14`} value={dk(lg.night_max_h)} onChange={(e) => set({ night_max_h: e.target.value.replace(",", ".") })} data-testid="payroll-legal-night-max" />
        timers arbejde i døgnet.
      </div>
      <div className="text-xs text-slate-500">
        Loven: kl. 01–05 og højst 10 t. En kollektiv overenskomst kan ændre det (natperioden skal være mindst 4 t mellem kl. 00 og 07).
        {changed && <button type="button" className="ml-1 underline text-emerald-700" onClick={() => set({ night_from: "01:00", night_to: "05:00", night_max_h: 10 })}>Brug lovens</button>}
      </div>
    </RuleCard>
  );
}

const PAY_HELP = {
  normal: ["Timeløn: dagen giver skemaets timer til løn.", "Fast løn: lønnen kører videre.", "Fx syg, barns sygedag."],
  allowance: ["Timeløn: ingen timer – pengene kommer fra feriepenge (12,5 %) eller frihedskontoen.", "Fast løn: ferie med løn (lønnen kører videre).", "Fx ferie, feriefridag."],
  unpaid: ["Timeløn: ingen timer.", "Fast løn: dagens timer trækkes i lønnen.", "Fx ferie uden løn, fri uden løn."],
};

function AbsenceSection({ r, up, meta }) {
  const [da, setDa, clearDa] = useRuleDraft("absence", { name: "", pay: "unpaid" });
  const name = da.name, pay = da.pay;
  const types = r.absence_types || [];
  const set = (code, patch) => up({ absence_types: types.map((x) => (x.code === code ? { ...x, ...patch } : x)) });
  const add = () => { up({ absence_types: withAbsenceType(types, meta, name, pay) }); clearDa(); };
  return (
    <RuleCard title="Fraværstyper" help="Hver type siger, hvordan dagen betales. Alle fraværsdage kommer med i lønfilen som dage, så lønsystemet kan holde styr på ferie- og fraværssaldi.">
      <div className="grid md:grid-cols-3 gap-2">
        {Object.entries(meta.pay_modes || {}).map(([k, l]) => (
          <div key={k} className="rounded-xl bg-slate-50 dark:bg-slate-900/40 p-3 text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
            <div className="font-bold text-slate-800 dark:text-slate-100 text-sm">{l}</div>
            {(PAY_HELP[k] || []).map((t, i) => <div key={i} className={i === 2 ? "text-slate-500" : ""}>{t}</div>)}
          </div>
        ))}
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm mt-2" data-testid="payroll-absence-types">
        <thead className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <tr><th className="py-1.5">Navn</th><th className="py-1.5">Betaling</th><th className="py-1.5" title="Overenskomstens betingelser - tom = ingen">Løn kræver (timeløn)</th><th className="py-1.5">Timer i lønfil</th><th className="py-1.5">Kan vælges</th><th /></tr>
        </thead>
        <tbody>
          {types.map((x) => (
            <tr key={x.code} className={`border-t border-slate-100 dark:border-slate-700 ${x.active ? "" : "opacity-60"}`}>
              <td className="py-1.5 pr-2"><input className={`${inputFit} w-48`} value={x.name} onChange={(e) => set(x.code, { name: e.target.value })} /></td>
              <td className="py-1.5 pr-2">
                <select className={inputFit} value={x.pay} onChange={(e) => set(x.code, { pay: e.target.value })} data-testid={`payroll-abs-pay-${x.code}`}>
                  {Object.entries(meta.pay_modes || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </td>
              <td className="py-1.5 pr-2">
                {x.pay === "normal" ? (
                  <div className="flex flex-wrap items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
                    <input className={`${inputFit} w-12 !py-1`} value={x.min_seniority_months || ""} placeholder="–" onChange={(e) => set(x.code, { min_seniority_months: e.target.value })} data-testid={`payroll-abs-sen-${x.code}`} /> mdr. ansættelse ·
                    højst <input className={`${inputFit} w-12 !py-1`} value={x.max_days_per_spell || ""} placeholder="–" onChange={(e) => set(x.code, { max_days_per_spell: e.target.value })} data-testid={`payroll-abs-max-${x.code}`} /> dage pr. forløb
                  </div>
                ) : <span className="text-xs text-slate-400">–</span>}
              </td>
              <td className="py-1.5"><input type="checkbox" checked={!!x.export_hours} onChange={(e) => set(x.code, { export_hours: e.target.checked })} aria-label={`${x.name}: send timerne med`} title="Send skemaets timer med i lønfilen (fx afspadsering, så saldoen kan trækkes)" /></td>
              <td className="py-1.5"><input type="checkbox" checked={x.active} onChange={(e) => set(x.code, { active: e.target.checked })} aria-label={`${x.name} kan vælges`} /></td>
              <td className="py-1.5 text-right">
                {x.custom && <button onClick={() => up({ absence_types: types.filter((y) => y.code !== x.code) })} className="text-xs font-semibold text-red-700" aria-label={`Slet ${x.name}`}><Trash2 className="w-3.5 h-3.5" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <input className={`${inputFit} w-48`} placeholder="Ny type, fx Omsorgsdag" value={name} onChange={(e) => setDa({ name: e.target.value })} data-testid="payroll-abs-new-name" />
        <select className={inputFit} value={pay} onChange={(e) => setDa({ pay: e.target.value })}>
          {Object.entries(meta.pay_modes || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button className={btnGhost} disabled={!name.trim()} onClick={add} data-testid="payroll-abs-add"><Plus className="w-4 h-4" />Tilføj</button>
      </div>
      <div className="text-xs text-slate-500">En type der ikke kan vælges, forsvinder fra listen ved dagene – registreringer der allerede er lavet, tæller stadig. Egne typer kan slettes, når de ikke er brugt. Lønartkoderne står under "Lønartkoder og kontrol".</div>
      <div className="text-xs text-slate-500"><b>Løn kræver</b>: fx løn under sygdom først efter et antal måneders ansættelse, eller barns sygedag højst 1-2 dage pr. gang - slå tallene op i jeres overenskomst. Ud over betingelsen registreres dagen uden løn (lønsystemet regner sygedagpenge) og vises orange. Et forløb er dage i træk; fridage og helligdage imellem bryder det ikke.</div>
    </RuleCard>
  );
}


// 2026-10-25: Hvad hvert felt betyder - i praksis og teknisk (brugerønske).
const MONEY_HELP = {
  ot1: "Kroner pr. time oven i timelønnen for de første overtimer (hvor mange står under 'Overtid, helligdage og tillæg').",
  ot2: "Kroner pr. time oven i timelønnen for overtimer ud over overtid 1 - og for søn- og helligdage fra første minut.",
  aften: "Kroner pr. time oven i timelønnen for timer i aften-tidsrummet (sat under 'Aften- og nattillæg').",
  nat: "Kroner pr. time oven i timelønnen for timer i nat-tidsrummet.",
  timer: "Timelønnen for chauffører, der hverken har en lønsats eller en egen timeløn.",
  syg: "Kroner pr. time for sygetimer med løn. Tom = chaufførens egen timeløn (det almindelige).",
};
const PCT_HELP = [
  ["Frihedskonto", "En opsparing, som firmaet betaler ind til hver medarbejder: en procent af lønnen (satsen står i overenskomsten - knappen \"Udfyld efter overenskomst\" sætter den). Medarbejderen får pengene, når der holdes fri uden løn - fx feriefridage eller barns sygedag - og det, der er tilbage, udbetales typisk én gang om året. Teknisk: procenten regnes af timelønnen + tillæg i første række. I ContainerFlow er det kun en omkostning i kroneoversigten - opsparingen og udbetalingen fører lønsystemet."],
  ["Særligt løntillæg", "Et procenttillæg efter overenskomsten (knappen \"Udfyld efter overenskomst\" sætter satserne med datoer) - regnes som frihedskontoen."],
  ["Feriepenge", "12,5 % af lønnen for timelønnede (ferieloven). Regnes af summen inkl. første række."],
  ["Pension (arbejdsgiver)", "Firmaets del af pensionen (satsen står i overenskomsten/pensionsaftalen). Medarbejderens egen del trækkes i lønnen og er ikke en ekstra omkostning."],
];

function MoneySection({ r, up, drivers = [], agreements = [] }) {
  const m = r.money;
  const groups = m.groups || [];
  const versions = m.versions || [];
  const setM = (patch) => up({ money: { ...m, ...patch } });
  const setG = (id, patch) => setM({ groups: groups.map((g) => (g.id === id ? { ...g, ...patch } : g)) });
  const addGroup = () => {
    const id = newId("g");
    setM({ groups: [...groups, { id, name: "Ny lønsats", rates: [{ valid_from: localDate(), rate: "" }], step_months: 0, step_to: "" }],
      default_group: m.default_group || id });
  };
  // Efter overenskomsten: grundløn (og evt. sats efter N måneder, som et trin) med datoerne.
  const addAgreement = (a) => {
    const gs = agreementGroups(a, newId("g"));
    setM({ groups: [...groups, ...gs], default_group: m.default_group || gs[0].id });
  };
  const fillAgreement = (a) => {
    if (versions.length && !window.confirm(`Erstat jeres tillæg og procenter med ${a.short}-satserne (${a.versions.length} datoer)?`)) return;
    setM({ versions: agreementVersions(a) });
  };
  // Nye satser fra overenskomsten (fx fra 1. marts): lægges til de satser, der kom derfra.
  const updates = agreementUpdates(m, agreements);
  const applyUpdate = ({ a, newer }) => {
    const vs = a.versions.filter((v) => newer.includes(v.valid_from));
    setM({
      groups: groups.map((g) => (g.agreement?.id !== a.id ? g : { ...g, rates: [...g.rates, ...vs.map((v) => ({ valid_from: v.valid_from, rate: g.agreement.kind === "step" ? v.step_rate : v.base_rate }))] })),
      versions: versions.some((v) => v.agreement === a.id) ? [...versions, ...agreementVersions(a, newer)].sort((x, y) => x.valid_from.localeCompare(y.valid_from)) : versions,
    });
  };
  const setV = (i, patch) => setM({ versions: versions.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <>
      {updates.map((u) => (
        <div key={u.a.id} className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm flex flex-wrap items-center gap-3" data-testid={`agreement-update-${u.a.id}`}>
          <span className="flex-1 min-w-0"><b>Nye {u.a.short}-satser</b> fra {u.newer.map(dkDate).join(" og ")} er klar. De lægges til jeres satser fra {u.a.short} (lønsatser og tillæg) - gem bagefter med "Gem regler".</span>
          <button className={btnGhost} onClick={() => applyUpdate(u)} data-testid={`agreement-apply-${u.a.id}`}><Plus className="w-4 h-4" />Tilføj</button>
        </div>
      ))}
      <RuleCard title="Lønsatser (grundløn pr. time)" help="Giv chaufførerne en lønsats i stedet for en timeløn hver. En ny sats fra en dato (fx 1. marts) gælder for timerne fra den dag. Et trin skifter chaufføren til en anden sats efter et antal måneders ansættelse (kræver ansættelsesdato). En egen timeløn på chaufføren går altid forud.">
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid={`payroll-group-card-${g.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <input className={`${inputFit} font-semibold w-56`} value={g.name} onChange={(e) => setG(g.id, { name: e.target.value })} aria-label="Lønsatsens navn" />
                <label className="flex items-center gap-1.5 text-sm"><input type="radio" name="std-group" checked={m.default_group === g.id} onChange={() => setM({ default_group: g.id })} /> Standard</label>
                <button onClick={() => {
                  // 2026-10-24 (N37): Sig hvem der mister satsen (de får standard eller ingen).
                  const users = drivers.filter((d) => d.profile?.pay_group_id === g.id || (m.default_group === g.id && !d.profile?.pay_group_id && d.profile?.base_rate == null)).map((d) => d.name);
                  if (users.length && !window.confirm(`${users.length} chauffør${users.length === 1 ? "" : "er"} får timeløn fra satsen (${users.slice(0, 5).join(", ")}${users.length > 5 ? " …" : ""}). Slettes den, ændres deres timeløn i kroneoversigten - også for den åbne periode. Slet?`)) return;
                  setM({ groups: groups.filter((x) => x.id !== g.id), default_group: m.default_group === g.id ? "" : m.default_group });
                }}
                  className="ml-auto text-xs font-semibold text-red-700 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Slet</button>
              </div>
              <div className="flex flex-wrap gap-3 items-end">
                {g.rates.map((x, i) => (
                  <div key={i} className="flex items-end gap-1.5">
                    <div><label className={label}>Fra</label><input type="date" className={inputFit} value={x.valid_from} onChange={(e) => setG(g.id, { rates: g.rates.map((y, j) => (j === i ? { ...y, valid_from: e.target.value } : y)) })} /></div>
                    <div><label className={label}>Kr/t</label><input className={`${inputFit} w-24`} value={dk(x.rate)} onChange={(e) => setG(g.id, { rates: g.rates.map((y, j) => (j === i ? { ...y, rate: e.target.value.replace(",", ".") } : y)) })} data-testid={`payroll-group-rate-${g.id}-${i}`} /></div>
                    {g.rates.length > 1 && <button className="pb-2" onClick={() => setG(g.id, { rates: g.rates.filter((_, j) => j !== i) })} aria-label="Fjern sats"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
                <button className="text-xs font-semibold text-emerald-700 pb-2" onClick={() => setG(g.id, { rates: [...g.rates, { valid_from: localDate(), rate: g.rates[g.rates.length - 1]?.rate || "" }] })}>+ Ny sats fra dato</button>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                Efter
                <input className={`${inputFit} w-16`} value={g.step_months || ""} placeholder="–" inputMode="numeric" onChange={(e) => setG(g.id, { step_months: Number(e.target.value.replace(/\D/g, "")) || 0 })} />
                måneders ansættelse skiftes til
                <select className={inputFit} value={g.step_to || ""} onChange={(e) => setG(g.id, { step_to: e.target.value })}>
                  <option value="">(intet trin)</option>
                  {groups.filter((x) => x.id !== g.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <button className={btnGhost} onClick={addGroup} data-testid="payroll-group-add"><Plus className="w-4 h-4" />Ny lønsats (tom)</button>
          {agreements.map((a) => (
            <button key={a.id} className={btnGhost} onClick={() => addAgreement(a)} data-testid={`payroll-group-add-${a.id}`}><Plus className="w-4 h-4" />Lønsatser efter {a.short}</button>
          ))}
        </div>
        <div className="text-xs text-slate-500">
          "Ny lønsats" er tom - I skriver selv navn og kroner.{" "}
          {agreements.map((a) => (
            <span key={a.id}>"Efter {a.short}" opretter satserne fra {a.name}: grundløn {a.versions.map((v) => `${kr2n(v.base_rate)} kr/t fra ${dkDate(v.valid_from)}`).join(", ")}
              {a.step_months ? ` - og efter ${a.step_months} måneders ansættelse ${a.versions.map((v) => kr2n(v.step_rate)).join(" / ")} (trinnet kræver ansættelsesdato på chaufføren)` : ""}. </span>
          ))}
          Satserne vedligeholdes af ContainerFlow; kommer der nye, får I besked her. Ret dem, hvis jeres aftale er en anden.
        </div>
      </RuleCard>

      <RuleCard title="Tillæg og procenter" help="Kronebeløb pr. time for overtid og tillæg, og procenter oven i (frihedskonto, feriepenge, pension). Bruges kun til oversigten over lønomkostning og dækningsbidrag – lønsystemet laver den rigtige løn. Procenterne i anden række regnes af summen inkl. første række (som ILK's regneark) - lønsystemet er facit.">
        <div className="flex flex-wrap gap-2">
          {agreements.map((a) => (
            <button key={a.id} className={btnGhost} onClick={() => fillAgreement(a)} data-testid={`payroll-money-fill-${a.id}`}>Udfyld efter overenskomst ({a.short})</button>
          ))}
          <button className={btnGhost} onClick={() => setM({ versions: [...versions, { valid_from: localDate(), rates: { ...(versions[versions.length - 1]?.rates || {}) }, pct1: [...(versions[versions.length - 1]?.pct1 || [])], pct2: [...(versions[versions.length - 1]?.pct2 || [])] }] })}>
            <Plus className="w-4 h-4" /> Nye satser fra dato
          </button>
        </div>
        {versions.map((v, i) => (
          <div key={i} className="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold">Gælder fra</label>
              <input type="date" className={inputFit} value={v.valid_from} onChange={(e) => setV(i, { valid_from: e.target.value })} />
              <button className="ml-auto text-xs text-red-700 font-semibold" onClick={() => setM({ versions: versions.filter((_, j) => j !== i) })}>Fjern</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* 2026-10-25: Med en standard-lønsats bruges "Timeløn uden lønsats"
                  aldrig - så vises den ikke (ellers to steder at hæve timelønnen). */}
              {[["ot1", "Overtid 1"], ["ot2", "Overtid 2"], ["aften", "Aften"], ["nat", "Nat"],
                ...(m.default_group && groups.some((g) => g.id === m.default_group) ? [] : [["timer", "Timeløn uden lønsats"]]),
                ["syg", "Syg (tom = timeløn)"]].map(([k, l]) => (
                <div key={k} title={MONEY_HELP[k]}><label className={label}>{l}</label>
                  <input className={`${inputFit} w-24`} value={dk(v.rates[k] ?? "")} onChange={(e) => setV(i, { rates: { ...v.rates, [k]: e.target.value.replace(",", ".") } })} aria-describedby="payroll-money-help" />
                </div>
              ))}
            </div>
            {[["pct1", "Procenter af timeløn med tillæg (fx frihedskonto, særligt løntillæg)"], ["pct2", "Procenter af summen (fx feriepenge, pension)"]].map(([k, l]) => (
              <div key={k}>
                <div className={label}>{l}</div>
                <div className="flex flex-wrap gap-2 items-center">
                  {v[k].map((p, pi) => (
                    <span key={pi} className="inline-flex items-center gap-1">
                      <input className={`${inputFit} w-36 !py-1`} value={p.name} onChange={(e) => setV(i, { [k]: v[k].map((q, qi) => (qi === pi ? { ...q, name: e.target.value } : q)) })} />
                      <input className={`${inputFit} w-16 !py-1`} value={dk(p.pct)} onChange={(e) => setV(i, { [k]: v[k].map((q, qi) => (qi === pi ? { ...q, pct: e.target.value.replace(",", ".") } : q)) })} />%
                      <button onClick={() => setV(i, { [k]: v[k].filter((_, qi) => qi !== pi) })} aria-label="Fjern"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  ))}
                  <button className="text-xs font-semibold text-emerald-700" onClick={() => setV(i, { [k]: [...v[k], { name: "", pct: 0 }] })}>+ Tilføj</button>
                </div>
              </div>
            ))}
          </div>
        ))}
        <details className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-900/40 p-3 text-xs text-slate-600 dark:text-slate-300" id="payroll-money-help" data-testid="payroll-money-help">
          <summary className="font-semibold cursor-pointer text-slate-800 dark:text-slate-100">Hvad betyder felterne?</summary>
          <dl className="mt-2 space-y-1.5">
            {[["ot1", "Overtid 1"], ["ot2", "Overtid 2"], ["aften", "Aften"], ["nat", "Nat"], ["timer", "Timeløn uden lønsats"], ["syg", "Syg"]].map(([k, l]) => (
              <div key={k}><dt className="inline font-semibold">{l}: </dt><dd className="inline">{MONEY_HELP[k]}</dd></div>
            ))}
            {PCT_HELP.map(([l, t]) => <div key={l}><dt className="inline font-semibold">{l}: </dt><dd className="inline">{t}</dd></div>)}
            <div className="pt-1 text-slate-500"><b>Første række</b> regnes af timeløn + tillæg. <b>Anden række</b> regnes af summen inkl. første række (som ILK's regneark). Kronerne bruges kun til oversigten over lønomkostning og dækningsbidrag - lønfilen sender timer og dage, og lønsystemet regner den rigtige løn med sine egne satser.</div>
            <div className="text-slate-500"><b>Forskellige satser for nogle medarbejdere?</b> Grundlønnen sættes pr. lønsats eller som egen timeløn på chaufføren; faste kroner oveni som personlige tillæg (Løn › Chauffører). Overtids- og aftentillæg er de samme for alle - som i ATL. Lønsystemet er facit for den enkeltes løn.</div>
          </dl>
        </details>
        {versions.length > 0 && m.default_group && groups.some((g) => g.id === m.default_group) && (
          <div className="mt-2 text-xs text-slate-500">Timelønnen kommer fra lønsatserne ovenfor (eller chaufførens egen timeløn). En regulering af timelønnen laves dér med "+ Ny sats fra dato"; tillæg og procenter reguleres her med "Nye satser fra dato".</div>
        )}
        {!versions.length && <div className="text-xs text-amber-700">Uden satser vises ingen kroner – kun timer.</div>}
      </RuleCard>
    </>
  );
}

// 2026-10-24: Alle lønartkoder i én tabel (før lå fraværstypernes koder et
// andet sted end arbejdstidens), og firmaets egne lønlinjer (C1).
function ExportSection({ r, up, meta }) {
  const types = r.absence_types || [];
  const setType = (code, patch) => up({ absence_types: types.map((x) => (x.code === code ? { ...x, ...patch } : x)) });
  const extras = r.extra_lines || [];
  const setX = (code, patch) => up({ extra_lines: extras.map((x) => (x.code === code ? { ...x, ...patch } : x)) });
  const [dx, setDx, clearDx] = useRuleDraft("extra", { name: "", unit: "stk" });
  const xName = dx.name, xUnit = dx.unit;
  const addX = () => { up({ extra_lines: withExtraLine(extras, meta, xName, xUnit) }); clearDx(); };
  const conditional = (t) => t.pay === "normal" && (num(t.min_seniority_months) > 0 || num(t.max_days_per_spell) > 0);
  const ex = r.export || { system: "generic", cvr: "", company_no: "", period_code: "" };
  const setEx = (patch) => up({ export: { ...ex, ...patch } });
  // 2026-10-25: Lønsystemernes lister med super admins tilføjelser (Super admin › Lønsystemer).
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    axios.get(`${API}/payroll/pay-systems`).then((res) => setCatalog(res.data)).catch(() => setCatalog({}));
  }, []);
  // Firmaets egne Danløn-typer (T6+/O6+) lægges oven på listen - tjekket nedenfor kender dem straks.
  const ownTypes = ex.own_types || [];
  applyPayCatalog(catalog || {}, ownTypes);
  const setOwn = (list) => setEx({ own_types: list });
  const [nOwn, setNOwn] = useState({ code: "", name: "", cols: "E" });
  const strict = ex.system === "danlon" || ex.system === "dataloen";
  const sysLabel = (k) => (k === "danlon" ? "Danløn" : k === "dataloen" ? "DataLøn" : "Andet (CSV)");
  const sysName = strict ? sysLabel(ex.system) : "";
  // 2026-10-25 (L1): Koderne hører til lønsystemet (Danløns 1 = Timeløn 1, men
  // DataLøns 0001 = Arbejdstimer). Skifter firmaet system, tømmes felterne, så
  // de gamle numre ikke sendes til det nye system. Serveren gør det samme ved "Gem".
  const anyCode = Object.values(r.codes || {}).some((v) => String(v || "").trim())
    || types.some((t) => [t.export_code, t.export_code_hours, t.export_code_unpaid].some((v) => String(v || "").trim()))
    || extras.some((x) => String(x.export_code || "").trim());
  const switchSystem = (k) => {
    if (k === ex.system) return;
    if (anyCode && !window.confirm(`Skift til ${sysLabel(k)}?\n\nKoderne fra ${sysLabel(ex.system)} passer ikke i ${sysLabel(k)} (fx er Danløns 1 Timeløn 1, men DataLøns 0001 er Arbejdstimer). Alle lønartkoder tømmes, så I kan skrive ${k === "generic" ? "jeres lønsystems koder" : `${sysLabel(k)}s koder`}.\n\nIntet gemmes, før du trykker "Gem regler".`)) return;
    up({ export: { ...ex, system: k, codes_for: k }, codes: Object.fromEntries(Object.keys(r.codes || {}).map((c) => [c, ""])),
      absence_types: types.map((t) => ({ ...t, export_code: "", export_code_hours: "", export_code_unpaid: "" })),
      extra_lines: extras.map((x) => ({ ...x, export_code: "" })) });
    if (anyCode) toast.info(`Indtast koderne fra ${k === "generic" ? "jeres lønsystem" : sysLabel(k)}. Koderne fra ${sysLabel(ex.system)} er tømt.`);
  };
  // 2026-10-25: Alle lønarter som rækker (lønart, enhed, bærer løn, forslag), så
  // koden kan tjekkes ved feltet - før kom fejlen først, når lønfilen blev hentet,
  // og den sagde ikke, hvad der skulle stå (Danløn bruger numre, fx 6 = Overtid 1).
  const S = DANLON_SUGGEST;
  const rows = [
    // Tillæg til afspadsering bruges kun med fluebenet under Afspadsering - ellers foreslås "-".
    ...(meta.base_lines || []).map(([k, l]) => ({ key: k, name: l, value: r.codes[k], unit: "timer", paid: !k.endsWith("_afsp") || !!r.bank_premiums,
      suggest: k.endsWith("_afsp") ? (r.bank_premiums ? "" : "-") : S.base[k] || "",
      kind: k === "afsp_timer" || k.endsWith("_afsp") ? "afsp" : "base",
      hint: k === "afsp_timer" ? "kun ved afspadsering - timerne til saldoen" : k.endsWith("_afsp") ? "kun med 'tillæggene afspadseres også' - antal timer med tillæg"
        : k === "timer" ? "kun timelønnede" : k === "overtimer" ? "fastlønnedes overtimer i alt - tillæggene står også på Overtid 1/2" : "",
      set: (v) => up({ codes: { ...r.codes, [k]: v } }), apply: ["codes", k] })),
    ...types.map((t) => ({ key: `abs-${t.code}`, name: t.name, value: t.export_code, unit: "dage", paid: false, suggest: S.days[t.code] ?? "", kind: "days",
      hint: "fravær - antal dage", set: (v) => setType(t.code, { export_code: v }), apply: ["type", t.code, "export_code"] })),
    ...types.filter((t) => t.pay !== "allowance" || t.export_hours).map((t) => ({ key: `absh-${t.code}`, name: `${t.name} (timer)`, value: t.export_code_hours,
      unit: "timer", paid: t.pay === "normal" || !!t.export_hours, suggest: S.hours[t.code] ?? "", kind: "hours",
      hint: strict ? (t.pay === "normal" ? "timer med løn - kun timelønnede" : "timer - fradrag / afspadsering") : "timer - tom = samme kode som dagene",
      set: (v) => setType(t.code, { export_code_hours: v }), apply: ["type", t.code, "export_code_hours"] })),
    ...types.filter(conditional).map((t) => ({ key: `absu-${t.code}`, name: `${t.name} uden løn`, value: t.export_code_unpaid, unit: "dage", paid: false, kind: "days",
      suggest: S.unpaid, hint: "dage ud over betingelsen", set: (v) => setType(t.code, { export_code_unpaid: v }), apply: ["type", t.code, "export_code_unpaid"] })),
    ...extras.map((x) => ({ key: `x-${x.code}`, name: x.name, value: x.export_code, unit: x.unit, paid: true, suggest: x.unit === "kr" ? S.kr : "", kind: "extra",
      hint: `egen linje (${x.unit})`, set: (v) => setX(x.code, { export_code: v }), apply: ["extra", x.code] })),
  ];
  // Koden, som den sendes (Danløn: "T1" = "1"; DataLøn: "47" = "0047").
  const keyOf = (v) => (ex.system === "danlon" ? (danlonType(v)?.code || "") : ex.system === "dataloen" ? (dataloenType(v)?.sent || String(v || "").trim().toUpperCase().padStart(4, "0"))
    : String(v || "").trim().toUpperCase());
  // Samme kode til dage og timer ville blive lagt sammen til ét tal.
  const unitsOf = {};
  rows.forEach((x) => {
    const k = String(x.value || "").trim() && String(x.value).trim() !== "-" ? keyOf(x.value) : "";
    if (k) (unitsOf[k] = unitsOf[k] || new Set()).add(x.unit);
  });
  const bankKey = String(r.codes?.afsp_timer || "").trim() ? keyOf(r.codes.afsp_timer) : "";
  const checked = rows.map((x) => {
    const c = checkCode(ex.system, x.value, x.unit, { paid: x.paid, suggest: ex.system === "danlon" ? x.suggest : "", kind: x.kind });
    const k = String(x.value || "").trim() && String(x.value).trim() !== "-" ? keyOf(x.value) : "";
    if (strict && c.level !== "error" && k && unitsOf[k]?.size > 1) {
      return { ...x, check: { level: "error", text: `Koden bruges til både ${[...unitsOf[k]].join(" og ")} - giv dem hver sin kode`, suggest: ex.system === "danlon" ? x.suggest : "" } };
    }
    // 2026-10-25 (L7): Tillægstimerne er antal timer med tillæg - på samme kode som
    // saldoens timer bliver de lagt til som hele timer (2 t + 2 t = 4 t).
    if ((x.key === "ot1_afsp" || x.key === "ot2_afsp") && k && k === bankKey && c.level !== "error") {
      return { ...x, check: { level: "warn", kind: "meaning", text: 'Samme kode som "Overtimer til afspadsering": tillægstimerne lægges til saldoen som hele timer - passer kun, hvis tillægget er 100 %' } };
    }
    return { ...x, check: c };
  });
  const errors = checked.filter((x) => x.check.level === "error").length;
  // 2026-10-25: Tælles hver for sig - før talte "sendes ikke" også forkortelserne med (16 i stedet for 5).
  const dashWarns = checked.filter((x) => x.check.kind === "dash").length;
  const abbrWarns = checked.filter((x) => x.check.kind === "abbr").length;
  const meaningWarns = checked.filter((x) => x.check.kind === "meaning" || x.check.kind === "unknown").length;
  // "Udfyld forslag": tomme og forkerte koder, og "-" på en lønart med løn - en gyldig kode røres ikke.
  const fixable = (x) => x.suggest && x.suggest !== x.value && (x.check.level === "error" || (x.check.kind === "dash" && x.suggest !== "-"));
  const fillSuggestions = () => {
    const codes = { ...r.codes };
    let tps = types.map((t) => ({ ...t }));
    let xs = extras.map((x) => ({ ...x }));
    let n = 0;
    checked.forEach((x) => {
      if (!fixable(x)) return;
      n += 1;
      if (x.apply[0] === "codes") codes[x.apply[1]] = x.suggest;
      else if (x.apply[0] === "type") tps = tps.map((t) => (t.code === x.apply[1] ? { ...t, [x.apply[2]]: x.suggest } : t));
      else xs = xs.map((e) => (e.code === x.apply[1] ? { ...e, export_code: x.suggest } : e));
    });
    up({ codes, absence_types: tps, extra_lines: xs });
    toast.success(n ? `${n} koder udfyldt med forslag - tjek dem med bogholderen, og tryk "Gem regler"` : "Ingen koder at udfylde");
  };
  return (
    <>
      {/* 2026-10-25: Lønfilen i lønsystemets eget format (Danløn, DataLøn). */}
      <RuleCard title="Lønsystem" help="Lønfilen laves i det format, jeres lønsystem kan indlæse. Antallet kommer fra perioden; koderne nedenfor fortæller lønsystemet, hvilken lønart det er.">
        <div className="space-y-3" data-testid="payroll-export-system">
          <div className="flex flex-wrap gap-2">
            {[["generic", "Andet (CSV)"], ["danlon", "Danløn"], ["dataloen", "DataLøn"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => switchSystem(k)} data-testid={`payroll-export-${k}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${ex.system === k ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 dark:border-slate-600"}`}>{l}</button>
            ))}
          </div>
          {ex.system === "danlon" && (
            <div className="space-y-2">
              <div className="max-w-xs"><label className="block text-xs font-semibold text-slate-500 mb-1">CVR-nummer</label>
                <input className={`${inputFit} w-40`} value={ex.cvr} onChange={(e) => setEx({ cvr: e.target.value })} inputMode="numeric" placeholder="12345678" data-testid="payroll-export-cvr" /></div>
              <ul className="text-xs text-slate-500 list-disc pl-4 space-y-0.5" data-testid="payroll-export-danlon-help">
                <li>Indlæses i Danløn under Lønberegning › Indlæs fra fil. Kodefeltet er Danløns løntype - fx 1-5 timeløn, 6-10 overtid, 51 sygeløn (timer), 53 sygedage, 60 afholdte feriedage, 70/71 afspadsering optjent/afholdt, 41 tillæg i kroner. Aftal numrene med bogholderen.</li>
                <li>Timer, dage og stk sendes som antal uden sats. Danløn bruger firmaets standardsats fra <b>Teksttilpasninger</b>, medmindre "Individuel" er krydset af på medarbejderen. <b>Kroner</b> (fx en egen lønlinje i kr) sendes som beløb - brug en løntype, der tager beløb, fx 41 tillæg ej ferieberettiget. Spørg bogholderen. (40 er refusion af udlæg - skattefri.)</li>
                <li><b>Fastlønnede</b>: den faste løn kører i Danløn, så filen har ingen "Timer" for dem - kun overtimer, tillæg og fraværsdage (fx 53 sygedage, 60 feriedage). Timer med løn ved fravær (fx 51 sygeløn) sendes kun for timelønnede. Fravær uden løn kan Danløn ikke tage som dage: Løn viser dem, når perioden godkendes, så bogholderen kan trække dem (fx 93 gagereduktion i kroner).</li>
                <li><b>Overtid 1/2, aften og nat er tillæg</b>: timerne står også under Timer. Sæt derfor kun tillægget som sats på løntypen i Danløn - ikke hele overtidsbetalingen.</li>
                <li>Skriv <b>-</b> som kode for en lønart, Danløn ikke skal have (fx dage for "Fri uden løn" - Danløn har ingen løntype til dem).</li>
                <li>Danløn afviser hele filen ved én fejl, så ContainerFlow tjekker koderne, CVR og lønnumrene først. En indlæst værdi <b>erstatter</b> den, der står på løntypen - indlæses filen igen, lægges tallene ikke oveni.</li>
                <li>Nye løntyper fra Danløn kommer i listen af sig selv (ContainerFlow vedligeholder den). Har I selv oprettet løntyper i Danløn (T6 og op for timeløn, O6 og op for overtid), så skriv dem herunder - så står de i listen med jeres navn.</li>
              </ul>
            </div>
          )}
          {ex.system === "danlon" && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="payroll-own-types">
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">Jeres egne løntyper i Danløn (T6+ og O6+)</div>
              {ownTypes.map((t, i) => (
                <div key={t.code} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-mono w-12">{t.code}</span>
                  <input className={`${inputFit} flex-1 min-w-[140px]`} value={t.name || ""} placeholder="Navn, fx Overtid 50 % afspadsering"
                    onChange={(e) => setOwn(ownTypes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  <select className={inputFit} value={t.cols || "EB"} onChange={(e) => setOwn(ownTypes.map((x, j) => (j === i ? { ...x, cols: e.target.value } : x)))}>
                    <option value="E">Antal</option><option value="B">Beløb</option><option value="EB">Antal og beløb</option>
                  </select>
                  <button type="button" className="text-red-700 text-xs inline-flex items-center gap-1" onClick={() => setOwn(ownTypes.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" />Fjern</button>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2">
                <input className={`${inputFit} w-20`} placeholder="O6" value={nOwn.code} onChange={(e) => setNOwn({ ...nOwn, code: e.target.value.toUpperCase() })} data-testid="payroll-own-code" />
                <input className={`${inputFit} flex-1 min-w-[140px]`} placeholder="Navn i Danløn" value={nOwn.name} onChange={(e) => setNOwn({ ...nOwn, name: e.target.value })} />
                <select className={inputFit} value={nOwn.cols} onChange={(e) => setNOwn({ ...nOwn, cols: e.target.value })}>
                  <option value="E">Antal</option><option value="B">Beløb</option><option value="EB">Antal og beløb</option>
                </select>
                <button type="button" className={btnGhost} data-testid="payroll-own-add" onClick={() => {
                  const m = /^([TO])0*(\d{1,2})$/.exec(nOwn.code.trim());
                  if (!m || Number(m[2]) < 6) { toast.error("Egne løntyper hedder T6 og op (timeløn) eller O6 og op (overtid) - som i Danløn"); return; }
                  const code = `${m[1]}${Number(m[2])}`;
                  if (ownTypes.some((x) => x.code === code)) { toast.error(`${code} står der allerede`); return; }
                  setOwn([...ownTypes, { code, name: nOwn.name.trim(), cols: nOwn.cols }]); setNOwn({ code: "", name: "", cols: "E" });
                }}><Plus className="w-4 h-4" />Tilføj</button>
              </div>
              <p className="text-[11px] text-slate-500">Fjerner I en type, der bruges, beholder lønarten koden - den står så som "brugertilføjet" uden jeres navn.</p>
            </div>
          )}
          {ex.system === "dataloen" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3">
                <div><label className="block text-xs font-semibold text-slate-500 mb-1">DataLøn-firmanummer</label>
                  <input className={`${inputFit} w-40`} value={ex.company_no} onChange={(e) => setEx({ company_no: e.target.value })} inputMode="numeric" data-testid="payroll-export-company-no" /></div>
                {/* 2026-10-25 (L10): DataLøns lønterminer (Minuba/Lønguiden; DataLøns ordbog nævner de fire terminer). */}
                <div><label className="block text-xs font-semibold text-slate-500 mb-1">Løntermin</label>
                  <select className={inputFit} value={ex.period_code || ""} data-testid="payroll-export-period-code"
                    onChange={(e) => {
                      if (e.target.value !== "__other") { setEx({ period_code: e.target.value }); return; }
                      // Et nummer, der ikke står i listen (bogholderen ved bedst) - listen vedligeholdes af ContainerFlow.
                      const v = (window.prompt("Lønterminens nummer i DataLøn (2 cifre):", ex.period_code || "") || "").trim();
                      if (/^\d{2}$/.test(v)) setEx({ period_code: v }); else if (v) toast.error("Lønterminen er 2 cifre, fx 51");
                    }}>
                    <option value="">Vælg ...</option>
                    {payTerms().map((t) => <option key={t.code} value={t.code}>{t.code} - {t.label}</option>)}
                    {ex.period_code && !payTerms().some((t) => t.code === ex.period_code) && <option value={ex.period_code}>{ex.period_code} (findes ikke i listen - tjek med bogholderen)</option>}
                    <option value="__other">Andet nummer ...</option>
                  </select></div>
              </div>
              <ul className="text-xs text-slate-500 list-disc pl-4 space-y-0.5" data-testid="payroll-export-dataloen-help">
                <li>Indlæses i DataLøn under Løn › Lønindberetning › Importer › "DataLøn fil – fleksible løndele". Kodefeltet er DataLøns løndel (4 tegn - 47 bliver til 0047; fx 1213 normaltimer 1, 1617 overtid 1, 0047 afholdt ferie).</li>
                <li>Ved hver kode står løndelens navn fra DataLøns løndeloversigt (marts 2024). En løndel, der ikke passer til lønarten, bliver gul - fx er 0008 <b>Fradrag efter skat</b> og 0006 <b>Sygeløn</b>. 9B "Afspadsering" står i oversigten blandt løndele uden lønbehandling - den giver ingen løn.</li>
                <li>Antal sendes uden sats, så DataLøn bruger satsen fra medarbejderens <b>faste løndele</b> - de skal være sat op i DataLøn. Kroner sendes som beløb.</li>
                <li><b>Overtid 1/2, aften og nat er tillæg</b>: timerne står også under normaltimer - sæt kun tillægget som sats i DataLøn.</li>
                <li>Skriv <b>-</b> som kode for en lønart, DataLøn ikke skal have. Linjer med fejl springer DataLøn over (DataLøns importvejledning), så ContainerFlow tjekker koderne først - og viser summen pr. kode, når filen hentes, så bogholderen kan se, at alt kom ind.</li>
                <li>Lønterminen skal være den, chaufførerne har i DataLøn. Den gælder alle chauffører - ContainerFlow har én lønperiode for hele firmaet.</li>
              </ul>
            </div>
          )}
          {ex.system === "generic" && <p className="text-xs text-slate-500">En CSV med lønnummer, navn, lønart, kode, antal, enhed og periodens fra- og til-dato (semikolon, decimalkomma - åbner i dansk Excel). Bruges til lønsystemer uden færdigt format (Zenegy, Lessor, Intect, Salary ...) - bogholderen kan indlæse den eller taste tallene. Salary kan også læse Danløn-formatet, men kræver en sats på timelinjerne - brug derfor denne. Kode <b>-</b> = kommer ikke med.</p>}
          {/* 2026-10-25 (T12): Besked til bogholderen, når perioden er godkendt. */}
          <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Mail til bogholderen, når en periode godkendes</label>
            <input className={`${inputFit} w-72 max-w-full`} type="email" value={ex.notify_email || ""} onChange={(e) => setEx({ notify_email: e.target.value })}
              placeholder="fx loen@firma.dk - tom = ingen mail" data-testid="payroll-export-notify-email" />
            {ex.notify_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ex.notify_email.trim()) && <p className="text-xs text-amber-700 mt-1">Det ligner ikke en mailadresse - den bliver ikke gemt.</p>}
            <p className="text-xs text-slate-500 mt-1">Mailen siger, at perioden er godkendt, antal chauffører og timer i alt, og hvornår lønfilen skal være sendt. Ingen navne eller timer pr. person - lønfilen og timesedlerne hentes under Løn.</p>
          </div>
        </div>
      </RuleCard>
      <RuleCard title="Lønartkoder" help={ex.system === "danlon"
        ? 'Danløns løntype-numre (fx 1 = Timeløn 1, 6 = Overtid 1, 53 = Sygedage). Hvad hvert nummer betyder og satsen, står i jeres Danløn - aftal numrene med bogholderen. Skriv "-" for en lønart, der ikke skal med.'
        : ex.system === "dataloen" ? 'DataLøns løndele (4 tegn, fx 1213). Numrene står i jeres DataLøn under løndele - aftal dem med bogholderen. Skriv "-" for en lønart, der ikke skal med.'
          : 'Koderne fra jeres lønsystem. De står i lønfilen ud for hver linje. Skriv "-" for en lønart, der ikke skal med.'}>
        {strict && (
          <div className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm ${errors ? "bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-100" : dashWarns || abbrWarns ? "bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100" : "bg-emerald-50 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100"}`} data-testid="payroll-codes-status">
            <span className="flex-1">{errors ? `${errors} ${errors === 1 ? "kode passer" : "koder passer"} ikke til ${sysName} - lønfilen kan ikke laves, før de er rettet.`
              : dashWarns || abbrWarns || meaningWarns ? `Lønfilen kan laves - men tjek de gule koder.` : `Alle koder findes i ${sysName}. Bogholderen skal bekræfte, at hver kode betyder det rigtige.`}
              {abbrWarns > 0 && <span className="block">{abbrWarns} {abbrWarns === 1 ? "kode ligner" : "koder ligner"} en forkortelse - brug løndelens nummer fra {sysName}.</span>}
              {meaningWarns > 0 && <span className="block">{meaningWarns} {meaningWarns === 1 ? "kode betyder" : "koder betyder"} noget andet i {sysName} end lønarten - se teksten ved koden.</span>}
              {dashWarns > 0 && <span className="block">{dashWarns} {dashWarns === 1 ? "lønart" : "lønarter"} med løn sendes ikke ("-") - tjek, at det er meningen.</span>}</span>
            {ex.system === "danlon" && checked.some(fixable) && <button type="button" className={btnGhost} onClick={fillSuggestions} data-testid="payroll-codes-fill">Udfyld forslag</button>}
          </div>
        )}
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="payroll-codes">
          <thead className="text-left text-[11px] uppercase tracking-wide text-slate-500"><tr><th className="py-1.5">Lønart</th><th className="py-1.5">{sysName ? `${sysName}-kode` : "Kode"}</th><th /></tr></thead>
          <tbody>
            {checked.map((x) => (
              <tr key={x.key} className="border-t border-slate-100 dark:border-slate-700 align-top">
                <td className="py-1.5 pr-2">{x.name}{x.hint && <span className="block text-[11px] text-slate-500">{x.hint}</span>}</td>
                <td className="py-1.5 pr-2 w-36">
                  {ex.system === "danlon"
                    ? <SuggestInput className={`${inputFit} w-36 ${x.check.level === "error" ? "!border-red-400" : ""}`} value={x.value || ""} onChange={x.set}
                        options={[{ value: "-", hint: "sendes ikke" }, ...danlonOptions(x.unit)]} testid={`payroll-code-${x.key}`} />
                    : <input className={`${inputFit} w-28 ${x.check.level === "error" ? "!border-red-400" : ""}`} value={x.value || ""} onChange={(e) => x.set(e.target.value)} data-testid={`payroll-code-${x.key}`} />}
                </td>
                <td className="py-1.5 text-xs">
                  {x.check.text && <span className={x.check.level === "error" ? "text-red-700 dark:text-red-300" : x.check.level === "warn" ? "text-amber-700 dark:text-amber-300" : "text-slate-500"} data-testid={`payroll-code-check-${x.key}`}>{x.check.text}</span>}
                  {(x.check.level === "error" || (x.check.level === "warn" && x.check.suggest !== "-")) && x.check.suggest && x.check.suggest !== x.value && (
                    <button type="button" className="ml-2 font-semibold text-emerald-700 underline" onClick={() => x.set(x.check.suggest)}>
                      Brug {x.check.suggest}{danlonType(x.check.suggest)?.name ? ` (${danlonType(x.check.suggest).name})` : x.check.suggest === "-" ? " (sendes ikke)" : ""}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </RuleCard>
      <RuleCard title="Egne lønlinjer" help="Linjer der sættes på en dag: udetillæg ved overnatning, smudstillæg, kursusdag, rådighedsvagt ... Tillæg, der kommer af sig selv (fx søndagstillæg), står under 'Overtid, helligdage og tillæg'."
        more={<>
          <p><b>Sådan bruges en linje:</b> Løn › Lønperiode › klik på dagen hos chaufføren › <b>Lønlinjer</b> › Tilføj › vælg linjen og skriv antallet › Gem. Kun linjer med flueben i "Kan vælges" kan vælges.</p>
          <p><b>Her kommer den med:</b> i lønfilen (med koden fra Lønartkoder ovenfor), på chaufførens periode under Lønarter, på timesedlen (under Bemærkning og som sum for perioden), hos chaufføren under "Mine timer" og - med en sats - i kroneoversigten (antal × sats).</p>
          <p><b>Navnet er ikke koden.</b> Navnet er det, I og chaufføren ser. Koden til lønsystemet skrives under Lønartkoder - tjek, hvad nummeret betyder dér (fx er DataLøns 0008 et fradrag efter skat).</p>
        </>}>
        {extras.some((x) => !x.auto) && (
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="payroll-extra-lines">
            <thead className="text-left text-[11px] uppercase tracking-wide text-slate-500"><tr><th className="py-1.5">Navn</th><th className="py-1.5">Enhed</th><th className="py-1.5">Kr pr. enhed</th><th className="py-1.5">Kan vælges</th></tr></thead>
            <tbody>
              {extras.filter((x) => !x.auto).map((x) => (
                <tr key={x.code} className={`border-t border-slate-100 dark:border-slate-700 ${x.active ? "" : "opacity-60"}`}>
                  <td className="py-1.5 pr-2"><input className={`${inputFit} w-48`} value={x.name} onChange={(e) => setX(x.code, { name: e.target.value })} /></td>
                  <td className="py-1.5 pr-2"><select className={inputFit} value={x.unit} onChange={(e) => setX(x.code, { unit: e.target.value })}>
                    <option value="stk">stk</option><option value="timer">timer</option><option value="kr">kr</option></select></td>
                  <td className="py-1.5 pr-2">{x.unit === "kr" ? <span className="text-xs text-slate-500">beløbet</span> : <input className={`${inputFit} w-24`} value={x.rate ?? ""} placeholder="–" onChange={(e) => setX(x.code, { rate: e.target.value })} />}</td>
                  <td className="py-1.5"><input type="checkbox" checked={x.active} onChange={(e) => setX(x.code, { active: e.target.checked })} aria-label={`${x.name} kan vælges`} /></td>
                  <td className="py-1.5 text-right"><button onClick={() => up({ extra_lines: extras.filter((y) => y.code !== x.code) })} className="text-red-700" aria-label={`Slet ${x.name}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input className={`${inputFit} w-48`} placeholder="Ny linje, fx Udetillæg" value={xName} onChange={(e) => setDx({ name: e.target.value })} data-testid="payroll-extra-new" />
          <select className={inputFit} value={xUnit} onChange={(e) => setDx({ unit: e.target.value })}><option value="stk">stk</option><option value="timer">timer</option><option value="kr">kr</option></select>
          <button className={btnGhost} disabled={!xName.trim()} onClick={addX} data-testid="payroll-extra-add"><Plus className="w-4 h-4" />Tilføj</button>
        </div>
        <div className="text-xs text-slate-500">En linje, der er brugt på en dag, bliver ved med at findes (som "kan ikke vælges"), så dagene beholder deres navn.</div>
      </RuleCard>
      <RuleCard title="Kontrol mod turene" help="Stemplingen sammenlignes med turenes tider (første tur startet – sidste tur afsluttet). Dage der afviger mere, bliver orange i lønperioden.">
        <div className="flex items-center gap-2 text-sm">Markér dage hvor stemplingen afviger mere end <input className={`${inputFit} w-16`} value={r.tour_tolerance_min} onChange={(e) => up({ tour_tolerance_min: e.target.value })} data-testid="payroll-tour-tol" /> min fra turene.</div>
      </RuleCard>
    </>
  );
}

// 2026-10-25: "more" = en forklaring bag "Hvad betyder det?" (begreber som fridag og normaltid).
/** 2026-10-28: Profilerne i et afsnit: Standard (firmaets regler) og navngivne udgaver. */
/** Historik (effective dating som SF): én række pr. "gælder fra"-dato med HELE aftalen den dag - tidligere, nu og
 *  kommende. Det, der er ændret i forhold til rækken før, er fremhævet. "Ret" lægger aftalen i chaufførens række. */
function TermsTimeline({ r, terms, rates, meta, scheds, stdSched, groups, payProfiles, timeProfiles, hasProfiles, editFrom, onEdit, onDeleteTerm, onDeleteRate }) {
  const td = localDate();
  const name = (list, id, fallback) => (list || []).find((x) => x.id === id)?.name || fallback;
  const termOn = (d) => { let t = terms[0]; for (const x of terms) if (x.valid_from <= d) t = x; return t || {}; };
  const rateOn = (d) => { let v = null; for (const x of rates) if (x.valid_from <= d) v = x; return v; };
  const cols = [
    ["Ansættelse", (t) => meta.employment[t.employment] || t.employment || ""],
    ["Skema", (t) => (t.employment === "ingen" ? "–" : t.schedule_id ? name(scheds, t.schedule_id, t.schedule_id) : `Standard (${stdSched?.name || "skema"})`)],
    ["Overtid", (t) => (["fuldtid", "deltid", "afloeser"].includes(t.employment) ? (t.overtime_mode === "bank" ? "Afspadseres" : "Udbetales") : "–")],
    ...(meta.can_money ? [["Lønsats", (t) => (t.pay_group_id ? name(groups, t.pay_group_id, t.pay_group_id) : groups.length ? "Standard" : "–")],
      ["Egen timeløn", (t, rt) => (rt ? (rt.rate == null ? "lønsatsen" : `${dk(rt.rate)} kr/t`) : "–")]] : []),
    ...(hasProfiles ? [["Overtidsprofil", (t) => (t.pay_profile_id ? name(payProfiles, t.pay_profile_id, t.pay_profile_id) : "Standard")],
      ["Pauseprofil", (t) => (t.time_profile_id ? name(timeProfiles, t.time_profile_id, t.time_profile_id) : "Standard")]] : []),
  ];
  const dates = [...new Set([...terms.map((x) => x.valid_from), ...rates.map((x) => x.valid_from)])].sort();
  const rows = dates.map((d) => ({ d, isTerm: terms.some((x) => x.valid_from === d), isRate: rates.some((x) => x.valid_from === d),
    vals: cols.map(([, f]) => f(termOn(d), rateOn(d))) }));
  const nowIdx = rows.reduce((acc, x, i) => (x.d <= td ? i : acc), 0);
  const status = (i) => (rows[i].d > td ? "Kommende" : i === nowIdx ? "Nu" : "Tidligere");
  const badge = (st) => st === "Kommende" ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" : st === "Nu" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300";
  if (!rows.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto" data-testid={`payroll-timeline-${r.id}`}>
      <table className="text-xs w-full">
        <thead className="bg-slate-50 dark:bg-slate-900 text-left text-[10px] uppercase tracking-wide text-slate-500">
          <tr><th className="px-3 py-1.5">Gælder fra</th><th className="px-2 py-1.5">Status</th>{cols.map(([h]) => <th key={h} className="px-2 py-1.5">{h}</th>)}<th className="px-2 py-1.5" /></tr>
        </thead>
        <tbody>
          {rows.map((x, i) => {
            const st = status(i);
            const termIdx = terms.findIndex((y) => y.valid_from === x.d);
            return (
              <tr key={x.d} className={`border-t border-slate-100 dark:border-slate-700 ${editFrom === x.d ? "bg-sky-50 dark:bg-sky-900/20" : ""}`} data-testid={`payroll-terms-${r.id}`}>
                <td className="px-3 py-1.5 whitespace-nowrap font-semibold">{x.d > "2000-01-01" ? dkDate(x.d) : "Fra start"}</td>
                <td className="px-2 py-1.5"><span className={`inline-block rounded-md px-1.5 py-0.5 text-[10px] font-bold ${badge(st)}`}>{st}</span></td>
                {x.vals.map((v, j) => {
                  const chg = i > 0 && rows[i - 1].vals[j] !== v;
                  return <td key={j} className={`px-2 py-1.5 whitespace-nowrap ${chg ? "font-bold text-sky-800 dark:text-sky-200" : "text-slate-600 dark:text-slate-300"}`} title={chg ? `Før: ${rows[i - 1].vals[j]}` : undefined}>{v}</td>;
                })}
                <td className="px-2 py-1.5 whitespace-nowrap text-right space-x-2">
                  {x.isTerm && editFrom !== x.d && <button type="button" className="text-sky-700 font-semibold hover:underline" title="Læg aftalen i rækken og ret den" data-testid={`payroll-terms-edit-${r.id}-${x.d}`} onClick={() => onEdit(x.d)}>Ret</button>}
                  {x.isTerm && editFrom === x.d && <span className="text-sky-700 font-semibold">I rækken</span>}
                  {x.isTerm && termIdx > 0 && <button type="button" className="text-rose-600 hover:underline" title="Slet aftalen - den foregående gælder i stedet" data-testid={`payroll-terms-del-${r.id}-${x.d}`} onClick={() => onDeleteTerm(x.d)}>Slet</button>}
                  {x.isRate && <button type="button" className="text-rose-600 hover:underline" title="Slet egen timeløn fra denne dato - satsen før gælder i stedet" data-testid={`payroll-rate-del-${r.id}-${x.d}`} onClick={() => onDeleteRate(x.d)}>Slet sats</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function versionIndexOn(list, day) {
  let i = 0;
  (list || []).forEach((v, k) => { if (v.from <= day) i = k; });
  return i;
}

function ProfileBar({ sec, r, setR, rv, sel, onSel, drivers, dirty = false, onReload, asOf, setAsOf }) {
  const [pk, keys, termKey] = PROFILE_GROUP[sec];
  const list = r[pk] || [];
  const usedBy = (id) => drivers.filter((d) => (d.profile?.terms || []).some((t) => (t[termKey] || "") === id) || (d.profile?.[termKey] || "") === id)
    .map((d) => d.name);
  const cur = list.find((x) => x.id === sel);
  const today = localDate();
  const versions = cur ? (cur.versions || []) : ((r.versions || {})[sec] || []);
  const vIdx = versions.length ? versionIndexOn(versions, asOf) : -1;
  const vTodayIdx = versions.length ? versionIndexOn(versions, today) : -1;
  const statusOf = (i) => (versions[i].from > today ? "Kommende" : (i < vTodayIdx ? "Historisk" : "Gælder nu"));
  const setVersions = (fn) => setR((x) => (cur
    ? { ...x, [pk]: (x[pk] || []).map((p) => (p.id === cur.id ? { ...p, versions: fn(p.versions || []) } : p)) }
    : { ...x, versions: { ...(x.versions || {}), [sec]: fn((x.versions || {})[sec] || []) } }));
  // 2026-10-29: Datoen vælges i en dialog (datovælger, standard i dag) - ikke som fritekst.
  const [dateAsk, setDateAsk] = useState(null);   // {status, what, date}
  const insertVersion = (status) => {
    const what = cur ? cur.name : "Standard";
    setDateAsk({ status, what, date: today });
  };
  const confirmInsert = () => {
    const { status, what, date: d } = dateAsk;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d || "")) { toast.error("Vælg en dato"); return; }
    if (versions.some((v) => v.from === d)) { toast.error(`Der er allerede en version fra ${dkShort(d)} - vælg den og ret den`); return; }
    setDateAsk(null);
    // Ny version fra en dato = et nyt udsnit (en kopi af det, der gælder den dag) - udsnittet før gælder til
    // dagen før, udsnit efter datoen bevares. Rettes bagefter og gemmes med "Gem regler".
    setVersions((vs) => {
      const base = vs[versionIndexOn(vs, d)] || vs[0];
      const nv = { from: d, status: status || base?.status || "active", settings: JSON.parse(JSON.stringify(base?.settings || Object.fromEntries(keys.map((k) => [k, rv[k]])))) };
      return [...vs, nv].sort((x, y) => x.from.localeCompare(y.from));
    });
    setAsOf(d);
    toast.success(status ? `${what} er ${status === "inactive" ? "deaktiveret" : "aktiv"} fra ${dkShort(d)} - tryk "Gem regler"` : `Ny version fra ${dkShort(d)} - ret den, og tryk "Gem regler"`);
  };
  const deleteVersion = () => {
    if (vIdx <= 0) { toast.error("Den første version kan ikke slettes - ret den i stedet"); return; }
    const v = versions[vIdx];
    if (!window.confirm(`Slet versionen fra ${dkShort(v.from)}? Versionen før gælder så også de dage.`)) return;
    setVersions((vs) => vs.filter((x) => x.from !== v.from));
    setAsOf(today);
  };
  const add = () => {
    const name = (window.prompt(`Navn på den nye profil (en kopi af ${cur ? cur.name : "Standard"}), fx "Lager" eller "Værksted":`) || "").trim();
    if (!name) return;
    if (name.toLowerCase() === "standard" || list.some((x) => x.name.toLowerCase() === name.toLowerCase())) { toast.error("Der er allerede en profil med det navn"); return; }
    const settings = Object.fromEntries(keys.map((k) => [k, JSON.parse(JSON.stringify(rv[k] ?? null))]));
    if (pk === "pay_profiles" && settings.auto_lines == null) settings.auto_lines = (r.extra_lines || []).filter((x) => x.auto).map((x) => x.code);
    const base = slugify(name);
    const id = base && base !== "standard" && !list.some((p) => p.id === base) ? base : newId("p");
    setR((x) => ({ ...x, [pk]: [...(x[pk] || []), { id, name, settings, versions: [{ from: "2000-01-01", status: "active", settings }] }] }));
    onSel(id);
    setAsOf(today);
    toast.success(`${name} er oprettet - ret den, og tryk "Gem regler"`);
  };
  const rename = () => {
    const name = (window.prompt("Nyt navn:", cur.name) || "").trim();
    if (!name || name === cur.name) return;
    if (name.toLowerCase() === "standard" || list.some((x) => x.id !== cur.id && x.name.toLowerCase() === name.toLowerCase())) { toast.error("Der er allerede en profil med det navn"); return; }
    setR((x) => ({ ...x, [pk]: x[pk].map((p) => (p.id === cur.id ? { ...p, name } : p)) }));
  };
  // En profil, en chauffør har haft, slettes ikke (dagene ville stille blive regnet med Standard) - den
  // deaktiveres fra en dato i stedet.
  const remove = () => {
    if (usedBy(cur.id).length) return;
    if (!window.confirm(`Slet profilen ${cur.name}? Ingen chauffør har haft den.`)) return;
    setR((x) => ({ ...x, [pk]: x[pk].filter((p) => p.id !== cur.id) }));
    onSel("");
  };
  const users = usedBy(sel || "").filter(Boolean);
  const curInactive = vIdx >= 0 && versions[vIdx].status === "inactive";
  return (
    <div className={`${card} p-3 space-y-2`} data-testid={`payroll-profiles-${sec}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-semibold mr-1">Profil:</span>
        <button className={`${chipBase} ${!sel ? chipOn : chipOff}`} onClick={() => { onSel(""); setAsOf(today); }} data-testid={`payroll-profile-${sec}-standard`}>Standard</button>
        {list.map((p) => (
          <button key={p.id} className={`${chipBase} ${sel === p.id ? chipOn : chipOff} ${p.active === false ? "opacity-60 line-through decoration-slate-400" : ""}`}
            title={p.active === false ? "Deaktiveret i dag - kan ikke vælges til nye vilkår" : ""} onClick={() => { onSel(p.id); setAsOf(today); }} data-testid={`payroll-profile-${sec}-${p.id}`}>{p.name}</button>
        ))}
        <button className={btnGhost} onClick={add} data-testid={`payroll-profile-${sec}-add`}><Plus className="w-4 h-4" /> Ny profil</button>
        {cur && <button className="text-xs font-semibold underline" onClick={rename}>Omdøb</button>}
        {cur && usedBy(cur.id).length === 0 && <button className="text-xs font-semibold underline text-red-700" onClick={remove} data-testid={`payroll-profile-${sec}-delete`}>Slet</button>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`payroll-versions-${sec}`}>
        <span className="text-sm font-semibold mr-1">Version:</span>
        {versions.map((v, i) => {
          const st = statusOf(i);
          const tone = st === "Kommende" ? "text-sky-700 border-sky-200" : st === "Historisk" ? "text-slate-500 border-slate-200" : "text-emerald-700 border-emerald-200";
          return (
            <button key={v.from} className={`${chipBase} ${i === vIdx ? chipOn : `${chipOff} ${tone}`}`} onClick={() => setAsOf(v.from)} data-testid={`payroll-version-${sec}-${v.from}`}
              title={`${st}: fra ${v.from > "2000-01-01" ? dkShort(v.from) : "start"}${versions[i + 1] ? ` til ${dkShort(versions[i + 1].from)} (eksklusive)` : ""}`}>
              {st}{v.from > "2000-01-01" ? ` fra ${dkShort(v.from)}` : " fra start"}{v.status === "inactive" ? " · deaktiveret" : ""}
            </button>
          );
        })}
        <label className="text-xs text-slate-500 ml-1">Vis pr. dato <input type="date" className={`${inputFit} ml-1`} value={asOf} onChange={(e) => e.target.value && setAsOf(e.target.value)} data-testid={`payroll-asof-${sec}`} /></label>
        <button className="text-xs font-semibold underline" onClick={() => insertVersion("")} data-testid={`payroll-version-${sec}-new`} title="Fx en ny overenskomst fra en dato midt i lønperioden">Ny version fra dato…</button>
        {vIdx > 0 && <button className="text-xs font-semibold underline text-red-700" onClick={deleteVersion} data-testid={`payroll-version-${sec}-delete`}>Slet version</button>}
        {cur && (curInactive
          ? <button className="text-xs font-semibold underline text-emerald-700" onClick={() => insertVersion("active")} data-testid={`payroll-profile-${sec}-active`}>Aktivér fra dato…</button>
          : <button className="text-xs font-semibold underline text-amber-700" onClick={() => insertVersion("inactive")} data-testid={`payroll-profile-${sec}-active`}>Deaktivér fra dato…</button>)}
      </div>
      <p className="text-xs text-slate-500">
        {cur ? <>Du retter profilen <b>{cur.name}</b></> : <><b>Standard</b> er firmaets regler og gælder chauffører uden profil</>}
        {vIdx >= 0 && <> - versionen <b>{statusOf(vIdx).toLowerCase()}</b>{versions[vIdx].from > "2000-01-01" ? ` fra ${dkShort(versions[vIdx].from)}` : ""}{versions[vIdx + 1] ? ` til ${dkShort(versions[vIdx + 1].from)} (eksklusive)` : ""}</>}.
        {" "}En profil er en fuld udgave af afsnittet til en gruppe med andre regler (fx lager, værksted eller en anden overenskomst) og vælges pr. chauffør under Løn › Chauffører - med "gælder fra".
        Hver dag regnes med den version, der gælder den dag - en kommende version ændrer intet, før dens dato.
        {users.length > 0 && <> Bruges af {users.slice(0, 6).join(", ")}{users.length > 6 ? " …" : ""}.</>}
        {curInactive && <> <b>Deaktiveret</b> fra denne dato - kan ikke vælges til nye vilkår; dagene, der bruger den, regnes som før.</>}
        {!sel && <> Chauffører med en anden profil: {drivers.filter((d) => d.profile?.[termKey]).length}.</>}
      </p>
      {dateAsk && createPortal(
        <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={() => setDateAsk(null)}>
          <div className={`${card} w-full max-w-sm p-5 space-y-3`} onClick={(e) => e.stopPropagation()} data-testid={`payroll-version-ask-${sec}`}>
            <div className="font-bold">{dateAsk.status === "inactive" ? `Deaktivér ${dateAsk.what} fra` : dateAsk.status === "active" ? `Aktivér ${dateAsk.what} fra` : `Ny version af ${dateAsk.what} fra`}</div>
            <input type="date" className={inputFit} value={dateAsk.date} onChange={(e) => setDateAsk({ ...dateAsk, date: e.target.value })} data-testid={`payroll-version-date-${sec}`} />
            <p className="text-xs text-slate-500">{dateAsk.status === "inactive" ? "Kan ikke vælges til nye vilkår fra datoen; dagene, der bruger den, regnes som før."
              : dateAsk.status === "active" ? "Kan vælges til nye vilkår igen fra datoen." : "Dagene før beholder reglerne, som de er. Versionen rettes bagefter og gemmes med \"Gem regler\"."}</p>
            <div className="flex justify-end gap-2">
              <button className={btnGhost} onClick={() => setDateAsk(null)}>Annuller</button>
              <button className={btnPrimary} onClick={confirmInsert} data-testid={`payroll-version-ok-${sec}`}><Check className="w-4 h-4" /> OK</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}

/** I en lønprofil: hvilke af firmaets automatiske tillæg (fx eksportkørsel) der gælder. */
function AutoPick({ r, up }) {
  const autos = (r.extra_lines || []).filter((x) => x.auto && x.active !== false);
  if (!autos.length) return null;
  const on = r.auto_lines ?? autos.map((x) => x.code);
  return (
    <div className="mt-3 text-sm space-y-1" data-testid="payroll-profile-autos">
      <div className="font-semibold">Automatiske tillæg i profilen</div>
      {autos.map((x) => (
        <label key={x.code} className="flex items-center gap-2"><input type="checkbox" checked={on.includes(x.code)}
          onChange={(e) => up({ auto_lines: e.target.checked ? [...on, x.code] : on.filter((c) => c !== x.code) })} /> {x.name}</label>
      ))}
      <div className="text-xs text-slate-500">Tillæggene selv (tidsrum, sats og lønart) oprettes under Standard.</div>
    </div>
  );
}

function RuleCard({ title, help, more, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${card} p-4 space-y-2`}>
      <div>
        <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-600" />{title}</div>
        {(help || more) && <div className="text-xs text-slate-500 mt-0.5">{help}
          {more && <button type="button" className="ml-1 font-semibold text-emerald-700 dark:text-emerald-400 hover:underline" onClick={() => setOpen((o) => !o)}>{open ? "Skjul" : "Hvad betyder det?"}</button>}</div>}
      </div>
      {more && open && <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3 space-y-1.5">{more}</div>}
      {children}
    </div>
  );
}

// Begreberne, som de bruges i reglerne (samme tekst flere steder).
const FRIDAG_TXT = <p><b>Fridag</b> = en ugedag med 0 timer i chaufførens skema - fx lørdag og søndag i et 5-dages skema, eller fredag i et 4-dages skema. Arbejde på en fridag er overtid fra første time (når overtiden følger skemaet). Fravær på en fridag tæller ikke (en sygedag på en fridag giver ingen timer). Garantien gælder på fridage.</p>;
const NORMAL_TXT = <p><b>Normaltid</b> = de timer, chaufføren skal arbejde den dag - fra skemaet (fx 7,4 t). Timer ud over normaltiden er overtid. En syge- eller feriedag giver normaltidens timer.</p>;
